// src/pages/UserPOSDashboard.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "flag-icons/css/flag-icons.min.css";

import { baseCurrency, getPhase, eurToBgn, bgnToEur, formatMoney, formatMainWithSecondary } from "../lib/currency";
import {
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Paper,
  Drawer,
  Fab,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Slide,

  
  // 🔽 add these:
  Grid,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  
} from "@mui/material";

import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import BookOnlineOutlinedIcon from "@mui/icons-material/BookOnlineOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";


import dayjs from "dayjs";
import "dayjs/locale/bg";
import ReportsStandalone from "./ReportsStandalone";
import AddShowtimes from "./AddShowtimes";
import AdminMovieImport from "./AdminMovieImport";
import PromotionPickerDialog from "../components/PromotionPickerDialog";



import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";





dayjs.locale("bg");
const API = process.env.REACT_APP_API_BASE || "";
// ---- UI palette (comfort)


// --- Theme tokens (soft-dark, from ColorsWall #81190)
const THEME = {
  bg:        "#282d40",
  surface:   "#70758A",      // derived from bg (slightly lighter)
  surfaceAlt:"#212738",      // slightly darker for contrast bands
  text:      "#e9eef6",
  textDim:   "#a4bdd3",
  primary:   "#164473",
  secondary: "#516e9e",
  cta:       "#ec8433",
  warn:      "#ec807c",
  border:    "#3a415a",
  // Seat status (calm, readable, color-blind friendly)
  seatFree:      "#5f9f77",
    seatReserved:  "#f2b84a",
  seatSold:      "#6b7186",
  seatSelected: "#6c56b5", // bright red
};

const SHOW_SEAT_SUMMARY = false; // hide the “Места … Тип … Общо …” line

function ChoicePills({ options = [], value, onChange, getLabel = (x) => String(x), sx }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ...sx }}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value ?? opt.key ?? opt.id ?? opt;
        const label = typeof opt === "string" ? opt : opt.label ?? getLabel(opt);
        const disabled = Boolean(opt.disabled);
        return (
          <Pill
            key={String(val)}
            active={value === val}
            disabled={disabled}
            onClick={() => onChange?.(value === val ? null : val, opt)}
          >
            {label}
          </Pill>
        );
      })}
    </Box>
  );
}

function seatTypeLabelBG(t) {
  const s = String(t || "");
  if (/^standard$/i.test(s)) return "Стандарт";
  if (/^vip$/i.test(s))      return "ВИП";
  if (/^(balcony|balkon|balcon|balcoon)$/i.test(s)) return "Балкон";
  return s; // fallback: show as-is
}

function seatTypeSwatchColor(t) {
  const s = String(t || "").trim().toLowerCase();

  // darker / calmer
  if (s.includes("vip") || s.includes("вип")) return "#54408d"; // dark violet
  if (s.includes("балкон") || s.includes("balcony") || s.includes("balkon") || s.includes("balcon")) return "#1f6f8b"; // dark teal-blue

  // Standard = keep your original
  return THEME.seatFree; // "#5f9f77"
}





// --- Movie category (BG) helpers ---
const CATEGORY_TEXT_BG = {
  "A":  "Препоръчва се за деца",
  "B":  "Без възрастови ограничения",
  "C":  "Не се препоръчва за деца под 12",
  "C+": "Не се препоръчва за лица под 14",
  "D":  "Забранен за лица под 16",
  "D+": "Не се препоръчва за лица под 18",
  "X":  "Забранен за лица под 18",
  "?":  "Очаква се категория",
  "N/A":"Няма категории",
};

function normalizeCategory(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;

  // 1) uppercase first
  let up = s.toUpperCase();

  // 2) map Cyrillic lookalikes → Latin
  up = up
    .replace(/А/g, "A")
    .replace(/В/g, "B")
    .replace(/С/g, "C")
    .replace(/Д/g, "D")
    .replace(/Х/g, "X");

  // 3) collapse spaces (keep '+')
  up = up.replace(/\s+/g, "");

  if (up === "NA" || up === "N-A") up = "N/A";

  if (["A","B","C","C+","D","D+","X","?","N/A"].includes(up)) return up;

  if (/ОЧАКВА|PENDING|TBD|UNKNOWN|N\/?A/.test(up)) return "N/A";

  return null;
}


// pull category from common fields in movie/showtime rows
function pickCategory(movie, showtime) {
  const candidates = [
    movie?.CATEGORY, movie?.CATEGORY_BG, movie?.AGE_CATEGORY, movie?.RATING_BG,
    showtime?.category, showtime?.CATEGORY, showtime?.rating_bg
  ];
  for (const v of candidates) {
    const n = normalizeCategory(v);
    if (n) return n;
  }
  return null;
}


// best-effort trailer extractor (add near other helpers)
function getTrailerUrl(movie) {
  const cands = [
    movie?.TRAILER_URL,
    movie?.TRAILER,
    movie?.MOVIE_TRAILER,
    movie?.TRAILER_LINK,
    movie?.YOUTUBE_URL,
    movie?.youtube_url,
  ].filter(Boolean);

  // normalize to full https URL if BE sends just a YouTube id
  const raw = cands[0];
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[\w-]{8,}$/.test(raw)) return `https://www.youtube.com/watch?v=${raw}`;
  return null;
}





function categoryColors(code) {
  switch (code) {
    case "A":  return { bg: "#15803d", fg: "#fff" }; // green
    case "B":  return { bg: "#0e7490", fg: "#fff" }; // teal
    case "C":  return { bg: "#7c3aed", fg: "#fff" }; // violet
    case "C+": return { bg: "#6d28d9", fg: "#fff" }; // deeper violet
    case "D":  return { bg: "#a16207", fg: "#fff" }; // amber
    case "D+": return { bg: "#b45309", fg: "#fff" }; // dark amber
    case "X":  return { bg: "#b91c1c", fg: "#fff" }; // red
    case "?":  return { bg: "#6b7280", fg: "#fff" }; // gray
    case "N/A":return { bg: "#6b7280", fg: "#fff" };
    default:   return { bg: "#6b7280", fg: "#fff" };
  }
}

function CategoryBadge({ code }) {
  const c = normalizeCategory(code);
  if (!c) return null;
  const { bg, fg } = categoryColors(c);
  const title = CATEGORY_TEXT_BG[c] || "Категория";
  return (
    <Box
      title={title}
      sx={{
        px: 0.75, py: 0.25,
        borderRadius: 999,
        fontSize: 12, fontWeight: 900,
        bgcolor: bg, color: fg,
        lineHeight: 1, minWidth: 28, textAlign: "center",
        border: "1px solid rgba(255,255,255,.25)",
      }}
    >
      {c}
    </Box>
  );
}

// --- Showtime meta helpers (2D/3D, HFR, SUB/DUB, language flags) ---
// --- Showtime meta helpers (2D/3D, HFR, SUB/DUB, language flags) ---

// map your language codes -> flag-icons country codes (lowercase ISO 3166-1 alpha-2)
const LANG_TO_FLAGICON = {
  BG: "bg",
  EN: "gb", // English -> UK flag (choose gb/us as you prefer)
  US: "us",
  DE: "de",
  FR: "fr",
  ES: "es",
  IT: "it",
  RU: "ru",
  GR: "gr",
  RO: "ro",
  SR: "rs", // Serbia is RS (not SR) in country codes
  MK: "mk",
  UA: "ua",
};

// optional aliases if backend sends different codes
const LANG_ALIAS = {
  UK: "EN",
  ENG: "EN",
  EL: "GR",
};

function LangFlag({ code }) {
  let c = String(code || "").trim().toUpperCase();
  c = LANG_ALIAS[c] || c;

  const cc = LANG_TO_FLAGICON[c];
  if (!cc) return <span title={c}>{c}</span>;

  return (
    <span title={c} style={{ display: "inline-flex", alignItems: "center" }}>
      <span
        className={`fi fi-${cc}`}
        style={{
          width: 16,
          height: 12,
          borderRadius: 2,
          boxShadow: "0 0 0 1px rgba(255,255,255,.18)",
        }}
      />
    </span>
  );
}




function getShowtimeMetaChips(showtime) {
  const s = showtime || {};
  const fmt = String(s.format_type || "").trim().toUpperCase();
  const isHfr = Number(s.is_hfr) ? true : false;
  const subType = String(s.sub_type || "").trim().toUpperCase();
  const isOcap = Number(s.is_ocap) ? true : false;
  const audio = String(s.audio_lang || "").trim().toUpperCase();
  const sub = String(s.sub_lang || "").trim().toUpperCase();

  const chips = [];
  if (fmt) chips.push({ key: "fmt", label: fmt });
  if (isHfr) chips.push({ key: "hfr", label: "HFR" });

  // Translation flags
  // Allow combinations like: DUB + OCAP SUB
  const hasDub = subType === "DUB";
  const hasSub = subType === "SUB" || !!sub || isOcap;

  if (hasSub) {
    const prefix = isOcap ? "OCAP " : "";
    chips.push({
      key: "sub",
      label: (
        <>
          {prefix}СУБ <LangFlag code={sub || "BG"} />
        </>
      ),
    });
  }
  
  if (hasDub) {
    chips.push({
      key: "dub",
      label: (
        <>
          ДУБ <LangFlag code={audio || "BG"} />
        </>
      ),
    });
  }
  
  // Optional explicit audio/sub languages
  if (audio && subType !== "DUB") {
    chips.push({
      key: "aud",
      label: (
        <>
          AUDIO <LangFlag code={audio} />
        </>
      ),
    });
  }
  
  if (sub && subType !== "SUB" && !isOcap) {
    chips.push({
      key: "subLang",
      label: (
        <>
          SUB <LangFlag code={sub} />
        </>
      ),
    });
  }
  

  return chips;
}



// Seat-type selector that reads your seatPricing
function SeatTypeStrip({ seatPricing = [], value, onChange, compact = false, sx }) {
  const opts = (seatPricing || []).map((p) => {
    const type = p.seat_type || p.category;
    const dot = seatTypeSwatchColor(type);
    const labelText = compact
      ? seatTypeLabelBG(type)
      : `${seatTypeLabelBG(type)} • ${formatMainWithSecondary(Number(p.price || 0))}`;

    return {
      value: type,
      label: (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: dot,
              border: "1px solid rgba(255,255,255,.25)",
              boxShadow: "0 0 0 1px rgba(0,0,0,.15) inset",
            }}
          />
          <span>{labelText}</span>
        </Box>
      ),
    };
  });

  return <ChoicePills options={opts} value={value} onChange={onChange} sx={sx} />;
}




// Example for BAR “sizes” (S / M / L) – use per article if you need it:
function SizeStrip({ sizes = [], value, onChange, sx }) {
  // sizes like: [{ value:'S', label:'S' }, {value:'M', label:'M'}, {value:'L', label:'L'}]
  return <ChoicePills options={sizes} value={value} onChange={onChange} sx={sx} />;
}


const UI = {
  // base
  bg: THEME.bg,               // #282d40
  text: THEME.text,           // #e9eef6
  surface: THEME.surface,     // #2f3650
  surfaceAlt: THEME.surfaceAlt, // #212738
  panel: "#ffffff",           // keep receipt/papers light
  panelAlt: "#f7f9fc",
  panelBorder: THEME.border,  // #3a415a
  toolbar: THEME.surface,     // the strip above seat map
  divider: THEME.border,

  card: THEME.surface,
  cardAlt: THEME.surfaceAlt,
  progressBack: "#334155",
  progress: THEME.seatFree,

  // brand
  primary: THEME.primary,     // #164473
  secondary: THEME.secondary, // #516e9e
  cta: THEME.cta,             // #ec8433
  warn: THEME.warn,           // #ec807c

  // seats (color-blind friendly)
  seatMapBg: THEME.surfaceAlt,
  seatFree: THEME.seatFree,         // #5f9f77
  seatReserved: THEME.seatReserved, // #f2b84a
  seatSold: THEME.seatSold,         // #6b7186
  seatSelected: THEME.seatSelected, // #164473
  seatMine: THEME.secondary,        //rgb(182, 33, 196)
  seatSoldStripe: "repeating-linear-gradient(45deg,#bdbdbd, #bdbdbd 6px,#9e9e9e 6px,#9e9e9e 12px)",



  // nav
  navBg: THEME.bg,
  navActive: THEME.primary,
  danger: THEME.warn,

  selectorBg: "#EFF3FA",
  inputBg: "#EFF3FA",      // or "#F7F9FC" / UI.panelAlt / whatever you like
  inputBorder: "#6b7280",  // subtle border





  // core brand
  accent:      "#b99bff",
  text:        "#EDEBFF",

  // surfaces
  bg:          "#201a35",   // app background
  nav:         "#2c204f",   // left nav
  card:        "#3a2f57",
  cardAlt:     "#40335f",

  // borders / pills / progress
  border:      "rgba(255,255,255,.10)",
  pill:        "rgba(255,255,255,.10)",
  progressBack:"rgba(255,255,255,.15)",
  progress:    "#b99bff",

  // status you already use
  danger:      "#d84a4a",
  success:     "#2e7d32",
  cardHover:   "#5a498f",
};



// feature toggles
const SHOW_CARD_BUTTON = false;
const SHOW_NONFISC_BUTTON = false;


// --- Auto API base (same as AdminFiscalDevices.jsx)
const API_BASE =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE || "";

async function apiJson(url, opts) {
  const token = localStorage.getItem("token") || "";
  const r = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    ...opts,
  });

  const ct = r.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await r.json() : null;

  if (!r.ok) {
    const msg = data?.device_error
      ? `${data.device_error}${data?.last_command ? ` (cmd: ${data.last_command})` : ""}`
      : data?.error || `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return data;
}

// Simple modal (vanilla Tailwind; use your own Modal if you have one)
function QuickFiscalModal({ open, onClose, cinemaId }) {
  const [qty, setQty] = React.useState(1);
  const [price, setPrice] = React.useState(10.0);
  const [taxes, setTaxes] = React.useState([]);
  const [taxCode, setTaxCode] = React.useState("");
  const [desc, setDesc] = React.useState("Артикули");
  const [paymentMethod, setPaymentMethod] = React.useState("cash"); // 'cash' | 'card'
  const [busy, setBusy] = React.useState(false);
  const total = Number(qty || 0) * Number(price || 0);






  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { taxes, default_tax_code } = await apiJson(
          `${API_BASE}/api/fiscal/taxes?cinema_id=${cinemaId}`
        );
        setTaxes(taxes || []);
        if (!taxCode) {
          if (default_tax_code) {
            setTaxCode(String(default_tax_code));
          } else if ((taxes || []).length) {
            const t20 = (taxes || []).find((t) => Number(t.rate) === 20) || taxes[0];
            setTaxCode(String(t20.code));
          }
        }
      } catch (e) {
        console.error(e);
        setTaxes([]);
      }
    })();
  }, [open, cinemaId]); // eslint-disable-line react-hooks/exhaustive-deps





  const onPrint = async () => {
    try {
      setBusy(true);
      const res = await apiJson(`${API_BASE}/api/pos/quick-sale`, {

        method: "POST",
        body: JSON.stringify({
          cinema_id: Number(cinemaId),
          qty: Number(qty),
          unit_price: Number(price),
          tax_code: taxCode,
          description: desc,
          payment_method: paymentMethod,
        }),
      });
      const shownBon =
        res?.bon_number ?? res?.fiscal_unp ?? res?.unp ?? res?.dy ?? res?.bon_id ?? null;

      if (shownBon) {
        alert(`✅ Печат успешно. Бон № ${shownBon}`);
      }
      onClose(true);
    } catch (e) {
      alert(`Грешка при печат: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Бърз фискален бон</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-sm mb-1">Описание</label>
            <input
              className="border rounded w-full p-2"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Брой хора</label>
            <input
              type="number"
              min="1"
              className="border rounded w-full p-2"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Цена на човек (лв.)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="border rounded w-full p-2"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm mb-1">Данъчна група</label>
            <select
              className="border rounded w-full p-2"
              value={taxCode}
              onChange={(e) => setTaxCode(e.target.value)}
            >
              {taxes.map((t) => (
                <option key={String(t.code)} value={String(t.code)}>
                  {t.name || `Група ${t.code}`}
                  {typeof t.rate === "number" ? ` (${t.rate}%)` : ""}
                </option>
              ))}
              {!taxes.length && <option value="">(няма налични групи)</option>}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm mb-1">Начин на плащане</label>
            <select
              className="border rounded w-full p-2"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="cash">КЕШ</option>
              <option value="card">КАРТА</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="text-sm">
            Общо: <span className="font-semibold">{formatMainWithSecondary(total)}</span>
          </div>
          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={() => onClose(false)}
              className="px-3 py-2 rounded border"
            >
              Отказ
            </button>
            <button
              disabled={busy || !taxCode || !cinemaId}
              onClick={onPrint}
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              {busy ? "Печат…" : "Печат на бон"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fit behavior
const MAX_FIT = 1.50;
const FIT_BONUS = 1.05;

const MOVIE_LIST_H = 350;
const SHOWTIMES_H = 260;
const TICKET_TYPES_H = 250;

const EUR_RATE = 1.95583;

// --- Small UI helpers for tiles/cards ---
const CARD_MAX = 520; // max width of a showtime card


function QtyBadge({ value, scale = 1 }) {
  const [bump, setBump] = React.useState(false);

  // Trigger a quick bump whenever the value changes
  React.useEffect(() => {
    setBump(true);
    const t = setTimeout(() => setBump(false), 180);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <Typography
      sx={{
        alignSelf: "center",
        justifySelf: "center",
        fontWeight: 900,
        fontSize: 28 * scale,             // scales with BAR zoom
        lineHeight: 1,
        minWidth: 36 * scale,
        textAlign: "center",
        color: "#F9F6EE",      // << red (MUI palette) or use "#d32f2f"
        transition: "transform 180ms ease, text-shadow 180ms ease",
        transform: bump ? "scale(1.15)" : "scale(1)",
        textShadow: bump ? "0 0 10px rgba(211,47,47,.35)" : "none",
      }}
    >
      {value}
    </Typography>
  );
}

function BridgeModePill({ bridgeOnline, bridgeMode, usePrintjob, sx }) {
  const modeLabel = bridgeMode === "printjob" ? "PRINTJOB" : "LEGACY";
  const innerBg = bridgeOnline ? "#2e7d32" : "#b71c1c";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
        bgcolor: innerBg,
        color: "#fff",
        border: "1px solid rgba(255,255,255,.25)",
        whiteSpace: "nowrap",
        userSelect: "none",
        ...sx,
      }}
      title={
        bridgeOnline
          ? `Bridge online · ${modeLabel}${usePrintjob ? " (pref)" : ""}`
          : "Bridge offline"
      }
    >
      BRIDGE · {modeLabel}
      {usePrintjob ? " (pref)" : ""}
    </Box>
  );
}

// simple “pill” chip
function Pill({
  children,
  active = false,
  disabled = false,
  onClick,
  sx,
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        borderRadius: 999,
        bgcolor: active ? "#6c56b5" : UI.pill,
        color: active ? "#fff" : "#EDEBFF",
        display: "inline-flex",
        alignItems: "center",
        border: `1px solid ${active ? "rgba(255,255,255,.28)" : UI.border}`,
        gap: 0.5,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : onClick ? "pointer" : "default",
        transition: "background 140ms ease, color 140ms ease, border-color 140ms ease",
        "&:hover": !disabled && onClick ? { bgcolor: active ? "#7a64c1" : "rgba(255,255,255,.16)" } : {},
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}


function PosBigPill({ userName, posBusy, posOk, refreshPosStatus, hideName = false, sx }) {
  const label  = posBusy ? "ПРОВЕРКА…" : (posOk ? "POS ONLINE" : "POS OFFLINE");
  const innerBg = posBusy ? "rgba(0,0,0,.35)" : (posOk ? "#2e7d32" : "#b71c1c");

  return (
    <Box
      onClick={(e) => { e.stopPropagation?.(); refreshPosStatus?.(); }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        minHeight: 36,
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,.15)",
        bgcolor: "rgba(0,0,0,.04)",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { bgcolor: "rgba(0,0,0,.07)" },
        ...sx,
      }}
      title="Щракни за обновяване на статуса"
    >
      <Box
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 900,
          lineHeight: 1,
          bgcolor: innerBg,
          color: "#fff",
          border: "1px solid rgba(255,255,255,.25)",
        }}
      >
        {label}
      </Box>
            {!hideName && (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {userName || "—"}
        </Typography>
      )}
    </Box>
  );
}



// Small pill that opens a trailer dialog when a URL exists
function TrailerPill({ trailerUrl, movieName, bg = "black" }) {
  const [open, setOpen] = React.useState(false);
  if (!trailerUrl) return null;

  const BG = bg === "purple" ? "#2c204f" : "#000"; // purple or black

  return (
    <>
      <Pill onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        ▶ Трейлър
      </Pill>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        // Make the dialog paper dark
        PaperProps={{
          sx: {
            bgcolor: BG,
            backgroundImage: "none",
            color: "#fff",
          },
        }}
        // Darker backdrop for cinematic vibe (optional)
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,.85)" },
        }}
      >
        <DialogTitle sx={{ bgcolor: BG, color: "#fff" }}>
          Трейлър — {movieName || "Филм"}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: BG }}>
          <Box sx={{ position: "relative", pt: "56.25%", bgcolor: BG }}>
            <iframe
              src={
                (trailerUrl.includes("youtube.com") || trailerUrl.includes("youtu.be"))
                  ? trailerUrl
                      .replace("watch?v=", "embed/")
                      .replace("youtu.be/", "www.youtube.com/embed/") + "?autoplay=1"
                  : trailerUrl
              }
              title={movieName || "Trailer"}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ bgcolor: BG, borderTopColor: "rgba(255,255,255,.12)" }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
            Затвори
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}




// Map age -> colors
function ageStyle(ageStr) {
  const s = String(ageStr || "").trim();
  const n = parseInt(s, 10);

  // default neutral
  let bg = "#6b7280", fg = "#fff", title = "Възрастова категория";

  if (Number.isFinite(n)) {
    if (n >= 18) { bg = "#b91c1c"; title = "Забранено за лица под 18 г."; }  // red
    else if (n >= 16) { bg = "#a16207"; title = "Препоръчително 16+"; }      // amber
    else if (n >= 12) { bg = "#7c3aed"; title = "Препоръчително 12+"; }      // violet
    else if (n >= 7)  { bg = "#0e7490"; title = "Подходящо 7+"; }            // teal
    else              { bg = "#15803d"; title = "За всички възрасти"; }      // green
  }
  return { label: s || "—", bg, fg, title };
}

function AgeBadge({ age }) {
  const { label, bg, fg, title } = ageStyle(age);
  return (
    <Box
      title={title}
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        bgcolor: bg,
        color: fg,
        lineHeight: 1,
        border: "1px solid rgba(255,255,255,.25)",
        minWidth: 36,
        textAlign: "center",
      }}
    >
      {label}
    </Box>
  );
}




// "HH:mm" from "HH:mm" or "HH:mm:ss"
const hm = (t) => (typeof t === "string" ? t.slice(0,5) : "");

// add movie duration (mins) to time
function endTimeFor(startHM, movie) {
  const s = String(startHM || "00:00");
  const [hh, mm] = s.split(":").map((n) => parseInt(n || "0", 10));
  const mins = getDurationMin(movie) || 0;
  const total = hh * 60 + mm + mins;
  const eh = Math.floor((total % (24 * 60)) / 60);
  const em = total % 60;
  return `${String(eh).padStart(2,"0")}:${String(em).padStart(2,"0")}`;
}

// return duration minutes as number (best-effort)
function getDurationMin(m) {
  const v = m?.DURATION_MIN ?? m?.DURATION ?? m?.duration_minutes ?? m?.duration ?? null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
const formatDurationBG = (n) => (n ? `${n} мин.` : "— мин.");

// naive BG age badge from common fields
// Normalizes whatever the BE gives us → "18+", "16+", "12+", "7+", or "0+"
function bgAge(movie, showtime) {
  // 1) candidates we’ll try in order (movie first, then showtime)
  const pick = (...fields) => {
    for (const f of fields) {
      const v = f != null ? String(f).trim() : "";
      if (v) return v;
    }
    return "";
  };

  // Common field names seen in various feeds / DBs
  const raw = pick(
    movie?.AGE_RATING, movie?.AGE, movie?.AGE_LIMIT, movie?.RATING, movie?.RATING_BG,
    movie?.AGE_CATEGORY, movie?.AGE_GROUP, movie?.CLASSIFICATION, movie?.PARENTAL_GUIDANCE,
    movie?.MPAA, movie?.MPAA_RATING, movie?.BBFC, movie?.FSK,
    showtime?.age_rating, showtime?.age, showtime?.age_limit, showtime?.rating,
    showtime?.classification
  );

  if (!raw) return "—"; // nothing found

  const s = raw.toString().trim();

  // 2) If there’s a number in there, normalize to N+
  const mNum = s.match(/\b(\d{1,2})\s*\+?\b/); // e.g. "12", "12+", "PG-12"
  if (mNum) {
    const n = Math.max(0, Math.min(18, parseInt(mNum[1], 10)));
    // Map 0..6 → "0+" (BG: for all), 7/8/9/10/11 → "7+" etc.
    if (n >= 18) return "18+";
    if (n >= 16) return "16+";
    if (n >= 12) return "12+";
    if (n >= 7)  return "7+";
    return "0+";
  }

  // 3) Map common non-numeric schemes → BG buckets
  const t = s.toLowerCase();
  // MPAA
  if (/^(g|general)/.test(t))           return "0+";
  if (/^pg([- ]?13)?$/.test(t))         return t.includes("13") ? "12+" : "7+";
  if (/^pg\b/.test(t))                  return "7+";
  if (/^r\b/.test(t))                   return "16+";
  if (/^nc[- ]?17\b/.test(t))           return "18+";
  // BBFC / FSK / misc
  if (/^(u|universal)$/.test(t))        return "0+";
  if (/^(pg)$/.test(t))                 return "7+";
  if (/^(12|12a)$/.test(t))             return "12+";
  if (/^(15)$/.test(t))                 return "16+";
  if (/^(16)$/.test(t))                 return "16+";
  if (/^(18)$/.test(t))                 return "18+";
  if (/fsk\s*0/.test(t))                return "0+";
  if (/fsk\s*6|7/.test(t))              return "7+";
  if (/fsk\s*12/.test(t))               return "12+";
  if (/fsk\s*16/.test(t))               return "16+";
  if (/fsk\s*18/.test(t))               return "18+";

  // 4) If it’s some unknown text like "All Audiences" → treat as 0+
  if (/(all|everyone|universal|family|kids)/i.test(s)) return "0+";

  // Fallback: show raw (your AgeBadge will render it in neutral color)
  return s;
}

// Fiscal reports
async function runXReport(cinemaId) {
  if (!cinemaId) throw new Error("cinema_id is required");
  return apiJson(`${API_BASE}/api/fiscal/x-report`, {
    method: "POST",
    body: JSON.stringify({ cinema_id: Number(cinemaId) }),
  });
}
async function runZReport(cinemaId) {
  if (!cinemaId) throw new Error("cinema_id is required");
  return apiJson(`${API_BASE}/api/fiscal/z-report`, {
    method: "POST",
    body: JSON.stringify({ cinema_id: Number(cinemaId) }),
  });
}

// Movie duration extractor (tries a few common fields)
const movieDuration = (m) => {
  const mins =
    m?.DURATION_MIN ??
    m?.DURATION ??
    m?.duration_minutes ??
    m?.duration ??
    null;
  const n = Number(mins);
  return Number.isFinite(n) && n > 0 ? `${n} мин.` : "— мин.";
};


// fetch helpers
async function fetchJsonStrict(url, opts) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status} on ${url}${text ? ` — ${text}` : ""}`);
    err.status = res.status;
    err.url = url;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return null;
}
async function fetchJsonSoft(url, opts) {
  try {
    return await fetchJsonStrict(url, opts);
  } catch {
    return null;
  }
}
// --- Storno-only normalizer (non-breaking; does NOT replace your trio) ---
const normDocDT = (val) => {
  const s = String(val || "");
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  // convert "2025-09-30T18:57:06.000Z" or "2025-09-30 18:57:06" -> "2025-09-30 18:57:06"
  return m ? `${m[1]} ${m[2]}` : s;
};


// Use DB fields directly: "YYYY-MM-DD HH:mm:ss"
const getDocDT = (row) => row?.device_doc_datetime || row?.bon_datetime || "";

// "DD.MM HH:mm" short chip/row
const fmtDocDTShort = (s) => {
  const v = String(s || "");
  return v ? `${v.slice(8,10)}.${v.slice(5,7)} ${v.slice(11,16)}` : "";
};

// Full raw display (use as-is from DB)
const fmtDocDTFull = (s) => String(s || "—");


// --- BG date/time helpers
const fmtBGDate = (val) => {
  if (!val) return "";
  const d = dayjs(val);
  if (d.isValid()) return d.format("DD.MM.YYYY");
  // fallback if backend sends only a string we can't parse
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); // YYYY-MM-DD...
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};
const fmtHM = (t) => (typeof t === "string" ? t.slice(0, 5) : "");


const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// --- Non-fiscal print helpers (80mm) ---
const nfEscape = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));

function openPrintWindow(html) {
  const w = window.open("", "_blank", "width=480,height=700");
  if (!w) return alert("Блокиран прозорец за печат.");
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {}
  }, 100);
}

const STORNO_PRESET_REASONS = [
  { code: "0", label: "ВРЪЩАНЕ / РЕКЛАМАЦИЯ" },
  { code: "1", label: "ОПЕРАТОРСКА ГРЕШКА" },
  { code: "2", label: "НАМАЛЯВАНЕ НА ДАН. ОСНОВА" },
];
const STORNO_REASON_LABELS = STORNO_PRESET_REASONS.map(r => r.label);


// Prefer movieInfo[] from backend; fallback to legacy fields or header parsing
function getBonMovieBits(row) {
  const mi = Array.isArray(row?.movieInfo) && row.movieInfo.length ? row.movieInfo[0] : null;

  const title = row?.movie_title || row?.movie || mi?.movie_name || null;

  // hall: use human name if present; else build from hall_id
  const hall =
    row?.hall_name ||
    row?.hall ||
    (mi?.hall_id != null ? `Зала ${mi.hall_id}` : null);

  const show_date = row?.show_date || mi?.show_date || null;
  const show_time = row?.show_time || mi?.show_time || null;

  return { title, hall, show_date, show_time };
}

// One-line string for list rows
// One-line string for list rows (robust)
function bonMovieLine(row) {
  // 1) prefer explicit fields or movieInfo[]
  const bits = getBonMovieBits(row);
  let title = bits.title;
  let hall  = bits.hall;
  let date  = bits.show_date ? fmtBGDate(bits.show_date) : null;
  let time  = bits.show_time ? fmtHM(bits.show_time) : null;

  // 2) if missing, fall back to parsing the header provided by fiscal payload
  if (!title || (!hall && !(date || time))) {
    const meta = extractBonMeta(row); // this looks at header_lines / header_text
    title = title || meta.movie || null;
    hall  = hall  || meta.hall  || null;

    // meta.date is already human (e.g. "03.01.2025"); keep as-is
    if (!date && meta.date) date = meta.date;
    if (!time && meta.time) time = meta.time;
  }

  const dt = [date, time].filter(Boolean).join(" ");
  return [title, hall, dt].filter(Boolean).join(" • ");
}



// Best-effort extract of movie/hall/date/time from a bon payload
function extractBonMeta(info) {
  const out = { movie: null, hall: null, date: null, time: null };
  if (!info) return out;

  // direct field (if backend provides it)
  if (info.movie_title) out.movie = String(info.movie_title);

  // header_lines or header_text from print-sale
  let hdr = [];
  if (Array.isArray(info.header_lines)) {
    hdr = info.header_lines;
  } else if (typeof info.header_text === "string") {
    hdr = info.header_text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  }

  if (hdr.length) {
    // convention: [0] Movie, [1] "Зала: …", [2] "Дата: …  Час: …"
    out.movie = out.movie || hdr[0] || null;

    const hallLine = hdr.find(l => /^Зала:/i.test(l));
    if (hallLine) out.hall = hallLine.replace(/^Зала:\s*/i, "").trim() || null;

    const dtLine = hdr.find(l => /^Дата:/i.test(l));
    if (dtLine) {
      const m = dtLine.match(/Дата:\s*([0-9.\-]+)\s+Час:\s*([0-9:]+)/i);
      if (m) {
        out.date = m[1] || null;
        out.time = m[2] || null;
      }
    }
  }
  return out;
}


// --- Amount keypad (inline) ---
// safe simple evaluator for "+/-" expressions like "20+10.5-5"
function evalPlusMinus(expr) {
  const tokens = String(expr || "")
    .replace(/,/g, ".")
    .replace(/[^\d.+-]/g, "")
    .match(/(\d+(?:\.\d+)?|[+\-])/g);
  if (!tokens || !tokens.length) return 0;
  let total = 0, op = "+";
  for (const t of tokens) {
    if (t === "+" || t === "-") { op = t; continue; }
    const n = parseFloat(t);
    if (!Number.isFinite(n)) continue;
    total = op === "+" ? total + n : total - n;
  }
  return total;
}

function AmountKeypadDialog({ open, onClose, totalBGN = 0, onConfirm }) {
  // NOTE: `totalBGN` is a legacy prop name. It actually means "total in BASE currency"
  // (BGN before 01.01.2026, EUR from 01.01.2026).
  const phase = getPhase();
  const base = baseCurrency(); // 'BGN' | 'EUR'

  const allowBGN = phase === "DUAL_DISPLAY" || base === "BGN";
  const allowEUR = phase !== "PRE_EUR";

  const [expr, setExpr] = React.useState("");
  const [cur, setCur] = React.useState(allowEUR ? base : "BGN"); // currency the cashier is entering

  // reset on open (keeps the dialog predictable)
  React.useEffect(() => {
    if (!open) return;
    setExpr("");
    // default to base currency; in Jan 2026 (DUAL_DISPLAY) base is EUR → default EUR
    const next = allowEUR ? base : "BGN";
    setCur(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // if phase changed and current selection becomes invalid, force back to a valid one
  React.useEffect(() => {
    if (cur === "BGN" && !allowBGN) setCur(base);
    if (cur === "EUR" && !allowEUR) setCur(base);
  }, [allowBGN, allowEUR, base, cur]);

  const round2 = React.useCallback(
    (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100,
    []
  );

  const dueBase = round2(Number(totalBGN || 0));
  const amountEntered = React.useMemo(() => evalPlusMinus(expr), [expr]);

  // Convert the entered amount to base currency
  const amountBase = React.useMemo(() => {
    const n = round2(amountEntered);
    if (cur === base) return n;
    if (base === "EUR" && cur === "BGN") return round2(bgnToEur(n));
    if (base === "BGN" && cur === "EUR") return round2(eurToBgn(n));
    return n;
  }, [amountEntered, base, cur, round2]);

  const changeBase = React.useMemo(() => round2(amountBase - dueBase), [amountBase, dueBase, round2]);
  const underPaid = React.useMemo(() => amountBase + 1e-9 < dueBase, [amountBase, dueBase]);

  // Convenience: value in both currencies (for reports/labels/debug if needed)
  const amountBGN = base === "BGN" ? amountBase : round2(eurToBgn(amountBase));
  const amountEUR = base === "EUR" ? amountBase : round2(bgnToEur(amountBase));
  const changeBGN = base === "BGN" ? changeBase : round2(eurToBgn(changeBase));
  const changeEUR = base === "EUR" ? changeBase : round2(bgnToEur(changeBase));

  // For the cashier: show change in the currency they entered (BGN/EUR)
  const changeInCur = React.useMemo(() => {
    if (cur === base) return changeBase;
    if (cur === "BGN" && base === "EUR") return round2(eurToBgn(changeBase));
    if (cur === "EUR" && base === "BGN") return round2(bgnToEur(changeBase));
    return changeBase;
  }, [cur, base, changeBase, round2]);

  const dueInCur = React.useMemo(() => {
    if (cur === base) return dueBase;
    if (cur === "BGN" && base === "EUR") return round2(eurToBgn(dueBase));
    if (cur === "EUR" && base === "BGN") return round2(bgnToEur(dueBase));
    return dueBase;
  }, [cur, base, dueBase, round2]);

  const fmtPlain = React.useCallback((n, c) => {
    const v = round2(n);
    const s = v.toFixed(2);
    return c === "EUR" ? `€${s}` : `${s} лв`;
  }, [round2]);

  const otherCur = cur === "EUR" ? "BGN" : "EUR";
  const amountOther = React.useMemo(() => {
    if (phase !== "DUAL_DISPLAY") return null;
    const n = round2(amountEntered);
    return otherCur === "BGN" ? round2(eurToBgn(n)) : round2(bgnToEur(n));
  }, [phase, otherCur, amountEntered, round2]);

  const changeOther = React.useMemo(() => {
    if (phase !== "DUAL_DISPLAY") return null;
    const n = round2(changeInCur);
    return otherCur === "BGN" ? round2(eurToBgn(n)) : round2(bgnToEur(n));
  }, [phase, otherCur, changeInCur, round2]);

  // focus trick so typing goes into the hidden input
  const inputRef = React.useRef(null);
  const refocus = React.useCallback(() => setTimeout(() => inputRef.current?.focus(), 0), []);
  React.useEffect(() => { if (open) refocus(); }, [open, refocus]);

  // Close on ESC and prevent other global ESC handlers (e.g. seat pane) from firing
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true); // capture
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);


  const append = (val) => {
    setExpr((s) => {
      if ((val === "+" || val === "-") && (!s || /[+\-]\s*$/.test(s))) return s;
      if (val === ".") {
        const last = s.split(/[+\-]/).pop() || "";
        if (last.includes(".")) return s;
      }
      return (s + String(val)).trim();
    });
    refocus();
  };
  const back = () => { setExpr((s) => s.slice(0, -1)); refocus(); };
  const clear = () => { setExpr(""); refocus(); };

  const exact = () => {
    // fill exact DUE in the currently selected currency
    setExpr(String(dueInCur.toFixed(2)));
    refocus();
  };

  const confirm = () => {
    if (underPaid) return; // underpayment not allowed
    onConfirm?.({
      // Base currency amounts (what the system uses)
      amount: amountBase,
      change: changeBase,
      baseCurrency: base,
      // Entered currency info (for UI/printing if you ever need it)
      expr,
      enteredCurrency: cur,
      enteredAmount: round2(amountEntered),
      enteredChange: changeInCur,
      // Convenience (both currencies)
      amountBGN,
      amountEUR,
      changeBGN,
      changeEUR,
    });
    onClose?.();
  };

  const acceptExact = () => {
    // set exact expression in the currently selected currency and confirm
    setExpr(String(dueInCur.toFixed(2)));
    // flush state update then confirm
    setTimeout(() => confirm(), 0);
  };

  const quickAdd = (v) => { setExpr((s) => (s ? `${s}+${v}` : String(v))); refocus(); };

  const KEYS = [
    ["7", "8", "9", "+"],
    ["4", "5", "6", "-"],
    ["1", "2", "3", "⌫"],
    ["0", ".", "C", "="],
  ];

  const QuickRow = (
    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
      {[5, 10, 20, 50, 100].map((v) => (
        <Button key={v} size="small" variant="outlined" onClick={() => quickAdd(v)}>
          +{v} {cur === "BGN" ? "лв" : "€"}
        </Button>
      ))}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose?.();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pr: 1.5 }}>
        Плащане в брой
        <Box sx={{ float: "right", display: "inline-flex", gap: 0.5 }}>
          {allowBGN && (
            <Button
              size="small"
              variant={cur === "BGN" ? "contained" : "outlined"}
              onClick={() => { setCur("BGN"); refocus(); }}
            >
              BGN
            </Button>
          )}
          {allowEUR && (
            <Button
              size="small"
              variant={cur === "EUR" ? "contained" : "outlined"}
              onClick={() => { setCur("EUR"); refocus(); }}
            >
              EUR
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {QuickRow}

        <Paper variant="outlined" sx={{ p: 1.25, mb: 1.25 }}>
          <Stack spacing={0.75}>
            <Typography sx={{ fontSize: 13, opacity: 0.8 }}>Сума за плащане</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
              {formatMainWithSecondary(dueBase)}
            </Typography>

            <Typography sx={{ fontSize: 13, opacity: 0.8, mt: 0.5 }}>
              Получено (въведено като {cur})
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
              {fmtPlain(amountEntered, cur)}{" "}
              {phase === "DUAL_DISPLAY" && amountOther != null && (
                <Typography component="span" sx={{ fontSize: 12, opacity: 0.7 }}>
                  (≈ {fmtPlain(amountOther, otherCur)})
                </Typography>
              )}
            </Typography>

            <Typography sx={{ fontSize: 13, opacity: 0.8, mt: 0.5 }}>Ресто</Typography>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 900,
                color: changeBase >= 0 ? "success.main" : "error.main",
              }}
            >
              {fmtPlain(changeInCur, cur)}{" "}
              {phase === "DUAL_DISPLAY" && changeOther != null && (
                <Typography component="span" sx={{ fontSize: 12, opacity: 0.7 }}>
                  (≈ {fmtPlain(changeOther, otherCur)})
                </Typography>
              )}
            </Typography>

            {/* Always show what the system will book (base currency) */}
            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
              Осчетоводяване: {formatMainWithSecondary(amountBase)} • Ресто: {formatMainWithSecondary(changeBase)}
            </Typography>
          </Stack>
        </Paper>

        {/* Hidden input so cashier can type */}
        <Paper variant="outlined" sx={{ p: 1, mb: 1.25, position: "relative" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
              {expr || "—"}
            </Typography>
            <Box sx={{ flex: 1 }} />
          </Stack>

                    <input
            ref={inputRef}
            value={expr}
            onChange={(e) => setExpr(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!expr.trim() || underPaid) acceptExact();
                else confirm();
              } else if (e.key === "Backspace") {
                e.preventDefault(); back();
              } else if (/^[0-9.+-]$/.test(e.key)) {
                e.preventDefault(); append(e.key);
              } else if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
              }
            }}
            inputMode="decimal"
            autoCapitalize="off"
            autoCorrect="off"
            style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
          />
        </Paper>

        {/* Keypad (compact) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 0.75,
          }}
        >
          {KEYS.flat().map((k) => {
            const common = {
              variant: ["+", "-", "="].includes(k) ? "contained" : "outlined",
              onClick: () => {
                if (k === "⌫") back();
                else if (k === "C") clear();
                else if (k === "=") {
                  if (!expr.trim() || underPaid) acceptExact();
                  else confirm();
                } else append(k);
              },
            };
            return (
              <Button key={k} {...common}>
                {k}
              </Button>
            );
          })}
        </Box>

        {/* Big helpers (cashier) — ТОЧНО + ПОТВЪРДИ (big) */}
<Stack spacing={1} sx={{ mt: 1.25 }}>
  <Button
    variant="contained"
    onClick={exact}
    sx={{
      py: { xs: 2.1, md: 2.4 },
      minHeight: { xs: 64, md: 72 },
      fontSize: { xs: 22, md: 26 },
      fontWeight: 900,
      borderRadius: 2,
      bgcolor: "#1e88e5",
      "&:hover": { bgcolor: "#1565c0" },
    }}
  >
    ТОЧНО
  </Button>

  {/* ✅ big confirm (takes the old place of C) */}
  <Button
    variant="contained"
    onClick={() => {
      // If nothing typed, treat it like "exact" then confirm
      if (!expr.trim()) acceptExact();
      else confirm();
    }}
    disabled={expr.trim() ? underPaid : false} // allow click when empty -> acceptExact()
    sx={{
      py: { xs: 2.0, md: 2.2 },
      minHeight: { xs: 60, md: 68 },
      fontSize: { xs: 20, md: 24 },
      fontWeight: 900,
      borderRadius: 2,
      bgcolor: "#2e7d32",
      "&:hover": { bgcolor: "#1b5e20" },
      "&.Mui-disabled": { bgcolor: "rgba(0,0,0,.12)" },
    }}
  >
    ПОТВЪРДИ {formatMainWithSecondary(expr.trim() ? amountBase : dueBase)}
  </Button>
</Stack>


      </DialogContent>

      <DialogActions sx={{ px: 1.25, py: 1 }}>
  <Box
    sx={{
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 1,
    }}
  >
    {/* ✅ C (ИЗЧИСТИ) */}
    <Button
      variant="contained"
      onClick={clear}
      sx={{
        minHeight: 52,
        fontSize: 16,
        fontWeight: 900,
        borderRadius: 2,
        bgcolor: "#ef6c00",
        "&:hover": { bgcolor: "#e65100" },
      }}
    >
      C (ИЗЧИСТИ)
    </Button>

    {/* ✅ ОТКАЗ */}
    <Button
      onClick={onClose}
      variant="outlined"
      color="inherit"
      sx={{
        minHeight: 52,
        fontSize: 16,
        fontWeight: 900,
        borderRadius: 2,
      }}
    >
      ОТКАЗ
    </Button>
  </Box>
</DialogActions>

    </Dialog>
  );
}


function LiveClock() {
  const [now, setNow] = React.useState(dayjs());
  React.useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, color: "#fff" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
      {now.format("DD.MM.YYYY")}, {now.format("dddd").replace(/^./, c => c.toUpperCase())}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {now.format("HH:mm:ss")}
      </Typography>
    </Box>
  );
}


export default function UserPOSDashboard() {









  
  // Fixed panels
  const theme = useTheme();
  const isXL = useMediaQuery(theme.breakpoints.up("xl"));
  const isLG = useMediaQuery(theme.breakpoints.up("lg"));
  const isMD = useMediaQuery(theme.breakpoints.up("md"));
  const isSM = useMediaQuery(theme.breakpoints.down("sm")); // <= 600px = mobile only
// after: const isSM = useMediaQuery(theme.breakpoints.down("sm"));
const isTablet  = useMediaQuery(theme.breakpoints.between("sm", "md"));
const isCompact = useMediaQuery(theme.breakpoints.down("md")); // phone + tablet

  // geometry
  // after
const seatSize = isXL ? 62 : isLG ? 56 : isMD ? 46 : 38; // tighter everywhere, esp. phone/tablet
  const gap = Math.max(2, Math.round(seatSize * 0.08));
  const rowGap = Math.max(2, Math.round(seatSize * 0.10));

  
  const leftLabelW  = Math.round(seatSize * 1.35);
  const rightLabelW = leftLabelW;
  const screenBarH = Math.round(seatSize * 0.6);

  const [activeTab, setActiveTab] = useState("tickets");
  const reportMode = activeTab === "reports";

  // 🗄️ Cinema archive export (user-scoped)
  const [archiveFrom, setArchiveFrom] = useState(dayjs());
  const [archiveTo, setArchiveTo] = useState(dayjs());

  // Валутен режим: след 01.01.2026 базовата валута е EUR;
  // през януари може да има DUAL display (EUR + BGN).
  const phase = getPhase();
  const base = baseCurrency();
  const allowBGN = phase === "DUAL_DISPLAY" || base === "BGN";
  const allowEUR = phase !== "PRE_EUR";

  // 💵 Cash drawer (служебно въвеждане/извеждане; "Каса от вчера")
  const [cashAmount, setCashAmount] = useState("");
  const [cashCurrency, setCashCurrency] = useState(allowEUR ? "EUR" : "BGN");
  const [cashComment, setCashComment] = useState("");
  const [cashBusy, setCashBusy] = useState(false);
  const [cashLast, setCashLast] = useState(null);


  // 💵 Cash drawer – четене от фискалното и история
  const [cashState, setCashState] = useState(null);
  const [cashStateLoading, setCashStateLoading] = useState(false);
  const [cashStateError, setCashStateError] = useState("");

  // offset в дни за историята: 0 = днес, 1 = вчера, 2 = -2 дни и т.н.
  const [cashHistoryDayOffset, setCashHistoryDayOffset] = useState(0);
  const [cashHistoryRows, setCashHistoryRows] = useState([]);
  const [cashHistoryLoading, setCashHistoryLoading] = useState(false);
  const [cashHistoryError, setCashHistoryError] = useState("");

  
// 🧾 Fiscal reports (X/Z + AUTO_Z snapshot) history
// Modes: day (by offset), month, year, range
const [fiscalHistoryMode, setFiscalHistoryMode] = useState("day");
const [fiscalHistoryDayOffset, setFiscalHistoryDayOffset] = useState(0);

const [fiscalHistoryMonth, setFiscalHistoryMonth] = useState(dayjs().format("YYYY-MM"));
const [fiscalHistoryYear, setFiscalHistoryYear] = useState(String(dayjs().year()));

const [fiscalHistoryFrom, setFiscalHistoryFrom] = useState(dayjs().format("YYYY-MM-DD"));
const [fiscalHistoryTo, setFiscalHistoryTo] = useState(dayjs().format("YYYY-MM-DD"));

const [fiscalHistoryRows, setFiscalHistoryRows] = useState([]);
const [fiscalHistoryLoading, setFiscalHistoryLoading] = useState(false);
const [fiscalHistoryError, setFiscalHistoryError] = useState("");
// 👁️ Fiscal report preview (receipt-like)
  const [fiscalViewOpen, setFiscalViewOpen] = useState(false);
  const [fiscalViewLoading, setFiscalViewLoading] = useState(false);
  const [fiscalViewError, setFiscalViewError] = useState("");
  const [fiscalViewRow, setFiscalViewRow] = useState(null);
  const [fiscalViewMode, setFiscalViewMode] = useState("text");



// 🧾 Fiscal bon tools (KLEN / copy / seat tickets)
const [fiscalBonPickerOpen, setFiscalBonPickerOpen] = useState(false);
const [fiscalBonQuery, setFiscalBonQuery] = useState("");
const [fiscalBonType, setFiscalBonType] = useState("all"); // all | sale | storno
const [fiscalBonLimit, setFiscalBonLimit] = useState(80);
const [fiscalBonFrom, setFiscalBonFrom] = useState(dayjs().format("YYYY-MM-DD"));
const [fiscalBonTo, setFiscalBonTo] = useState(dayjs().format("YYYY-MM-DD"));
const [fiscalBonRows, setFiscalBonRows] = useState([]);
const [fiscalBonLoading, setFiscalBonLoading] = useState(false);
const [fiscalBonError, setFiscalBonError] = useState("");

const [fiscalSelectedBon, setFiscalSelectedBon] = useState(null);
const [fiscalSelectedBonInfo, setFiscalSelectedBonInfo] = useState(null);

const [fiscalKlenOpen, setFiscalKlenOpen] = useState(false);
const [fiscalKlenText, setFiscalKlenText] = useState("");
const [fiscalKlenLoading, setFiscalKlenLoading] = useState(false);
const [fiscalKlenError, setFiscalKlenError] = useState("");

const [fiscalCopyBusy, setFiscalCopyBusy] = useState(false);
const [fiscalSeatsBusy, setFiscalSeatsBusy] = useState(false);


  // вътрешен таб за ПРОЖЕКЦИИ: "showtimes" | "movies"
  const [projMode, setProjMode] = useState("showtimes");

  const [cinemaId, setCinemaId] = useState(null);


  const [cinemaName, setCinemaName] = useState("");
  const [cinemaHdr, setCinemaHdr] = useState(null);


// --- POS status + username (left pane pill) ---
const [userName, setUserName]   = React.useState("");
const [posBusy, setPosBusy]     = React.useState(false);
const [posOk, setPosOk]         = React.useState(null); // true/false/null

// --- WS Bridge status + mode (LEGACY vs PRINTJOB) ---
const [bridgeOnline, setBridgeOnline] = React.useState(null); // true/false/null
const [bridgeMode, setBridgeMode]     = React.useState("legacy"); // 'legacy' | 'printjob'
const [usePrintjob, setUsePrintjob]   = React.useState(false);   // preference flag from device/meta

const refreshPosStatus = React.useCallback(async () => {
  if (!cinemaId) return;
  setPosBusy(true);
  try {
    const meta = await fetchJsonSoft(`${API_BASE}/api/fiscal/device-meta?cinema_id=${cinemaId}`);
    // Treat "no error" (and/or presence of serial) as ONLINE
    setPosOk(Boolean(meta && !meta.error));

    // If backend provides flags (recommended), use them to label the mode.
    // (These are safe: if absent, we keep defaults.)
    if (meta?.bridge_mode) setBridgeMode(String(meta.bridge_mode).toLowerCase());
    if (typeof meta?.use_printjob !== "undefined") setUsePrintjob(Boolean(meta.use_printjob));

    // Real bridge reachability check (best-effort; does not fail the POS pill)
    const bs = await fetchJsonSoft(`${API_BASE}/api/pos/bridge-selftest?cinema_id=${cinemaId}`);
    if (bs && typeof bs.ok !== "undefined") setBridgeOnline(Boolean(bs.ok));
  } catch {
    setPosOk(false);
    setBridgeOnline(false);
  } finally {
    setPosBusy(false);
  }
}, [cinemaId]);



  const [cashOpen, setCashOpen] = React.useState(false);


  const [showtimes, setShowtimes] = useState([]);
  const [movies, setMovies] = useState([]);
  const [seatPricing, setSeatPricing] = useState([]); 
  const [movieOverride, setMovieOverride] = useState(null); // {exists, base_price, balcony_extra} or null
// [{ seat_type, price, cinema_id, ... }]
  const [filterDate, setFilterDate] = useState(dayjs());

  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedShowtime, setSelectedShowtime] = useState(null);
  const [hallName, setHallName] = useState("");

  // NEW: seat_type selection (replaces old "category")
  const [selectedSeatType, setSelectedSeatType] = useState(null);

  // seats
  const [selectedSeats, setSelectedSeats] = useState(new Map()); // Map(seat_id => {label, seat_type})
  // ⬅️ move this up so nothing references it before it's initialized
const clearSelection = React.useCallback(() => {
  setSelectedSeats(new Map());
}, []);

  const [seats, setSeats] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [reservedMetaMap, setReservedMetaMap] = useState({}); // seat_id -> reservation/meta
  const [hoverReservationGroup, setHoverReservationGroup] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [reservationSeatIds, setReservationSeatIds] = useState(new Set());

  // Reservation dialog UI state
  const [reserveDlgOpen, setReserveDlgOpen] = useState(false);
  const [reservedInfoDlgOpen, setReservedInfoDlgOpen] = useState(false);
  const [reservedInfo, setReservedInfo] = useState(null);

  const [reserveForm, setReserveForm] = useState({ name: "", phone: "", email: "", note: "" });

  // === RESERVATIONS state ===
  const [resSearch, setResSearch] = useState("");
  

  // --- Drag-select ("paint") state ---
const dragRef = React.useRef({ down:false, mode:null, processed:new Set() }); 
// mode: 'add' | 'remove'

// helpers for (de)selecting a seat object consistently
const makeSeatLabel = (seat) => {
  const num = seat.label_text || `${seat.seat_column}`;
  const rowL = seat.row_label || `Ред ${seat.seat_row}`;
  return `${rowL} — Място ${num}`;
};

const canInteract = (seat) => {
  const id = seat?.seat_id;
  if (!id) return false;
  const st = statusMap[id];
  // don't allow touching sold/reserved
  if (st === "sold" || st === "reserved") return false;
  // while editing a reservation (move mode), skip drag to avoid conflicts
  if (currentReservation) return false;
  return true;
};

const addSeat = (seat) => {
  if (!canInteract(seat)) return;
  const id = seat.seat_id;
  setSelectedSeats((prev) => {
    if (prev.has(id)) return prev; // already selected
    const next = new Map(prev);
    const autoType =
      selectedSeatType ||
      pickSeatTypeFor(seat, seatPricing) ||
      defaultSeatType();
    next.set(id, { label: makeSeatLabel(seat), seat_type: autoType, pricing_mode: selectedSeatType ? "manual" : "auto" });
    return next;
  });
};

const removeSeat = (seat) => {
  const id = seat?.seat_id;
  if (!id) return;
  setSelectedSeats((prev) => {
    if (!prev.has(id)) return prev;
    const next = new Map(prev);
    next.delete(id);
    return next;
  });
};

// Remove a ticket line from the right receipt (by seat_id)
const removeSeatById = (seat_id) => {
  const id = Number(seat_id);
  if (!id) return;
  setSelectedSeats((prev) => {
    if (!prev.has(id)) return prev;
    const next = new Map(prev);
    next.delete(id);
    return next;
  });
};

// Remove a bar line from the right receipt (remove entire row)
const removeBarById = (article_id) => {
  const id = Number(article_id);
  if (!id) return;
  setBarCart((prev) => {
    if (!prev.has(id)) return prev;
    const next = new Map(prev);
    next.delete(id);
    return next;
  });
};

// start / continue / end dragging

// Open reserved-seat info dialog (and allow "Зареди в бон")
const openReservedInfoForSeat = (seat) => {
  const sid = seat?.seat_id;
  if (!sid) return;
  const meta = reservedMetaMap?.[sid] || null;
  if (!meta) {
    setSnackbar({ open: true, message: "Няма данни за резервацията за това място.", severity: "warning" });
    return;
  }
  setReservedInfo(meta);
  setReservedInfoDlgOpen(true);
};

const startDragOn = (seat) => {
  const st = statusMap?.[seat?.seat_id];
  if (st === "reserved" || st === "hold") {
    openReservedInfoForSeat(seat);
    return;
  }
  if (!canInteract(seat)) return;
  dragRef.current.down = true;
  dragRef.current.mode = selectedSeats.has(seat.seat_id) ? "remove" : "add";
  dragRef.current.processed = new Set();
  // apply to the first seat too (single click = one seat)
  if (dragRef.current.mode === "add") addSeat(seat);
  else removeSeat(seat);
  dragRef.current.processed.add(seat.seat_id);
};

const continueDragOn = (seat) => {
  if (!dragRef.current.down || !seat?.seat_id) return;
  const id = seat.seat_id;
  if (dragRef.current.processed.has(id)) return; // avoid re-toggling
  dragRef.current.processed.add(id);
  if (dragRef.current.mode === "add") addSeat(seat);
  else removeSeat(seat);
};

const endDrag = () => {
  dragRef.current.down = false;
  dragRef.current.mode = null;
  dragRef.current.processed.clear();
};

// stop drag even if mouseup happens outside the seat map
React.useEffect(() => {
  const up = () => endDrag();
  window.addEventListener("pointerup", up);
  window.addEventListener("mouseup", up);
  return () => {
    window.removeEventListener("pointerup", up);
    window.removeEventListener("mouseup", up);
  };
}, []);

  
  const [resStatus, setResStatus] = useState("active");
  const [resResults, setResResults] = useState([]);

  const [resSortKey, setResSortKey] = useState("reservation"); // reservation | showtime
  const [resSortDir, setResSortDir] = useState("desc"); // asc | desc

  // New-reservation highlight (only for ACTIVE reservations)
  const [newResIds, setNewResIds] = useState(new Set());
  const prevActiveResIdsRef = useRef(new Set());
  const newResTimersRef = useRef(new Map()); // id -> timeout
  const resNewInitRef = useRef(false);

  useEffect(() => {
    return () => {
      for (const t of newResTimersRef.current.values()) clearTimeout(t);
      newResTimersRef.current.clear();
    };
  }, []);

  const [currentReservation, setCurrentReservation] = useState(null);
  const [seatMoves, setSeatMoves] = useState([]);


// add near your other state
const [printedBons, setPrintedBons] = useState([]);
const [stornoBons, setStornoBons] = useState([]);
const [selectedBon, setSelectedBon] = useState(null); // currently selected bon (from either list)
// fetch-size + filters + sources
const [stornoLimit, setStornoLimit] = React.useState(50);
const [stornoDateFrom, setStornoDateFrom] = React.useState(null); // dayjs or null
const [stornoDateTo, setStornoDateTo] = React.useState(null);     // dayjs or null
const [stornoMovieFilter, setStornoMovieFilter] = React.useState("");
const [stornoSource, setStornoSource] = React.useState("device"); // 'device' | 'db' | 'both'

// optional DB copy of recent bons (see #2)
const [stornoRecentDB, setStornoRecentDB] = React.useState([]);

// --- Refund/Storno UI state
const [refundMode, setRefundMode] = useState("full-storno"); 
// "full-storno" | "partial-refund" | "full-refund"

const [refundPicks, setRefundPicks] = useState({}); 
// { [rowIndex]: { checked: true, qty: number } }


// slide-in seat panel (replaces the old showtimes area)
const [seatPaneOpen, setSeatPaneOpen] = useState(false);




const clearBonRef = useRef(() => {});

// when a showtime is chosen from a card, we open the panel
// slide-in seat panel opener
const openSeatPaneFor = async (s, movie) => {
  sessionStorage.setItem("postersScrollY", String(window.scrollY || 0)); // 👈 remember posters scroll
  if (movie) setSelectedMovie(movie);
  await loadSeats(s);
  setSeatPaneOpen(true);
};

// keep your current declaration
const handleSeatsExit = useCallback(() => {
  clearBonRef.current();
  const y = Number(sessionStorage.getItem("postersScrollY") || 0);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: ("instant" in window) ? "instant" : "auto" });
    });
  });
}, []);


// — normalize messy seat-type naming from DB → canonical keys from seat_pricing
// Normalize seat_type coming from DB (seats.seat_type) and from seat_pricing rows
const normalizeSeatType = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;

  if (/^(std|standart|standard|станд|стандарт)\s*$/i.test(s)) return "STANDARD";
  if (/^(vip|вип)\s*$/i.test(s)) return "VIP";
  if (/^(balcony|balkon|balcon|балкон)\s*$/i.test(s)) return "BALCONY";

  return s.toUpperCase();
};

const seatTypeFreeColor = (raw) => {
  const k = normalizeSeatType(raw);

  // darker / calmer
  if (k === "VIP") return "#54408d";       // dark violet
  if (k === "BALCONY") return "#1f6f8b";   // dark teal-blue

  // Standard (your previous green)
  return UI.seatFree; // "#5f9f77"
};



// ✅ IMPORTANT: normalize seat_pricing too (Bulgarian "Балкон" must match BALCONY)
const pickSeatTypeFor = (seat, seatPricing) => {
  const want = normalizeSeatType(seat?.seat_type);
  if (!want) return null;

  const hit = (seatPricing || []).find((p) => normalizeSeatType(p?.seat_type) === want);
  return hit ? hit.seat_type : null; // return the actual label from seat_pricing (e.g. "Балкон")
};




// ref to always call the latest version
const seatsExitRef = useRef(() => {});
seatsExitRef.current = handleSeatsExit;

// effect no longer references the function variable during render
useEffect(() => {
  if (!seatPaneOpen) return;
  const onKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      seatsExitRef.current(); // calls the latest handleSeatsExit
    }
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}, [seatPaneOpen]);


// Device browser (full list with filters)
const [deviceBrowserOpen, setDeviceBrowserOpen] = React.useState(false);
const [deviceBrowserBusy, setDeviceBrowserBusy] = React.useState(false);
const [deviceBrowserRows, setDeviceBrowserRows] = React.useState([]);


  // 🔎 STORNO search UI state (must be declared before any usage)
  const [stornoQuery, setStornoQuery] = React.useState("");
  const [stornoType, setStornoType] = React.useState("storno"); // 'all' | 'printed' | 'storno'

const bonSearchText = React.useCallback((row) => {
  const dt = fmtDocDTFull(normDocDT(getDocDT(row)));
  const unp = row?.bon_number || row?.unp || "";
  const doc = row?.device_doc_no ?? "";
  const pm  = row?.payment_method || "";
  const tot = Number(row?.total_amount || 0).toFixed(2);
  const hall = row?.hall_name || row?.hall || "";
  const movie = row?.movie_title || row?.movie || (Array.isArray(row?.movieInfo) && row.movieInfo[0]?.movie_name) || "";

  return [
    unp, doc, pm, tot, dt, hall, movie
  ].filter(Boolean).join(" ").toLowerCase();
}, []);

// tokenized, lenient contains
const matchesQuery = React.useCallback((row, q) => {
  if (!q) return true;
  const hay = bonSearchText(row);
  const tokens = String(q).toLowerCase().trim().split(/\s+/).filter(Boolean);
  return tokens.every(t => hay.includes(t));
}, [bonSearchText]);


const applyExtraFilters = (rows) => {
  let out = rows;
  // date range
  if (stornoDateFrom || stornoDateTo) {
    out = out.filter(r => {
      const d = parseDocDayjs(r);
      if (!d) return false;
      if (stornoDateFrom && d.isBefore(stornoDateFrom, "day")) return false;
      if (stornoDateTo && d.isAfter(stornoDateTo, "day")) return false;
      return true;
    });
  }
  // movie
  const qMovie = stornoMovieFilter.trim().toLowerCase();
  if (qMovie) {
    out = out.filter(r => {
      const bits = getBonMovieBits(r);
      const meta = extractBonMeta(r);
      const title = (bits.title || meta.movie || "").toLowerCase();
      return title.includes(qMovie);
    });
  }
  return out;
};


const openDeviceBrowser = React.useCallback(async () => {
  if (!cinemaId) return;
  setDeviceBrowserBusy(true);
  setDeviceBrowserOpen(true);
  try {
    // pull a bigger slice from the device; backend may cap it
    const lim = Math.max(100, Number(stornoLimit) || 500);
    const dev = await apiJson(
      `${API_BASE}/api/fiscal/recent-bons?cinema_id=${cinemaId}&limit=${lim}&include_negative=1`
    );
    let rows = Array.isArray(dev?.bons) ? dev.bons : Array.isArray(dev) ? dev : [];

    // de-dupe by (UNP|Doc)
    const seen = new Set();
    rows = rows.filter(r => {
      const unp = r?.bon_number || r?.unp || "";
      const doc = r?.device_doc_no ?? "";
      const key = `${unp}|${doc}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return !!unp;
    });

    // apply the same filters you already use in the storno pane
    if (stornoType !== "all") {
      const wantStorno = stornoType === "storno";
      rows = rows.filter(r => (Number(r.total_amount || 0) < 0) === wantStorno);
    }
    if (stornoQuery.trim()) {
      rows = rows.filter(r => matchesQuery(r, stornoQuery));
    }
    rows = applyExtraFilters(rows);

    // newest first
    rows.sort((a,b) =>
      normDocDT(getDocDT(b)).localeCompare(normDocDT(getDocDT(a)))
    );
    setDeviceBrowserRows(rows);
  } catch (e) {
    setSnackbar({ open:true, severity:"error", message:`Устройството не върна данни: ${e.message}` });
    setDeviceBrowserRows([]);
  } finally {
    setDeviceBrowserBusy(false);
  }
}, [cinemaId, stornoLimit, stornoType, stornoQuery, applyExtraFilters, matchesQuery]);





// helper — JS only
const isStorno = (b) => Number(b && b.total_amount) < 0 || !!(b && b.storno_of_bon_id);


const normDocNo = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  if (!s || /^0+$/.test(s)) return null;
  return String(parseInt(s, 10)); // strip leading zeros
};





// make a searchable haystack for a bon row



  // STORNO state
  const [stornoBon, setStornoBon] = useState("");
  const [stornoInfo, setStornoInfo] = useState(null);
  const [stornoReason, setStornoReason] = useState(STORNO_PRESET_REASONS[0].label);
  const [stornoBusy, setStornoBusy] = useState(false);
  const [stornoRecent, setStornoRecent] = useState([]);
  // When clicking a storno bon we can highlight the ORIGINAL bon in the printed list.
  // (DB has storno_of_unp; device list often does not.)
  const [stornoFocusOrigUnp, setStornoFocusOrigUnp] = useState("");

  // Expand / collapse storno details inside the list
  const [stornoOpenKeys, setStornoOpenKeys] = useState(() => new Set());

  // Refs used to scroll Printed list to the original bon when a storno is selected
  const printedBonRowRefs = useRef(new Map());

  // Scroll should happen ONLY when the user explicitly clicks a storno row.
  // We keep a one-time pending scroll key so polling / filtering won't keep jumping the list.
  const stornoScrollPendingRef = useRef("");
  const requestStornoFocus = useCallback((origUnp) => {
    const key = String(origUnp || "").trim();
    setStornoFocusOrigUnp(key);
    stornoScrollPendingRef.current = key;
  }, []);

  // If operator leaves the storno tab, clear focus.
  useEffect(() => {
    if (activeTab !== "storno") {
      setStornoFocusOrigUnp("");
      stornoScrollPendingRef.current = "";
    }
  }, [activeTab]);

  const loadRecentBons = useCallback(async () => {
    if (!cinemaId) return;
    try {
      // DEVICE
      const dev = await apiJson(
        `${API_BASE}/api/fiscal/recent-bons?cinema_id=${cinemaId}&limit=${stornoLimit}&include_negative=1`
      );
      let devRows = Array.isArray(dev?.bons) ? dev.bons : Array.isArray(dev) ? dev : [];
  
      // unique by (unp|doc)
      const seen = new Set();
      devRows = devRows.filter(r => {
        const unp = r?.bon_number || r?.unp || "";
        const doc = r?.device_doc_no ?? "";
        const key = `${unp}|${doc}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return !!unp;
      });
  
      devRows = devRows
        .map(r => ({ ...r, is_storno: Number(r.total_amount) < 0 }))
        .sort((a, b) => {
          const sa = a.is_storno ? 1 : 0;
          const sb = b.is_storno ? 1 : 0;
          if (sa !== sb) return sa - sb;
          return normDocDT(getDocDT(b)).localeCompare(normDocDT(getDocDT(a)));
        });
  
      setStornoRecent(devRows);
  
      // DB (best effort; endpoint name may differ on your BE – see #2)
      if (stornoSource !== "device") {
        try {
          const db = await apiJson(
            `${API_BASE}/api/fiscal/recent-bons-db?cinema_id=${cinemaId}&limit=${stornoLimit}&include_negative=1`
          );
          const dbRows = Array.isArray(db?.bons) ? db.bons : Array.isArray(db) ? db : [];
          setStornoRecentDB(dbRows);
        } catch {
          setStornoRecentDB([]); // if API not present, we still show device data
        }
      } else {
        setStornoRecentDB([]);
      }
    } catch (e) {
      console.error(e);
      setStornoRecent([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: `Не мога да заредя скорошните бонове: ${e?.message || "неизвестна грешка"}`,
      });
    }
  }, [cinemaId, stornoLimit, stornoSource]);
  
  const parseDocDayjs = (row) => {
    const s = normDocDT(getDocDT(row)); // "YYYY-MM-DD HH:mm:ss"
    if (!s) return null;
    const d = dayjs(s.replace(" ", "T"));
    return d.isValid() ? d : null;
  };
  


// choose a stable key – prefer device_doc_no else unp
const bonKey = (r) => {
  const doc = normDocNo(r?.device_doc_no);
  const unp = r?.bon_number || r?.unp || "";
  return doc ? `DOC#${doc}` : `UNP#${unp}`;
};

const asPos = (rows) => {
  const m = new Map();
  for (const r of rows) m.set(bonKey(r), r);
  return m;
};

const [mismatches, setMismatches] = React.useState(new Set());

useEffect(() => {
  if (stornoSource !== "both") { setMismatches(new Set()); return; }
  const dev = asPos(stornoRecent || []);
  const db  = asPos(stornoRecentDB || []);
  const bad = new Set();

  const keys = new Set([...dev.keys(), ...db.keys()]);
  for (const k of keys) {
    const A = dev.get(k);
    const B = db.get(k);
    if (!A || !B) { bad.add(k); continue; }
    const totA = Number(A.total_amount || 0).toFixed(2);
    const totB = Number(B.total_amount || 0).toFixed(2);
    const pmA  = (A.payment_method||"").toLowerCase();
    const pmB  = (B.payment_method||"").toLowerCase();
    const storA = Number(A.total_amount||0) < 0;
    const storB = Number(B.total_amount||0) < 0;
    if (totA !== totB || pmA !== pmB || storA !== storB) bad.add(k);
  }
  setMismatches(bad);
}, [stornoSource, stornoRecent, stornoRecentDB]);

// Source selector actually affects what we show in the lists.
// - device: uses /api/fiscal/recent-bons
// - db:     uses /api/fiscal/recent-bons-db (if available)
// - both:   merges device + db and enriches storno_of_* fields from DB where possible
const recentAll = React.useMemo(() => {
  const devRows = Array.isArray(stornoRecent) ? stornoRecent : [];
  const dbRows = Array.isArray(stornoRecentDB) ? stornoRecentDB : [];

  if (stornoSource === "db") return dbRows;
  if (stornoSource !== "both") return devRows;

  const dev = asPos(devRows);
  const db = asPos(dbRows);

  const merged = [];
  const seen = new Set();

  // keep device order first (what cashier sees on the device), but enrich from DB
  for (const r of devRows) {
    const k = bonKey(r);
    const B = db.get(k);
    const out = { ...(B || {}), ...(r || {}) };
    // keep the storno reference if DB knows it
    out.storno_of_unp = out.storno_of_unp || out.storno_of_unp_code || (B && (B.storno_of_unp || B.storno_of_unp_code)) || null;
    out.storno_of_bon_id = out.storno_of_bon_id || (B && B.storno_of_bon_id) || null;
    out.is_storno = Number(out.total_amount || 0) < 0 || !!out.is_storno || !!out.storno_of_bon_id;
    merged.push(out);
    seen.add(k);
  }

  // append DB-only rows at the end
  for (const r of dbRows) {
    const k = bonKey(r);
    if (seen.has(k)) continue;
    const out = { ...(r || {}) };
    out.storno_of_unp = out.storno_of_unp || out.storno_of_unp_code || null;
    out.is_storno = Number(out.total_amount || 0) < 0 || !!out.is_storno || !!out.storno_of_bon_id;
    merged.push(out);
  }

  return merged;
}, [stornoSource, stornoRecent, stornoRecentDB]);




  
  




// unified filtered view (applies type filter + search)
const filteredBons = React.useMemo(() => {
  let rows = recentAll || [];
  if (stornoType !== "all") {
    const wantStorno = stornoType === "storno";
    rows = rows.filter(r => (Number(r.total_amount || 0) < 0) === wantStorno);
  }
  if (stornoQuery.trim()) {
    rows = rows.filter(r => matchesQuery(r, stornoQuery));
  }
  return rows;
}, [recentAll, stornoType, stornoQuery, matchesQuery]);


// --- NEW: device storno check by Док. № (doc_no) over the latest 50 device bons
const checkDocNoStornoInDevice = React.useCallback(
  async (rawDocNo) => {
    if (!cinemaId) return { ok:false, reason:"no_cinema" };
    const doc = String(rawDocNo || "").replace(/\D/g, "");
    if (!doc) return { ok:false, reason:"no_doc" };

    try {
      // ask device for the latest 50 (we already have an endpoint in use)
      const data = await apiJson(
        `${API_BASE}/api/fiscal/recent-bons?cinema_id=${cinemaId}&limit=50&include_negative=1`
      );
      const rows = Array.isArray(data?.bons) ? data.bons : Array.isArray(data) ? data : [];
      // any negative bon with the same device_doc_no means "storno present"
      const hitStorno = rows.some(r =>
        String(r?.device_doc_no || "").replace(/\D/g, "") === doc &&
        Number(r?.total_amount || 0) < 0
      );
      const hitPrinted = rows.some(r =>
        String(r?.device_doc_no || "").replace(/\D/g, "") === doc &&
        Number(r?.total_amount || 0) >= 0
      );
      return { ok:true, storno: hitStorno, exists: hitPrinted || hitStorno };
    } catch (e) {
      return { ok:false, reason:String(e?.message||"error") };
    }
  }, [cinemaId]
);

// search box for doc-no quick check
const [stornoDocNo, setStornoDocNo] = React.useState("");


// 1) compute recent groups first
const printedRecent = useMemo(
  () => (recentAll || []).filter(r => Number(r.total_amount || 0) >= 0),
  [recentAll]
);
const stornoOnly = useMemo(
  () => (recentAll || []).filter(r => Number(r.total_amount || 0) < 0),
  [recentAll]
);

// Always keep a "device-only" storno set (used to mark printed bons as stornoed by the device).
const stornoOnlyDevice = useMemo(
  () => (stornoRecent || []).filter(r => Number(r.total_amount || 0) < 0),
  [stornoRecent]
);

// declare these BEFORE they're used anywhere
const [showDeviceMismatchOnly, setShowDeviceMismatchOnly] = React.useState(false);
const deviceStornoDocNos = React.useMemo(() => {
  const docs = (stornoOnlyDevice || [])
    .map(r => normDocNo(r?.device_doc_no))
    .filter(Boolean);
  return new Set(docs);
}, [stornoOnlyDevice]);

// 2) then filter those groups by search
const printedFiltered = React.useMemo(() => {
  const q = stornoQuery.trim();
  let rows = (printedRecent || []).filter(r => !q || matchesQuery(r, q));
  if (showDeviceMismatchOnly) {
    rows = rows.filter(r => deviceStornoDocNos.has(normDocNo(r?.device_doc_no)));
  }
  return applyExtraFilters(rows);
}, [printedRecent, stornoQuery, matchesQuery, showDeviceMismatchOnly, deviceStornoDocNos, applyExtraFilters]);

const stornoFiltered = React.useMemo(() => {
  const q = stornoQuery.trim();
  return applyExtraFilters(
    (stornoOnly || []).filter(r => !q || matchesQuery(r, q))
  );
}, [stornoOnly, stornoQuery, matchesQuery, applyExtraFilters]);

  // Group storno under printed bons (by UNP or by doc.№)
  const stornoGrouped = React.useMemo(() => {
    const printedUnpSet = new Set(
      (printedFiltered || [])
        .map((b) => String((b && (b.unp_code || b.bon_number)) || "").trim())
        .filter(Boolean)
    );
    const printedDocSet = new Set(
      (printedFiltered || [])
        .map((b) => normDocNo(b && b.device_doc_no))
        .filter(Boolean)
    );

    const byUnp = new Map();
    const byDocNo = new Map();
    const unresolved = [];

    for (const s of stornoFiltered || []) {
      const ref = String(
        (s && (
          s.storno_of_unp ||
          s.storno_of_unp_code ||
          s.storno_of_bon_number ||
          s.storno_of_bon_id
        )) || ""
      ).trim();
      const docNo = normDocNo(s && s.device_doc_no);

      if (docNo) {
        const arr = byDocNo.get(docNo) || [];
        arr.push(s);
        byDocNo.set(docNo, arr);
      }

      const linkedByUnp = ref && printedUnpSet.has(ref);
      if (linkedByUnp) {
        const arr = byUnp.get(ref) || [];
        arr.push(s);
        byUnp.set(ref, arr);
        continue;
      }

      const linkedByDoc = docNo && printedDocSet.has(docNo);
      if (!linkedByDoc) unresolved.push(s);
    }

    return { byUnp, byDocNo, unresolved };
  }, [stornoFiltered, printedFiltered]);


// Scroll the Printed list to the ORIGINAL bon referenced by the selected storno.
// IMPORTANT: do it only once per user click (avoid jumping on every poll/re-render).
useEffect(() => {
  const key = String(stornoScrollPendingRef.current || "").trim();
  if (!key) return;

  const el = printedBonRowRefs.current.get(key);
  if (el && typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    stornoScrollPendingRef.current = ""; // consume the pending scroll
  }
}, [stornoFocusOrigUnp, printedFiltered]);



// optional small polling while on "storno"
useEffect(() => {
  if (activeTab !== "storno" || !cinemaId) return;
  const id = setInterval(loadRecentBons, 15000); // every 15s
  return () => clearInterval(id);
}, [activeTab, cinemaId, loadRecentBons]);



  
// 🔔 blink dot when a new ACTIVE reservation appears (any future showtime)
const [hasNewRes, setHasNewRes] = useState(false);


// --- Hard reset when the calendar date changes ---
const resetForDateChange = React.useCallback(() => {
  // movie/showtime & hall
  setSelectedMovie(null);
  setSelectedShowtime(null);
  setHallName("");

  // seats & seat status
  setSeats([]);
  setStatusMap({});
  setSelectedSeats(new Map());
  setReservationSeatIds(new Set());
  setSeatMoves([]);

  // any loaded reservation context
  setCurrentReservation(null);

  // right pane / fiscal bits
  setBonPreview(null);
  setLastBon(null);

  // selection helpers
  setSelectedSeatType(null);

  // visuals (optional but keeps UX tidy)
  setZoom(1);
}, []);

useEffect(() => {
  if (skipDateResetRef.current) {
    // пропускаме ресета точно веднъж (за "Зареди в бон")
    skipDateResetRef.current = false;
    return;
  }
  resetForDateChange();
}, [filterDate, resetForDateChange]);



useEffect(() => {
  if (!cinemaId) return;
  let lastIds = new Set();
  let inited = false;

  const poll = async () => {
    const params = new URLSearchParams({ cinema_id: String(cinemaId) });
    const rows = await fetchJsonSoft(`${API}/api/reservations/search?${params}`);
    const actives = (Array.isArray(rows) ? rows : [])
      .filter(r => normResStatus(r.status) === 'active');
    const ids = new Set(actives.map(r => r.id));
    if (!inited) { lastIds = ids; inited = true; return; }
    const newCame = [...ids].some(id => !lastIds.has(id));
    if (newCame) setHasNewRes(true);
    lastIds = ids;
  };

  poll();
  const id = setInterval(poll, 10000); // every 20s
  return () => clearInterval(id);
}, [cinemaId]);

// when you actually open the tab, clear the dot
useEffect(() => {
  if (activeTab === 'reservations') setHasNewRes(false);
}, [activeTab]);



  // BAR
  const [barArticles, setBarArticles] = useState([]);
  const [barCart, setBarCart] = useState(new Map());
  const BAR_CART_KEY = "pos_bar_cart";

  // BAR zoom (card sizing) – similar controls to seat zoom, but reflows the grid
  const BAR_ZOOM_KEY = "pos_bar_zoom";
  const [barZoom, setBarZoom] = useState(() => {
    const saved = Number(localStorage.getItem(BAR_ZOOM_KEY));
    return Number.isFinite(saved) && saved > 0 ? clamp(saved, 0.6, 1.6) : 1;
  });

  useEffect(() => {
    try {
      localStorage.setItem(BAR_ZOOM_KEY, String(barZoom));
    } catch (_) {}
  }, [barZoom]);

  // zoom
  const [fitScale, setFitScale] = useState(1);
  const [zoom, setZoom] = useState(1.00);
  const gridHostRef = useRef(null);
  const totalScale = fitScale * zoom;

    // 👇 Add this here (top-level inside UserPOSDashboard)
    const skipDateResetRef = useRef(false);

  // persist per-showtime
  const seatScrollRef = useRef({ left: 0, top: 0 });
  const keyZoom = (id) => `pos_zoom_${id || "global"}`;
  const keyScroll = (id) => `pos_scroll_${id || "global"}`;

  const handleGridScroll = (e) => {
    const host = e.currentTarget;
    seatScrollRef.current = { left: host.scrollLeft, top: host.scrollTop };
    if (selectedShowtime?.showtime_id) {
      sessionStorage.setItem(keyScroll(selectedShowtime.showtime_id), JSON.stringify(seatScrollRef.current));
    }
  };


const handleCashDrawerSubmit = async (dir) => {
  const amtAbs = Number(cashAmount);
  if (!Number.isFinite(amtAbs) || amtAbs <= 0) {
    alert("Моля, въведете положителна сума.");
    return;
  }
  if (!cinemaId) {
    alert("Не е избрано кино.");
    return;
  }

  const signedAmount = dir === "OUT" ? -Math.abs(amtAbs) : Math.abs(amtAbs);

  try {
    setCashBusy(true);

    const token = localStorage.getItem("token") || "";

    const resp = await fetch(`${API_BASE}/api/pos/cash-drawer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        cinema_id: cinemaId,
        amount: signedAmount,
        currency: cashCurrency,
        comment: cashComment,
      }),
    });

    const data = await resp.json();
    if (!data.ok) {
      alert("Грешка при операцията: " + (data.error || "Неизвестна грешка"));
      return;
    }

    setCashLast(data);
    setCashAmount("");
    setCashComment("");
    setSnackbar({
      open: true,
      severity: "success",
      message:
        dir === "OUT"
          ? "Служебното извеждане е изпратено към фискалното."
          : "Служебното въвеждане е изпратено към фискалното.",
    });
  } catch (e) {
    console.error(e);
    alert("Грешка при комуникация със сървъра.");
  } finally {
    setCashBusy(false);
  }
};

  // 🔍 Четене на текущата касова наличност от фискалното
  const handleCashDrawerReadState = async () => {
    if (!cinemaId) {
      alert("Моля, изберете кино преди да четете касата.");
      return;
    }

    setCashStateLoading(true);
    setCashStateError("");
    try {
      const data = await apiJson(
        `${API_BASE}/api/pos/cash-drawer/state?cinema_id=${cinemaId}`
      );

      if (!data?.ok) {
        throw new Error(data?.error || "Грешка при четене на касовата наличност");
      }

      setCashState(data);
    } catch (err) {
      console.error("cash-drawer/state error", err);
      setCashStateError(err.message || "Грешка при четене от фискалното устройство");
    } finally {
      setCashStateLoading(false);
    }
  };

  // 📜 История по избрана дата (чете от fiscal_service_sum, но логиката идва от FDServiceSum)
  const handleCashDrawerHistory = async () => {
    if (!cinemaId) {
      alert("Моля, изберете кино преди да четете историята на касата.");
      return;
    }

    setCashHistoryLoading(true);
    setCashHistoryError("");
    setCashHistoryRows([]);

    try {
      const baseDate = dayjs().subtract(cashHistoryDayOffset, "day");
      const dateStr = baseDate.format("YYYY-MM-DD");

      const data = await apiJson(
        `${API_BASE}/api/pos/cash-drawer/history?cinema_id=${cinemaId}&from=${dateStr}&to=${dateStr}`
      );

      const rows = data?.rows || [];
      setCashHistoryRows(rows);

      // Ако четем за "Днес", попълни автоматично последния прочит и последната операция
      if (cashHistoryDayOffset === 0 && Array.isArray(rows) && rows.length) {
        const lastState = rows.find((r) => r.direction === "STATE");
        if (lastState) {
          setCashState({
            ...lastState,
            read_at: lastState.created_at,
          });
        }
        const lastOp = rows.find((r) => r.direction === "IN" || r.direction === "OUT");
        if (lastOp) setCashLast(lastOp);
      }

      if (!rows.length) {
        setCashHistoryError("Няма записани операции за избраната дата.");
      }
    } catch (err) {
      console.error("cash-drawer/history error", err);
      setCashHistoryError(err.message || "Грешка при зареждане на историята");
    } finally {
      setCashHistoryLoading(false);
    }
  };

  // 🔄 При отваряне на таб "Каса" – зареди днешната история, за да се покажат последните стойности
  useEffect(() => {
    if (activeTab !== "cashdrawer") return;
    if (!cinemaId) return;
    handleCashDrawerHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, cinemaId]);

  // 🧾 Fiscal reports history (reads from fiscal_report_log)
  const handleFiscalReportsHistory = async () => {
    if (!cinemaId) {
      alert("Моля, изберете кино преди да четете историята на фискалните отчети.");
      return;
    }

    setFiscalHistoryLoading(true);
    setFiscalHistoryError("");
    setFiscalHistoryRows([]);

    try {
  let fromStr = "";
  let toStr = "";

  if (fiscalHistoryMode === "day") {
    const baseDate = dayjs().subtract(fiscalHistoryDayOffset, "day");
    const dateStr = baseDate.format("YYYY-MM-DD");
    fromStr = dateStr;
    toStr = dateStr;
  } else if (fiscalHistoryMode === "month") {
    const start = dayjs(`${fiscalHistoryMonth}-01`);
    fromStr = start.format("YYYY-MM-DD");
    toStr = start.endOf("month").format("YYYY-MM-DD");
  } else if (fiscalHistoryMode === "year") {
    const y = Number(fiscalHistoryYear || dayjs().year());
    fromStr = dayjs(`${y}-01-01`).format("YYYY-MM-DD");
    toStr = dayjs(`${y}-12-31`).format("YYYY-MM-DD");
  } else {
    // range
    fromStr = fiscalHistoryFrom;
    toStr = fiscalHistoryTo;
  }

  const data = await apiJson(
    `${API_BASE}/api/fiscal/reports?cinema_id=${cinemaId}&from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`
  );
  const rows = data?.rows || [];
  setFiscalHistoryRows(rows);
  if (!rows.length) setFiscalHistoryError("Няма записани отчети за избрания период.");
} catch (err) {
      console.error("fiscal/reports error", err);
      setFiscalHistoryError(err.message || "Грешка при зареждане на отчетите");
    } finally {
      setFiscalHistoryLoading(false);
    }
  };



  // 🧾 Fiscal bon picker (search + select, like STORNO)
  const loadFiscalBonPickerRows = useCallback(async () => {
    if (!cinemaId) return;
    setFiscalBonLoading(true);
    setFiscalBonError("");
    try {
      const params = new URLSearchParams({
        cinema_id: String(cinemaId),
        from: fiscalBonFrom,
        to: fiscalBonTo,
        q: fiscalBonQuery || "",
        type: fiscalBonType || "all",
        limit: String(fiscalBonLimit || 80),
      });
      const data = await apiJson(`${API_BASE}/api/fiscal/bons-text?${params.toString()}`);
      const rows = data?.rows || data?.bons || [];
      setFiscalBonRows(Array.isArray(rows) ? rows : []);
      if (!rows.length) setFiscalBonError("Няма бонове за избрания период/филтър.");
    } catch (e) {
      console.error("loadFiscalBonPickerRows", e);
      setFiscalBonError(e.message || "Грешка при зареждане на боновете");
    } finally {
      setFiscalBonLoading(false);
    }
  }, [cinemaId, fiscalBonFrom, fiscalBonTo, fiscalBonQuery, fiscalBonType, fiscalBonLimit]);

  useEffect(() => {
    if (!fiscalBonPickerOpen) return;
    if (!cinemaId) return;
    loadFiscalBonPickerRows();
  }, [fiscalBonPickerOpen, cinemaId, loadFiscalBonPickerRows]);

  const selectFiscalBonRow = async (row) => {
    if (!row) return;
    setFiscalBonPickerOpen(false);
    setFiscalSelectedBon(row);
    setFiscalSelectedBonInfo(null);
    const unp = String(row.unp_code || row.bon_number || "").trim();
    if (!unp) return;
    try {
      const full = await apiJson(`${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}?cinema_id=${cinemaId}`);
      setFiscalSelectedBonInfo(full || row);
    } catch (e) {
      console.error("selectFiscalBonRow", e);
      setFiscalSelectedBonInfo(row);
      setSnackbar({ open:true, severity:"warning", message:`Бонът е избран, но детайлите не се заредиха: ${e.message}` });
    }
  };

  const openFiscalKlen = async (refresh = false) => {
    const src = fiscalSelectedBonInfo || fiscalSelectedBon;
    const unp = String(src?.unp_code || src?.bon_number || "").trim();
    if (!unp) {
      setSnackbar({ open:true, severity:"warning", message:"Първо изберете бон." });
      return;
    }
    setFiscalKlenOpen(true);
    setFiscalKlenLoading(true);
    setFiscalKlenError("");
    setFiscalKlenText("");
    try {
      const url = `${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}/klen?refresh=${refresh ? 1 : 0}`;
      const data = await apiJson(url);
      setFiscalKlenText(String(data?.klen_text || ""));
      if (!data?.klen_text) setFiscalKlenError("Няма върнат KLEN текст (възможно: устройството не върна данни или документът е извън дневника).");
    } catch (e) {
      console.error("openFiscalKlen", e);
      setFiscalKlenError(e.message || "Грешка при KLEN");
    } finally {
      setFiscalKlenLoading(false);
    }
  };

  const printFiscalCopy = async () => {
    const src = fiscalSelectedBonInfo || fiscalSelectedBon;
    const unp = String(src?.unp_code || src?.bon_number || "").trim();
    if (!unp) {
      setSnackbar({ open:true, severity:"warning", message:"Първо изберете бон." });
      return;
    }
    setFiscalCopyBusy(true);
    try {
      await apiJson(`${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}/print-copy`, { method: "POST" });
      setSnackbar({ open:true, severity:"success", message:"Отпечатано е НЕФИСКАЛНО копие." });
    } catch (e) {
      console.error("printFiscalCopy", e);
      setSnackbar({ open:true, severity:"error", message:`Печатът не успя: ${e.message}` });
    } finally {
      setFiscalCopyBusy(false);
    }
  };

  const printFiscalSeatTickets = async () => {
    const info = fiscalSelectedBonInfo;
    if (!info) {
      setSnackbar({ open:true, severity:"warning", message:"Първо изберете бон (с детайли)." });
      return;
    }

    const movie = Array.isArray(info.movieInfo) && info.movieInfo.length ? info.movieInfo[0] : null;
    const movieTitle2 = movie?.movie_name || info.movie_name || "—";
    const showDate2 = movie?.show_date || "";
    const showTime2 = String(movie?.show_time || "").slice(0, 5) || "—";
    const hall2 = movie?.hall_id != null ? String(movie.hall_id) : "—";

    const tix = (info.items || []).filter((it) => /^\s*Билет\b/i.test(String(it.text || "")));
    if (!tix.length) {
      setSnackbar({ open:true, severity:"info", message:"Този бон няма билетни редове." });
      return;
    }

    const WIDTH = 42;
    const bgWeekdays = ["Неделя","Понеделник","Вторник","Сряда","Четвъртък","Петък","Събота"];
    const bgMonthsShort = ["ян","фев","мар","апр","май","юни","юли","авг","сеп","окт","ное","дек"];

    const wrap = (text, width = WIDTH) => {
      const s = String(text || "").replace(/\r?\n/g, " ").trim();
      if (!s) return [];
      const words = s.split(/\s+/);
      const out = [];
      let line = "";
      for (const w of words) {
        if (!line) line = w;
        else if ((line + " " + w).length <= width) line += " " + w;
        else { out.push(line); line = w; }
        if (line.length > width) { out.push(line.slice(0, width)); line = line.slice(width); }
      }
      if (line) out.push(line);
      return out;
    };

    const center = (text, width = WIDTH) => {
      const s = String(text || "").trim();
      if (!s) return "";
      if (s.length >= width) return s.slice(0, width);
      const pad = Math.floor((width - s.length) / 2);
      return " ".repeat(pad) + s;
    };

    const dotFill = (label, value, width = WIDTH) => {
      const left = `${label}:`;
      let val = String(value ?? "");
      const maxValLen = Math.max(1, width - left.length - 2);
      if (val.length > maxValLen) val = val.slice(0, maxValLen);
      const dotsCount = Math.max(1, width - left.length - 1 - val.length);
      return left + " " + ".".repeat(dotsCount) + val;
    };

    const fmtBgDate = (isoDate) => {
      const d = dayjs(isoDate);
      const dd = String(d.date()).padStart(2, "0");
      return `${dd} ${bgMonthsShort[d.month()]} ${d.year()}`;
    };
    const weekdayBg = (isoDate) => {
      const d = dayjs(isoDate);
      return bgWeekdays[d.day()];
    };

    const cinemaName2 = (cinemaHdr?.name || theaterTitle || "").trim();
    const cinemaAddr2 = (cinemaHdr?.location || cinemaHdr?.address || "").trim();
    const cinemaPhone2 = (cinemaHdr?.phone || "").trim();

    const cinemaNameLines = wrap(cinemaName2).map(center);
    const titleLine = cinemaNameLines[0] || "";
    const headerExtra = cinemaNameLines.slice(1);

    const headerLines = [
      ...headerExtra,
      ...wrap(cinemaAddr2).map(center),
      ...(cinemaPhone2 ? [center(`Тел: ${cinemaPhone2}`)] : []),
      "",
    ];

    const showIso = showDate2 || dayjs().format("YYYY-MM-DD");

    const parseSeat = (lab) => {
      const s = String(lab || "");
      const mRow = s.match(/Ред\s*[:\-]?\s*([^\s—-]+)/i);
      const mSeat = s.match(/Място\s*[:\-]?\s*([^\s—-]+)/i);
      return { row: mRow ? mRow[1] : null, seat: mSeat ? mSeat[1] : null };
    };

    const parseTicket = (itText) => {
      const raw = String(itText || "").replace(/^\s*Билет\s*/i, "").trim();
      const parts = raw.split(/\s+—\s+/);
      const seatLabel = (parts[0] || "").trim();
      const seatType = (parts[1] || "").trim();
      return { seatLabel, seatType };
    };

    setFiscalSeatsBusy(true);
    try {
      for (const it of tix) {
        const { seatLabel } = parseTicket(it.text);
        const { row, seat } = parseSeat(seatLabel);
        const price = Number(it.unit_price || 0);

        const infoLines = [
          ...wrap(`Филм: ${movieTitle2 || "—"}`),
          `Час: ${showTime2}`,
          `Дата: ${fmtBgDate(showIso)} ${weekdayBg(showIso)}`,
          `Зала: ${hall2}`,
          "",
        ];

        const rowLine = dotFill("Ред", row || "—");
        const seatLine = dotFill("Място", seat || seatLabel || "—");
        const priceLine = dotFill("Цена", `${formatMainWithSecondary(price)}`);
        const codeLine = (info?.bon_number || info?.unp_code) ? `Код: ${info?.bon_number || info?.unp_code}` : null;

        const lines = [
          ...headerLines,
          ...infoLines,
          rowLine,
          seatLine,
          priceLine,
          ...(codeLine ? [codeLine] : []),
          "",
          center("СЛУЖЕБЕН БОН"),
        ].filter(Boolean);

        await apiJson(`${API_BASE}/api/fiscal/service-bon`, {
          method: "POST",
          body: JSON.stringify({
            cinema_id: Number(cinemaId),
            title: titleLine || center(" "),
            lines,
            width: 42,
            include_nonfiscal_footer: false,
          }),
        });
      }
      setSnackbar({ open:true, severity:"success", message:`Отпечатани билети: ${tix.length} бр.` });
    } catch (e) {
      console.error("printFiscalSeatTickets", e);
      setSnackbar({ open:true, severity:"error", message:`Печатът на билети не успя: ${e.message}` });
    } finally {
      setFiscalSeatsBusy(false);
    }
  };

  const downloadFiscalReport = async (id, format = "txt") => {
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${API_BASE}/api/fiscal/reports/${id}/download?format=${encodeURIComponent(format)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const filename = (m && m[1]) ? m[1] : `fiscal_report_${id}.${format === "json" ? "json" : "txt"}`;

      const a = document.createElement("a");
      const objectUrl = window.URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("downloadFiscalReport", e);
      alert("Неуспешно изтегляне: " + (e.message || "грешка"));
    }

  };

  const downloadToolsFile = async (url, fallbackFilename) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const filename = (m && m[1]) ? m[1] : fallbackFilename;

      const a = document.createElement("a");
      const objectUrl = window.URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("downloadToolsFile", e);
      alert("Неуспешно изтегляне: " + (e.message || "грешка"));
    }
  };

  const openFiscalReportPreview = async (id) => {
    if (!id) return;
    setFiscalViewOpen(true);
    setFiscalViewLoading(true);
    setFiscalViewError("");
    setFiscalViewRow(null);
    // Default to human readable report (not raw JSON)
    setFiscalViewMode("text");
    try {
      const data = await apiJson(`${API_BASE}/api/fiscal/reports/${id}`);
      setFiscalViewRow(data?.row || null);
    } catch (e) {
      console.error("openFiscalReportPreview", e);
      setFiscalViewError(e.message || "Грешка при зареждане на отчета");
    } finally {
      setFiscalViewLoading(false);
    }
  };


  // 🔄 Auto-load when opening the "Фискал" tab
  useEffect(() => {
    if (activeTab !== "fiscal") return;
    if (!cinemaId) return;
    handleFiscalReportsHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, cinemaId]);








  const showRightReceipt = ["tickets", "bar", "reservations"].includes(activeTab);

  // Fixed panels sizes
  const leftNavW = isSM ? 72 : isLG ? 170 : isMD ? 150 : 130;
  const receiptW = isSM ? 0 : isXL ? 380 : isLG ? 320 : isMD ? 280 : 260;

  // mobile receipt
  const [receiptOpen, setReceiptOpen] = useState(false);
  const toggleReceipt = useCallback((v) => {
    setReceiptOpen((prev) => (typeof v === "boolean" ? v : !prev));
  }, []);

  const [lastBon, setLastBon] = useState(null);
// Preview of the next fiscal bon (UNP) for the current cart
const [bonPreview, setBonPreview] = useState(null);
const [promoDlgOpen, setPromoDlgOpen] = useState(false);
const [selectedPromo, setSelectedPromo] = useState(null);
const [promoEffectPreview, setPromoEffectPreview] = useState(null);

// Reports (X/Z) busy flag
const [reportBusy, setReportBusy] = useState(false);


// Нормализиране на статусите от бекенда -> active | sold | cancelled
const normResStatus = (s) => {
  const v = String(s || "").toLowerCase();
  if (["sold","completed","paid","finished","closed"].includes(v)) return "sold";
  if (["cancelled","canceled","void","deleted"].includes(v)) return "cancelled";
  return "active";
};

// Дата/час на прожекцията (string -> dayjs)
const parseShowDT = (r) => {
  const d = r?.show_date ? dayjs(r.show_date) : null;
  const t = r?.show_time ? String(r.show_time) : null;
  if (!d || !d.isValid()) return null;
  // show_time идва като "HH:mm" или "HH:mm:ss"
  const [hh, mm, ss] = (t || "00:00:00").split(":").map((x) => parseInt(x || "0", 10));
  return d.hour(hh || 0).minute(mm || 0).second(ss || 0);
};

// Дата/час на резервацията (order_date). За отменени използваме canceled_at.
const parseOrderDT = (r) => {
  const v = r?.order_date ?? r?.ORDER_DATE ?? r?.canceled_at ?? r?.CANCELED_AT ?? null;
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};



  const navigate = useNavigate();

  const handleClose = React.useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    } finally {
      localStorage.removeItem("token");
      sessionStorage.clear();
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // old grid template (kept; no longer used for selectors)
  const gridTemplate = isLG
    ? {
        columns:
          "clamp(200px, 40vw, 250px) clamp(120px, 14vw, 220px) clamp(160px, 16vw, 230px) clamp(180px, 18vw, 150px)",
        areas: `"movies shows tickets poster"`,
      }
    : isMD
    ? {
        columns: "1.4fr .7fr .8fr",
        areas: `
        "movies shows tickets"
        "poster poster poster"
      `,
      }
    : {
        columns: "1fr",
        areas: `
        "movies"
        "shows"
        "tickets"
        "poster"
      `,
      };

  // Fetch fiscal device meta
  useEffect(() => {
    if (!cinemaId) return;
    (async () => {
      try {
        const meta = await fetchJsonSoft(`${API_BASE}/api/fiscal/device-meta?cinema_id=${cinemaId}`);
        if (meta && !meta.error && meta.serial) {
          setCinemaHdr((prev) => ({ ...(prev || {}), fiscal_id: meta.serial }));
        }
      } catch {}
    })();
  }, [cinemaId]);


// When cinema changes, also refresh the pill state
useEffect(() => {
  if (cinemaId) refreshPosStatus();
}, [cinemaId, refreshPosStatus]);


  // auth -> cinema (+ derive storno permission)
  const [canStorno, setCanStorno] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const payload = JSON.parse(atob((token.split(".")[1] || "").trim()));

      if (payload?.cinema_id != null) setCinemaId(payload.cinema_id);





      const nameFromToken =
        payload?.cinema_name ||
        payload?.cinemaName ||
        payload?.theater_name ||
        payload?.theatre_name ||
        payload?.name ||
        payload?.company_name ||
        null;
      if (nameFromToken) setCinemaName(String(nameFromToken));

      const hasTrue = (v) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
      const perms = Array.isArray(payload?.permissions)
        ? payload.permissions.map((s) => String(s).toLowerCase())
        : [];

      const stornoAllowed =
        hasTrue(payload?.can_storno) ||
        hasTrue(payload?.storno) ||
        perms.includes("storno") ||
        /admin|owner|manager/i.test(String(payload?.role || ""));

      setCanStorno(Boolean(stornoAllowed));

    // Logged username (best-effort from common fields)
    const u =
      payload?.username ||
      payload?.name ||
      payload?.user_name ||
      payload?.user ||
      payload?.email ||
      null;
    if (u) setUserName(String(u));




    } catch {
      /* ignore */
    }
  }, []);

  // base data
  useEffect(() => {
    if (!cinemaId) return;
    (async () => {
      try {
        const [st, mv, pr, hdr] = await Promise.all([
          fetchJsonSoft(`${API}/api/showtimes/${cinemaId}`),
          fetchJsonSoft(`${API}/api/movies`),
          fetchJsonSoft(`${API}/api/seat-pricing/${cinemaId}`),
          fetchJsonSoft(`${API}/api/cinemas/${cinemaId}/brief`),
        ]);
        setShowtimes(Array.isArray(st) ? st : []);
        setMovies(Array.isArray(mv) ? mv : []);
        setSeatPricing(Array.isArray(pr) ? pr : []);
        if (hdr && !hdr.error) {
          setCinemaHdr(hdr);
          if (!cinemaName && hdr.name) setCinemaName(hdr.name);
        }
        // default seat_type on first pricing load
        setSelectedSeatType((prev) => prev ?? null);
      } catch {
        setSnackbar({ open: true, message: "Грешка при зареждане на данни", severity: "error" });
      }
    })();
  }, [cinemaId, cinemaName]);



    







// Бонът, който е зареден за сторно, вече е сторно (отрицателен тотал)?
const stornoReasonCode = React.useMemo(() => {
  const hit = STORNO_PRESET_REASONS.find(
    r => r.label === stornoReason || r.code === stornoReason
  );
  return hit?.code || null; // null if user typed a custom reason
}, [stornoReason]);





const isLoadedBonStorno = useMemo(
  () => Number(stornoInfo?.total_amount || 0) < 0,
  [stornoInfo]
);








  useEffect(() => {
    if (activeTab === "storno" && cinemaId) loadRecentBons();
  }, [activeTab, cinemaId, loadRecentBons]);

  async function loadStornoBon() {
    setStornoInfo(null);
    const unp = stornoBon.replace(/\s+/g, "").trim();
    if (!unp) {
      setSnackbar({ open: true, severity: "warning", message: "Въведете номер на бон (UNP)." });
      return;
    }
    try {
      const data = await apiJson(
        `${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}?cinema_id=${cinemaId}`
      );
      setStornoInfo(data);
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: `Не е намерен бон: ${e.message}` });
    }
  }

  async function dbMarkStornoByUnp(unp, reason) {
    return apiJson(`${API}/api/fiscal/db/mark-storno`, {
      method: "POST",
      body: JSON.stringify({ cinema_id: Number(cinemaId), unp, reason }),
    });
  }
  async function dbFixTotals(unp, total_amount, payment_method) {
    return apiJson(`${API}/api/fiscal/db/fix-totals`, {
      method: "POST",
      body: JSON.stringify({ cinema_id: Number(cinemaId), unp, total_amount, payment_method }),
    });
  }
  

  // 👇 put these small normalizers near runStorno (top of the component is fine)
const toDocNo = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  if (!s || /^0+$/.test(s)) return null;
  // strip leading zeros but keep numeric, max 8 digits
  const n = String(parseInt(s, 10));
  return /^\d{1,8}$/.test(n) ? n : null;
};
const toFM = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  return s ? s.padStart(8, "0") : null;
};
const toSerial = (v) => {
  const m = String(v || "").toUpperCase().match(/\bDY\d{6}\b/);
  return m ? m[0] : null;
};



// convert MySQL "YYYY-MM-DD HH:mm:ss" -> "DD-MM-YY HH:mm:SS" (Daisy)
const toDocDT_YY = (v) => {
  const s = normDocDT(v); // returns "YYYY-MM-DD HH:mm:ss" or original if not matched
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1].slice(-2)} ${m[4]}:${m[5]}:${m[6] || "00"}`;
};



const buildRefundLines = (info, picks) => {
  // returns [{ text, qty, unit_price, tax_code }] for selected rows
  const out = [];
  (info?.items || []).forEach((it, i) => {
    const pick = picks[i];
    if (!pick?.checked) return;
    const qty = Math.max(0, Math.min(Number(it.qty || 0), Number(pick.qty || 0)));
    if (!qty) return;
    out.push({
      text: it.text,
      qty,
      unit_price: Number(it.unit_price || 0),
      ...(it.tax_code != null ? { tax_code: Number(it.tax_code) } : {}),
    });
  });
  return out;
};




// we already have normDocDT(...) in this file → it yields "YYYY-MM-DD HH:mm:ss"
// backend accepts multiple formats and normalizes; this is fine to send.
const toDocDT = (v) => normDocDT(v);

  async function runStorno(method) {
    if (!cinemaId) return setSnackbar({ open: true, severity: "warning", message: "Липсва кино." });
    if (!stornoInfo) return setSnackbar({ open: true, severity: "warning", message: "Заредете бон." });
    try {
      setStornoBusy(true);
// inside runStorno(...)





// 🔁 inside runStorno(method) — REPLACE ONLY the `payload` object:
// 🔁 inside runStorno(method) — REPLACE ONLY the `payload` object:
const docNo = toDocNo(
  stornoInfo?.device_doc_no ??
  stornoInfo?.deviceDocNo ??
  stornoInfo?.device_doc_number
);
const docDTyy = toDocDT_YY(
  stornoInfo?.device_doc_datetime ||
  stornoInfo?.bon_datetime ||
  null
);
const fm = toFM(stornoInfo?.device_fm_no ?? stornoInfo?.fm_no);
const serial = toSerial(stornoInfo?.device_serial ?? stornoInfo?.deviceSerial);


const basePayload = {
  cinema_id: Number(cinemaId),
  bon_number: stornoInfo?.bon_number || stornoBon.trim(),
  original_unp: stornoInfo?.bon_number || stornoBon.trim(),
  reason: (stornoReasonCode ?? stornoReason)?.trim(),
  payment_method: (method || stornoInfo?.payment_method || "cash").toLowerCase(),
  doc_link: {
    docNo,
    docDateTime: docDTyy,
    fmNo: fm,
    deviceSerial: serial,
  },
  // fallbacks for older BE
  doc_link_doc_no: docNo,
  doc_link_dt_yy: docDTyy,
  doc_link_fm_no: fm,
  device_serial: serial,
};

// If later you add partial refunds, you can spread:
const payload = {
  cinema_id: Number(cinemaId),

  // we search by UNP; keep both for BE compatibility
  bon_number: stornoInfo?.bon_number || stornoBon.trim(),
  original_unp: stornoInfo?.bon_number || stornoBon.trim(),

  // preset code (0/1/2) if selected, else free text
  reason: (stornoReasonCode ?? stornoReason)?.trim(),

  // keep caller’s override or original method
  payment_method: (method || stornoInfo?.payment_method || "cash").toLowerCase(),

  // ✅ canonical doc_link for Daisy/NRA (DD-MM-YY)
  doc_link: {
    docNo,
    docDateTime: docDTyy,     // << two-digit year, seconds included
    fmNo: fm,
    deviceSerial: serial,

  },

  // ↩︎ fallbacks for older BE handlers (you can remove later)
  doc_link_doc_no: docNo,
  doc_link_dt_yy: docDTyy,
  doc_link_fm_no: fm,
  device_serial: serial,
};

// after building `payload`:

// and then post:
const resp = await apiJson(`${API_BASE}/api/pos/storno`, {
  method: "POST",
  body: JSON.stringify(payload),
});


      
      setLastBon(resp?.storno_bon_number || null);
      setSnackbar({
        open: true,
        severity: "success",
        message: `Сторно успешно. Нов бон: ${resp?.storno_bon_number || "—"}`,
      });
      setStornoBon("");
      setStornoInfo(null);

        // If the storno response tells us the showtime that changed, reload it
        if (resp?.freed?.showtime_id) {
          // If the freed showtime is the one currently open → refresh the map
          if (selectedShowtime?.showtime_id === resp.freed.showtime_id) {
            await loadSeats(selectedShowtime);
          }
        }
        await loadRecentBons();
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: `Грешка при сторно: ${e.message}` });
    } finally {
      setStornoBusy(false);
      await loadRecentBons(); // << ensures the page always renews even if error happened earlier
    }
  }






  const renderStornoPane = () => (
    <Box
      sx={{
        p: 2,
        height: "100vh",
        display: "grid",
        // title + filters + quick-check + (scrollable body) + footer
        gridTemplateRows: "auto auto auto 1fr auto",
        rowGap: 1,
        bgcolor: "#f5f5f5",
        color: "#111",
        overflow: "hidden",
      }}
    >
  
      {/* TITLE */}
      <Typography variant="h6" sx={{ mb: 0 }}>Сторно</Typography>
  
      {/* HEADER (outside scroll) */}
<Paper
  sx={{
    p: 1,
    borderRadius: 2,
    border: `1px solid ${UI.panelBorder}`,
    bgcolor: "#f4f6f8",
    "& .MuiTypography-root": { fontSize: 13, lineHeight: 1.25 },
  }}
>
  
  
  <Stack spacing={1}>
    {/* exact UNP load */}
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
      <TextField
        label="Номер на бон (UNP)"
        value={stornoBon}
        onChange={(e) => setStornoBon(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") loadStornoBon();
        }}
        size="small"
        placeholder="напр. DY-OP01-1234567"
        sx={{
          flex: 1,
          bgcolor: "#fff",
          borderRadius: 1,
          "& .MuiInputBase-root": { height: 40 },
        }}
      />
      <Button variant="contained" size="small" onClick={loadStornoBon}>
        ЗАРЕДИ
      </Button>
    </Stack>

    {/* smart search across ALL fields + filter */}
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
      <TextField
        label="Търсене в скорошни (UNP, Док.№, сума, метод, дата/час, зала, филм...)"
        value={stornoQuery}
        onChange={(e) => setStornoQuery(e.target.value)}
        size="small"
        placeholder="пример: карта 12.50 зала 2 2025-01-03"
        sx={{
          flex: 1,
          bgcolor: "#fff",
          borderRadius: 1,
          "& .MuiInputBase-root": { height: 40 },
        }}
      />

      <TextField
        label="Филтър"
        value={stornoType}
        onChange={(e) => setStornoType(e.target.value)}
        size="small"
        select
        SelectProps={{ native: true }}
        sx={{ minWidth: 140, bgcolor: "#fff", borderRadius: 1, "& .MuiInputBase-root": { height: 40 } }}
      >
        <option value="all">Всички</option>
        <option value="printed">Само печатани</option>
        <option value="storno">Само сторно</option>
      </TextField>


      {/* Extra filters row */}
<Stack direction={{ xs:"column", sm:"row" }} spacing={1} alignItems="center" sx={{ mt: .5 }}>
  <TextField
    label="Лимит (брой за зареждане)"
    size="small"
    type="number"
    value={stornoLimit}
    onChange={(e) => setStornoLimit(Math.max(10, Math.min(1000, Number(e.target.value)||50)))}
    sx={{ width: 180, bgcolor:"#fff", borderRadius:1 }}
  />
  <DatePicker
    label="От дата"
    value={stornoDateFrom}
    onChange={(v) => setStornoDateFrom(v)}
    slotProps={{ textField: { size:"small", sx:{ bgcolor:"#fff", borderRadius:1, minWidth:150 } } }}
  />
  <DatePicker
    label="До дата"
    value={stornoDateTo}
    onChange={(v) => setStornoDateTo(v)}
    slotProps={{ textField: { size:"small", sx:{ bgcolor:"#fff", borderRadius:1, minWidth:150 } } }}
  />
  <TextField
    label="Филм (заглавие)"
    size="small"
    value={stornoMovieFilter}
    onChange={(e) => setStornoMovieFilter(e.target.value)}
    sx={{ flex:1, minWidth:200, bgcolor:"#fff", borderRadius:1 }}
  />
  <TextField
    label="Източник"
    select
    size="small"
    value={stornoSource}
    onChange={(e) => setStornoSource(e.target.value)}
    SelectProps={{ native:true }}
    sx={{ minWidth:160, bgcolor:"#fff", borderRadius:1 }}
  >
    <option value="device">Устройство</option>
    <option value="db">База данни</option>
    <option value="both">И двете</option>
  </TextField>
  <Button variant="contained" size="small" onClick={loadRecentBons}>Приложи</Button>
</Stack>


      <Button variant="text" size="small" onClick={loadRecentBons}>
        ОБНОВИ
      </Button>

      <FormControlLabel
  control={
    <Checkbox
      size="small"
      checked={showDeviceMismatchOnly}
      onChange={(e) => setShowDeviceMismatchOnly(e.target.checked)}
    />
  }
  label={<span style={{fontSize:12}}>Само с „СТОРНО в устройство“</span>}
/>

    </Stack>
  </Stack>
</Paper>



{/* SEARCH + ACTIONS (header stays the same above) */}

{/* QUICK DOC-NO CHECK */}
<Paper sx={{ p: 1, mt: 1, borderRadius: 2, border: `1px solid ${UI.panelBorder}`, bgcolor: "#fff" }}>
  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
    <TextField
      label="Провери Док. № (фискално устройство)"
      value={stornoDocNo}
      onChange={(e) => setStornoDocNo(e.target.value)}
      size="small"
      placeholder="например 00012345"
      sx={{ flex: 1, "& .MuiInputBase-root": { height: 40 } }}
    />
    <Button
      variant="outlined"
      size="small"
      onClick={async () => {
        const res = await checkDocNoStornoInDevice(stornoDocNo);
        if (!res.ok) {
          setSnackbar({ open:true, severity:"error", message:`Проверка неуспешна: ${res.reason}` });
          return;
        }
        if (!res.exists) {
          setSnackbar({ open:true, severity:"info", message:"Този Док. № не е сред последните 50 бона на устройството." });
        } else if (res.storno) {
          setSnackbar({ open:true, severity:"success", message:"✅ В устройството има СТОРНО за този Док. №" });
        } else {
          setSnackbar({ open:true, severity:"warning", message:"ℹ️ Открит е бон с този Док. №, но НЕ е сторно." });
        }
      }}
    >
      Провери
    </Button>
  </Stack>
  <Typography variant="caption" sx={{ display:"block", mt: .5, opacity:.75 }}>
    * Проверява последните 50 бона от фискалното устройство и маркира дали има сторно за този Док. №
  </Typography>



</Paper>





  
      {/* SCROLLABLE BODY */}
      <Box sx={{ overflowY: "auto", minHeight: 0, pr: 1 }}>


{/* LATEST 50 FROM DEVICE (grouped by bon, storno inside) */}


<Box sx={{ mt: 0.75 }}>
  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75 }}>
    Бонове (печатани) + СТОРНО към тях (до 50)
  </Typography>


  <Paper sx={{ borderRadius: 2, border: `1px solid ${UI.panelBorder}`, overflow: "hidden" }}>
    <Box sx={{ maxHeight: 380, overflow: "auto", p: 0 }}>
      {printedFiltered.length === 0 ? (
        <Typography variant="body2" sx={{ p: 1, opacity: 0.7 }}>
          Няма бонове.
        </Typography>
      ) : (
        printedFiltered.map((b, i) => {
          const unp = String(b.unp_code || b.bon_number || "").trim();
          const docNo = normDocNo(b.device_doc_no);
          const dt = fmtDocDTShort(getDocDT(b));
          const pm = (b.payment_method || "").toLowerCase() || "—";
          const tot = Number(b.total_amount || 0);
          

          // Is this the currently selected bon? (used only for highlight)
          const selUnp = String(
            selectedBon?.unp_code ||
              selectedBon?.bon_number ||
              selectedBon?.unp ||
              selectedBon?.fiscal_unp ||
              ""
          ).trim();
          const selDocNo = normDocNo(
            selectedBon?.device_doc_no || selectedBon?.doc_no || ""
          );
          const sel = !!selectedBon && (
            (selUnp && unp && selUnp === unp) ||
            (selDocNo && docNo && selDocNo === docNo)
          );

          const stornoByUnp = (unp && stornoGrouped.byUnp.get(unp)) || [];
          const stornoByDoc = (docNo && stornoGrouped.byDocNo.get(docNo)) || [];
          const stornoList = stornoByUnp.length ? stornoByUnp : stornoByDoc;
          const stornoLabel = stornoByUnp.length ? "по УНП" : stornoByDoc.length ? "по док.№" : "";
          const hasStorno = stornoList.length > 0;

          const openKey = unp || `doc:${docNo}` || `i:${i}`;
          const isOpen = stornoOpenKeys.has(openKey);

          const isFocused = stornoFocusOrigUnp && stornoFocusOrigUnp === unp;
          const bg = sel ? "#fffbe6" : isFocused ? "#fff1f2" : "#fff";
          const isLast = i === printedFiltered.length - 1;

          return (
            <Box
              key={openKey}
              ref={(el) => {
                if (el) printedBonRowRefs.current.set(unp, el);
                else printedBonRowRefs.current.delete(unp);
              }}
              sx={{
                mb: 0,
                borderRadius: 0,
                border: "none",
                bgcolor: bg,
                overflow: "hidden",
                borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.12)",
              }}
            >
              <Box
                onClick={async () => {
                  setSelectedBon(b);
                  setStornoBon(unp);
                  setStornoFocusOrigUnp("");
                  setStornoInfo(null);
                  try {
                    const full = await apiJson(
                      `${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}?cinema_id=${cinemaId}`
                    );
                    setStornoInfo(full);
                  } catch (e) {
                    // Fallback (at least keep the header info selected)
                    setStornoInfo(b);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  cursor: "pointer",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <Box
                      onClick={() => {
                        if (!hasStorno) return;
                        setStornoOpenKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(openKey)) next.delete(openKey);
                          else next.add(openKey);
                          return next;
                        });
                      }}
                      sx={{
                        width: 18,
                        textAlign: "center",
                        opacity: hasStorno ? 1 : 0.25,
                        userSelect: "none",
                        cursor: hasStorno ? "pointer" : "default",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                      title={hasStorno ? "Покажи/скрий сторно" : ""}
                    >
                      {hasStorno ? (isOpen ? "▾" : "▸") : ""}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: 700,
                        color: hasStorno ? "#b91c1c" : "#111",
                      }}
                    >
                      {dt} • Док.{docNo || "—"} • №{unp || "—"} • {pm}{hasStorno ? ` • СТОРНО: ${stornoList.length}` : ""}
                    </Typography>
                  </Box>

                  {bonMovieLine(b) && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        mt: -0.25,
                      }}
                    >
                      {bonMovieLine(b)}
                    </Typography>
                  )}
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 800, color: hasStorno ? "#b91c1c" : "#111" }}>
                  {formatMainWithSecondary(tot)}
                </Typography>
              </Box>

              {hasStorno && isOpen && (
                <Box sx={{ p: 1, borderTop: "1px solid #fecaca", bgcolor: "#fff1f2" }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 0.5, fontWeight: 800, color: "#b00020" }}
                  >
                    Сторно към този бон {stornoLabel ? `(${stornoLabel})` : ""}
                  </Typography>

                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Дата/час
                          </th>
                          <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Фискален №
                          </th>
                          <th style={{ textAlign: "right", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Сума
                          </th>
                          <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Плащане
                          </th>
                          <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Потребител
                          </th>
                          <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                            Сторно на
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stornoList
                          .slice()
                          .sort((a, b) =>
                            normDocDT(a.device_doc_datetime).localeCompare(normDocDT(b.device_doc_datetime))
                          )
                          .map((s, idx2) => {
                            const sUnp = String(s.unp_code || s.bon_number || "").trim();
                            const sDoc = normDocNo(s.device_doc_no);
                            const sDt = fmtDocDTShort(getDocDT(s));
                            const sPm = (s.payment_method || "").toLowerCase() || "—";
                            const sTot = Number(s.total_amount || 0);
                            const ref = String(
                              s.storno_of_unp ||
                                s.storno_of_unp_code ||
                                s.storno_of_bon_number ||
                                s.storno_of_bon_id ||
                                ""
                            ).trim();

                            return (
                              <tr
                                key={`${openKey}-storno-${idx2}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  stornoScrollPendingRef.current = unp;
                                  setStornoFocusOrigUnp(unp);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Кликни за детайли"
                              >
                                <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                                  {sDt}{sDoc ? ` • Док.${sDoc}` : ""}
                                </td>
                                <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                                  {sUnp || "—"}
                                </td>
                                <td
                                  style={{
                                    borderBottom: "1px solid #f6c7c7",
                                    padding: "4px 6px",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                    fontWeight: 800,
                                    color: "#b91c1c",
                                  }}
                                >
                                  {formatMainWithSecondary(sTot)}
                                </td>
                                <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                                  {sPm}
                                </td>
                                <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                                  {s.username || "—"}
                                </td>
                                <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                                  {ref || "—"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })
      )}

      {stornoGrouped.unresolved.length > 0 && (
        <Box sx={{ mt: 1, p: 1, borderRadius: 2, border: "1px solid #f0bcbc", bgcolor: "#fff1f2" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: "#b00020" }}>
            СТОРНО без намерен оригинал (по текущите филтри)
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                    Дата/час
                  </th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                    Фискален №
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                    Сума
                  </th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #f0bcbc", padding: "4px 6px", whiteSpace: "nowrap" }}>
                    Сторно на
                  </th>
                </tr>
              </thead>
              <tbody>
                {stornoGrouped.unresolved.map((s, idx3) => {
                  const sUnp = String(s.unp_code || s.bon_number || "").trim();
                  const sDt = fmtDocDTShort(getDocDT(s));
                  const sTot = Number(s.total_amount || 0);
                  const ref = String(
                    s.storno_of_unp || s.storno_of_unp_code || s.storno_of_bon_number || s.storno_of_bon_id || ""
                  ).trim();

                  return (
                    <tr
                      key={`unresolved-${idx3}`}
                      onClick={() => {
                        setSelectedBon(s);
                        setStornoBon(sUnp);
                        setStornoInfo(s);
                        if (ref) requestStornoFocus(ref);
                      }}
                      style={{ cursor: "pointer" }}
                      title="Кликни за детайли"
                    >
                      <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                        {sDt}
                      </td>
                      <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                        {sUnp || "—"}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #f6c7c7",
                          padding: "4px 6px",
                          whiteSpace: "nowrap",
                          textAlign: "right",
                          fontWeight: 800,
                          color: "#b91c1c",
                        }}
                      >
                        {formatMainWithSecondary(sTot)}
                      </td>
                      <td style={{ borderBottom: "1px solid #f6c7c7", padding: "4px 6px", whiteSpace: "nowrap" }}>
                        {ref || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
  </Paper>

  <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.75 }}>
    Филтърът по-горе (търсене) се прилага и тук.
  </Typography>
</Box>









        {stornoInfo ? (
          <Paper
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${UI.panelBorder}`,
              bgcolor: "#f4f6f8",
            }}
          >
            {/* Bon meta */}
            {(() => {
  const meta = extractBonMeta(stornoInfo);
  const bits = getBonMovieBits(stornoInfo);

  const movieTitle = bits.title || meta.movie || null;
  const hallLine   = bits.hall  || meta.hall  || null;
  const dateLine   = fmtBGDate(bits.show_date) || null;
  const timeLine   = fmtHM(bits.show_time) || null;

  return (
    <>
      <Typography>
        Бон: <strong>{stornoInfo.bon_number}</strong>
      </Typography>

      {!!movieTitle && (
        <Typography>Филм: <strong>{movieTitle}</strong></Typography>
      )}

      {!!hallLine && <Typography>Зала: {hallLine}</Typography>}

      {(dateLine || timeLine) && (
        <Typography>
          Дата/час (прожекция): {dateLine || "—"} {timeLine || ""}
        </Typography>
      )}

      <Typography>
        Дата/час (документ): {
          fmtDocDTFull(normDocDT(stornoInfo?.device_doc_datetime || stornoInfo?.bon_datetime))
        }
      </Typography>
      <Typography>
        Док. №: <strong>{stornoInfo?.device_doc_no ?? "—"}</strong>
      </Typography>

      {(() => {
        const stornoOf = String(
          stornoInfo?.storno_of_unp ||
            stornoInfo?.storno_of_unp_code ||
            stornoInfo?.storno_of_bon_number ||
            stornoInfo?.storno_of_bon_id ||
            ""
        ).trim();
        return stornoOf ? (
          <Typography>
            Сторно на: <strong>{stornoOf}</strong>
          </Typography>
        ) : null;
      })()}

      <Typography>
        Плащане: {stornoInfo.payment_method?.toUpperCase?.() || "—"}
      </Typography>

      {!!(Number(stornoInfo.total_amount) < 0) && (
        <Alert severity="warning" sx={{ my: 1 }}>
          Този бон е СТОРНО (отрицателен).
        </Alert>
      )}
    </>
  );
})()}

  
            {/* Items table */}
            <Typography sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>Артикули:</Typography>
              <Paper
                variant="outlined"
                sx={{
                  bgcolor: UI.panel,
                  color: UI.text,
                  overflowX: "auto",   // optional: only scroll horizontally if table is wide
                  // no maxHeight, no overflow: "auto" here
                }}
              >
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 8px" }}>
                <thead>
                  <tr>
                    {["Описание","К-во","Ед. цена","Сума"].map((h) => (
                      <th key={h}
                          style={{ textAlign: h==="Описание"?"left":"right", padding:"10px 12px",
                                   color:"#4b5563", fontWeight:700 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
  {(stornoInfo.items || []).map((it, i) => {
    const qty = Number(it.qty || 0);
    const up  = Number(it.unit_price || 0);
    const sum = qty * up;

    const picked = refundPicks[i]?.checked || false;
    const pickedQty = Math.max(0, Math.min(qty, Number(refundPicks[i]?.qty ?? qty)));

    return (
      <tr key={i}>
        {/* description + selector */}
        <td style={{ padding:"12px 14px", textAlign:"left", color:"#4b5563", background:UI.panelAlt,
                     borderTop:`1px solid ${UI.panelBorder}`, borderBottom:`1px solid ${UI.panelBorder}` }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              size="small"
              disabled={refundMode !== "partial-refund"}
              checked={picked}
              onChange={(e) => {
                setRefundPicks((m) => ({
                  ...m,
                  [i]: { checked: e.target.checked, qty: e.target.checked ? pickedQty || qty : 0 },
                }));
              }}
            />
            <span>{it.text}</span>
          </Stack>
        </td>

        {/* qty (sold) */}
        <td style={{ padding:"12px 14px", textAlign:"right", color:"#4b5563", background:UI.panelAlt,
                     borderTop:`1px solid ${UI.panelBorder}`, borderBottom:`1px solid ${UI.panelBorder}` }}>
          {qty}
        </td>

        {/* unit price */}
        <td style={{ padding:"12px 14px", textAlign:"right", color:"#4b5563", background:UI.panelAlt,
                     borderTop:`1px solid ${UI.panelBorder}`, borderBottom:`1px solid ${UI.panelBorder}` }}>
          {formatMainWithSecondary(up)}
        </td>

        {/* sum or partial qty input */}
        <td style={{ padding:"12px 14px", textAlign:"right", color:"#4b5563", background:UI.panelAlt,
                     borderTop:`1px solid ${UI.panelBorder}`, borderBottom:`1px solid ${UI.panelBorder}` }}>
          {refundMode === "partial-refund" && picked ? (
            <TextField
              size="small"
              type="number"
              inputProps={{ min: 0, max: qty, step: 1, style: { textAlign: "right", width: 72 } }}
              value={pickedQty}
              onChange={(e) => {
                const v = Math.max(0, Math.min(qty, Number(e.target.value || 0)));
                setRefundPicks((m) => ({ ...m, [i]: { ...(m[i]||{checked:true}), qty: v } }));
              }}
            />
          ) : (
            `${formatMainWithSecondary(sum)}`
          )}
        </td>
      </tr>
    );
  })}
</tbody>

              </table>
            </Paper>
          </Paper>
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Въведете номер на бон или изберете от списъка горе.
          </Typography>
        )}
      </Box>
  
      {/* FOOTER (outside scroll) */}
      <Paper
        sx={{
          p: 1,
          borderRadius: 2,
          border: `1px solid ${UI.panelBorder}`,
          bgcolor: "#f4f6f8",
          display: "grid",
          gap: 0.75,
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          Общо: <strong>{Number(stornoInfo?.total_amount || 0).toFixed(2)} лв</strong>
        </Typography>
  
        <Autocomplete
  size="small"
  freeSolo
  options={STORNO_REASON_LABELS}
  value={stornoReason}
  onChange={(_, val) => setStornoReason(val || "")}
  onInputChange={(_, val) => setStornoReason(val || "")}

  // ✅ ensure it portals to <body> and sits above the fixed receipt panel
  disablePortal={false}
  slotProps={{
    popper: {
      sx: { zIndex: 2000 }, // RightReceipt uses ~1000; this goes above it
      modifiers: [
        { name: "flip", enabled: true },                // open upward if there’s no space
        { name: "preventOverflow", enabled: true, options: { rootBoundary: "document", altBoundary: true, tether: true } },
      ],
    },
    paper: {
      sx: {
        maxHeight: 280,          // ✅ internal scroll for long lists
        overflowY: "auto",
      },
    },
  }}
  ListboxProps={{ style: { maxHeight: 280, overflowY: "auto" } }} // older MUI style prop fallback
  renderInput={(params) => (
    <TextField
      {...params}
      label="Причина за сторно"
      size="small"
      sx={{
        bgcolor: "#fff",
        borderRadius: 1,
        "& .MuiInputBase-root": { height: 40 },
      }}
      
    />
    
  )}
  
/>
<Stack direction={{ xs:"column", sm:"row" }} spacing={1} sx={{ mt: .5 }}>
  <TextField
    label="Режим"
    size="small"
    select
    SelectProps={{ native: true }}
    value={refundMode}
    onChange={(e) => setRefundMode(e.target.value)}
    sx={{ minWidth: 220, bgcolor:"#fff", borderRadius:1 }}
  >
    <option value="full-storno">Пълно СТОРНО (целият бон)</option>
    <option value="partial-refund">Частично ВРЪЩАНЕ (избраните артикули)</option>
    <option value="full-refund">Пълно ВРЪЩАНЕ (целият бон)</option>
  </TextField>
</Stack>




{refundMode === "partial-refund" && (
  <Typography sx={{ mt: .5 }}>
    Сума за връщане (избрани):{" "}
    <strong>
      {(() => {
        let total = 0;
        (stornoInfo.items || []).forEach((it, i) => {
          const pick = refundPicks[i];
          if (pick?.checked && (pick.qty || 0) > 0) {
            total += Number(it.unit_price || 0) * Number(pick.qty || 0);
          }
        });
        return total.toFixed(2);
      })()} лв
    </strong>
  </Typography>
)}

  
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            disabled={stornoBusy || isLoadedBonStorno}
            onClick={() => runStorno(stornoInfo?.payment_method || "cash")}
            sx={{ bgcolor: "#d32f2f" }}
          >
            СТОРНИРАЙ (С ОРИГИНАЛНИЯ МЕТОД)
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={stornoBusy || isLoadedBonStorno}
            onClick={() => runStorno("cash")}
          >
            СТОРНО КЕШ
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={stornoBusy || isLoadedBonStorno}
            onClick={() => runStorno("card")}
          >
            СТОРНО КАРТА
          </Button>


          <Divider flexItem orientation="vertical" sx={{ mx: .5 }} />

<Button
  variant="text"
  size="small"
  onClick={async () => {
    if (!stornoInfo?.bon_number) return;
    try {
      await dbMarkStornoByUnp(stornoInfo.bon_number, stornoReason);
      setSnackbar({ open:true, severity:"success", message:"Маркиран като сторно в базата." });
      loadRecentBons();
    } catch(e){
      setSnackbar({ open:true, severity:"error", message:`Грешка при корекция: ${e.message}` });
    }
  }}
>
  🛠 Маркирай СТОРНО (DB)
</Button>

<Button
  variant="text"
  size="small"
  onClick={async () => {
    if (!stornoInfo?.bon_number) return;
    const tot = Number(window.prompt("Нова сума (напр. 12.50):", Number(stornoInfo.total_amount||0).toFixed(2)));
    if (!Number.isFinite(tot)) return;
    const pm = window.prompt("Метод (cash/card):", (stornoInfo.payment_method||"cash")).toLowerCase();
    try {
      await dbFixTotals(stornoInfo.bon_number, tot, pm);
      setSnackbar({ open:true, severity:"success", message:"Обновен тотал/метод в базата." });
      loadStornoBon();
      loadRecentBons();
    } catch(e){
      setSnackbar({ open:true, severity:"error", message:`Грешка при корекция: ${e.message}` });
    }
  }}
>
  🛠 Коригирай сума/метод (DB)
</Button>
</Stack>




      </Paper>



      <Dialog
  open={deviceBrowserOpen}
  onClose={() => setDeviceBrowserOpen(false)}
  fullWidth
  maxWidth="md"
>
  <DialogTitle>
    Данни от фискалното устройство (по текущите филтри)
  </DialogTitle>

  <DialogContent dividers sx={{ p: 0 }}>
    <Box sx={{ px: 1, py: .75, borderBottom: '1px solid #eee', display:'flex', gap:1 }}>
      <Button size="small" variant="outlined" onClick={openDeviceBrowser} disabled={deviceBrowserBusy}>
        {deviceBrowserBusy ? "Зареждане…" : "Обнови"}
      </Button>
      <Typography variant="caption" sx={{ alignSelf:'center', opacity:.75 }}>
        Показва до {Math.max(100, Number(stornoLimit)||500)} реда (де-дуплирани).
      </Typography>
    </Box>

    <Box sx={{ maxHeight: 520, overflow: 'auto', p: 1 }}>
      {deviceBrowserRows.length === 0 ? (
        <Typography variant="body2" sx={{ opacity:.75, p:1 }}>
          Няма резултати.
        </Typography>
      ) : (
        deviceBrowserRows.map((b) => {
          const unp = b.bon_number || b.unp || "";
          const dt  = fmtDocDTShort(normDocDT(getDocDT(b)));
          const tot = Number(b.total_amount || 0);
          const pm  = b.payment_method ? String(b.payment_method).toUpperCase() : "";
          const doc = b.device_doc_no ?? "—";
          const movieLine = bonMovieLine(b);

          return (
            <Paper
              key={`${unp}-${doc}`}
              variant="outlined"
              sx={{ p: .75, mb: .5, display:'grid', gridTemplateColumns:'auto 1fr auto', gap:1 }}
            >
              <Typography variant="caption" sx={{ opacity:.75 }}>{dt}</Typography>
              <Box sx={{ minWidth:0 }}>
                <Typography variant="body2" sx={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  №{unp} • Док.{doc} • {pm}
                </Typography>
                {movieLine && (
                  <Typography variant="caption" sx={{ display:'block', color:'#64748b', mt:-.25, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {movieLine}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ fontWeight:800 }}>
                {formatMainWithSecondary(tot)}
              </Typography>
            </Paper>
          );
        })
      )}
    </Box>
  </DialogContent>




  <DialogActions>
    <Button onClick={() => setDeviceBrowserOpen(false)}>Затвори</Button>


 




  </DialogActions>
</Dialog>


    </Box>
  );
  


  // BAR persistence
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(BAR_CART_KEY);
      if (saved) {
        const arr = JSON.parse(saved);
        setBarCart(new Map(arr.map((x) => [x.article_id, x])));
      }
    } catch {}
  }, []);
  useEffect(() => {
    const arr = Array.from(barCart.values());
    sessionStorage.setItem(BAR_CART_KEY, JSON.stringify(arr));
  }, [barCart]);

  useEffect(() => {
    if (activeTab !== "bar" || !cinemaId) return;
    (async () => {
      const list = await fetchJsonSoft(`${API}/api/bar/articles/${cinemaId}`);
      setBarArticles(Array.isArray(list) ? list : []);
    })();
  }, [activeTab, cinemaId]);

  // restore zoom/scroll
  useEffect(() => {
    if (!selectedShowtime?.showtime_id) return;
    sessionStorage.setItem(keyZoom(selectedShowtime.showtime_id), String(zoom));
  }, [zoom, selectedShowtime]);

  useEffect(() => {
    if (!selectedShowtime?.showtime_id) return;
    const saved = parseFloat(sessionStorage.getItem(keyZoom(selectedShowtime.showtime_id)));
    if (!Number.isNaN(saved)) setZoom(clamp(saved, 0.5, 3));
  }, [selectedShowtime, activeTab]);

  useEffect(() => {
    if (activeTab !== "tickets") return;
    if (!gridHostRef.current || !selectedShowtime?.showtime_id) return;
    const saved = sessionStorage.getItem(keyScroll(selectedShowtime.showtime_id));
    if (!saved) return;
    const pos = JSON.parse(saved);
    requestAnimationFrame(() => {
      if (gridHostRef.current) {
        gridHostRef.current.scrollLeft = pos.left || 0;
        gridHostRef.current.scrollTop = pos.top || 0;
      }
    });
  }, [activeTab, selectedShowtime, seats]);

  const handleWheelZoom = (e) => {
    if (!gridHostRef.current || !e.ctrlKey) return;
    e.preventDefault();
    e.stopPropagation();
    const host = gridHostRef.current;
    const rect = host.getBoundingClientRect();
    const contentX = e.clientX - rect.left + host.scrollLeft;
    const contentY = e.clientY - rect.top + host.scrollTop;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    setZoom((z) => {
      const nextZ = clamp(z * factor, 0.5, 3);
      const prevScale = fitScale * z;
      const nextScale = fitScale * nextZ;
      const ratio = nextScale / prevScale || 1;
      host.scrollLeft = contentX * ratio - (e.clientX - rect.left);
      host.scrollTop = contentY * ratio - (e.clientY - rect.top);
      return nextZ;
    });
  };
  const resetZoom = () => setZoom(1);

  const hallTitle =
    selectedShowtime?.hall_name ??
    selectedShowtime?.hall ??
    selectedShowtime?.HALL_NAME ??
    hallName ??
    (Array.isArray(seats) && seats.length
      ? seats[0].hall_name ?? seats[0].HALL_NAME ?? null
      : null);

  // fallback cinema name if missing
  useEffect(() => {
    if (!cinemaId) return;
    if (cinemaName) return;
    const st = showtimes.find((s) => s?.cinema_name || s?.CINEMA_NAME || s?.cinema);
    if (st) {
      setCinemaName(st.cinema_name ?? st.CINEMA_NAME ?? st.cinema);
      return;
    }
  }, [cinemaId, showtimes, cinemaName]);

  // Default seat_type for new selections
  const defaultSeatType = useCallback(() => {
    if (!seatPricing?.length) return null;
    const std =
      seatPricing.find((p) => (p.seat_type || "").toUpperCase() === "STANDARD") ||
      seatPricing.find((p) => (p.seat_type || "").toUpperCase() === "NORMAL");
    return (std?.seat_type || seatPricing[0]?.seat_type || null) ?? null;
  }, [seatPricing]);

  // Reservations search helpers
  // По подразбиране не филтрираме по конкретна дата — показваме бъдещите
  const [resUseDate, setResUseDate] = useState(false);
  const [resDate, setResDate] = useState(dayjs());





  const norm = (s) => String(s ?? "").toLowerCase();
  const digits = (s) => String(s ?? "").replace(/\D/g, "");

  
  const searchReservations = useCallback(async () => {
    if (!cinemaId) return;

    const params = new URLSearchParams({ cinema_id: String(cinemaId) });

    const q = (resSearch || "").trim();
    if (q) {
      params.set("q", q);
      const qDigits = digits(q);
      if (q.includes("@")) params.set("email", q);
      if (qDigits.length >= 5) params.set("phone", qDigits);
    }

    if (resUseDate && resDate && resDate.isValid()) {
      params.set("date", resDate.format("YYYY-MM-DD"));
    }

    // backend filters + sorts (we still do a safe client sort too)
    params.set("status", resStatus || "active");          // active | sold | cancelled | all
    params.set("sort", resSortKey || "reservation");      // reservation | showtime
    params.set("dir", resSortDir || "desc");              // asc | desc

    let list = await fetchJsonSoft(`${API}/api/reservations/search?${params}`);
    list = Array.isArray(list) ? list : [];

    // normalize status to active|sold|cancelled
    list = list.map((r) => ({ ...r, _status: normResStatus(r.status || r.STATUS) }));

    // if NOT filtering by date -> keep only future showtimes
    if (!resUseDate) {
      const now = dayjs();
      list = list.filter((r) => {
        const dt = parseShowDT(r);
        return !dt || dt.isAfter(now) || dt.isSame(now, "day");
      });
    }

    // extra client filter (safe)
    if (resStatus !== "all") {
      list = list.filter((r) => r._status === resStatus);
    }

    // extra client text filter (safe)
    if (q) {
      const qNorm = norm(q);
      const qDigits = digits(q);
      list = list.filter((r) => {
        const hay = [
          r.customer_name, r.customer_email, r.customer_phone, r.customer_note, r.note,
          r.movie_title, r.hall_name, r.show_date, r.show_time,
          Array.isArray(r.seat_labels) ? r.seat_labels.join(", ") : ""
        ].filter(Boolean).map(norm).join(" ");
        const hitsText = hay.includes(qNorm);
        const hitsPhone = qDigits ? digits(r?.customer_phone).includes(qDigits) : false;
        const hitsEmail = r?.customer_email ? norm(r.customer_email).includes(qNorm) : false;
        return hitsText || hitsPhone || hitsEmail;
      });
    }

    // detect new ACTIVE reservations (light + blink for a while)
    if (resStatus === "active" || resStatus === "all") {
      const activeIds = new Set(list.filter((r) => r._status === "active").map((r) => r.id));

      if (!resNewInitRef.current) {
        prevActiveResIdsRef.current = activeIds;
        resNewInitRef.current = true;
      } else {
        const newOnes = [...activeIds].filter((id) => !prevActiveResIdsRef.current.has(id));
        if (newOnes.length) {
          setNewResIds((prev) => {
            const next = new Set(prev);
            newOnes.forEach((id) => next.add(id));
            return next;
          });

          newOnes.forEach((id) => {
            const old = newResTimersRef.current.get(id);
            if (old) clearTimeout(old);

            const t = setTimeout(() => {
              setNewResIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
              newResTimersRef.current.delete(id);
            }, 20000);

            newResTimersRef.current.set(id, t);
          });
        }
        prevActiveResIdsRef.current = activeIds;
      }
    }

    // client sort (works even if backend sorts; especially useful for "all")
    const dirMul = (resSortDir === "asc") ? 1 : -1;
    list.sort((a, b) => {
      const da = (resSortKey === "showtime") ? parseShowDT(a) : parseOrderDT(a);
      const db = (resSortKey === "showtime") ? parseShowDT(b) : parseOrderDT(b);
      if (da && db) return (da.valueOf() - db.valueOf()) * dirMul;
      if (da) return -1 * dirMul;
      if (db) return 1 * dirMul;
      return 0;
    });

    setResResults(list);
  }, [cinemaId, resSearch, resDate, resStatus, resUseDate, resSortKey, resSortDir]);
  

  useEffect(() => {
    const t = setTimeout(() => {
      searchReservations();
    }, 200);
    return () => clearTimeout(t);
  }, [searchReservations]);


  // refresh immediately when opening the Reservations tab
  useEffect(() => {
    if (activeTab === "reservations") searchReservations();
  }, [activeTab, searchReservations]);


  useEffect(() => {
    if (activeTab !== "reservations") return;
    const id = setInterval(searchReservations, 15000);
    return () => clearInterval(id);
  }, [activeTab, searchReservations]);

  // showtimes helpers
  const showtimesFor = (movie) => {
    if (!movie) return [];
    const ymd = filterDate.format("YYYY-MM-DD");
    return showtimes
      .filter((s) => s.movie_title === movie.MOVIE_NAME && s.show_date === ymd)
      .sort((a, b) => a.show_time.localeCompare(b.show_time));
  };

  const nearestShowtimeFor = (movie) => {
    const list = showtimesFor(movie);
    if (!list.length) return null;
    const isToday = filterDate.isSame(dayjs(), "day");
    if (!isToday) return list[0];
    const nowHM = dayjs().format("HH:mm:ss");
    const upcoming = list.filter((s) => s.show_time >= nowHM);
    return upcoming[0] || list[list.length - 1] || list[0];
  };

  const moviesForDay = useMemo(() => {
    const ymd = filterDate.format("YYYY-MM-DD");
    return movies.filter((m) =>
      showtimes.some((s) => s.movie_title === m.MOVIE_NAME && s.show_date === ymd)
    );
  }, [movies, showtimes, filterDate]);


    // Sorted by showtime for the selected date.
    // If today: next upcoming showtime first; past-only movies sink to bottom.
    const moviesForDaySorted = useMemo(() => {
      const isToday = filterDate.isSame(dayjs(), "day");
      const nowHM = dayjs().format("HH:mm:ss");
  
      const rows = moviesForDay.map((m) => {
        const list = showtimesFor(m); // already sorted asc by time
        if (!list.length) return { m, key: "99:99:99" };
  
        if (isToday) {
          const next = list.find((s) => s.show_time >= nowHM);
          // No upcoming? push to the end
          return { m, key: next ? next.show_time : "99:99:99" };
        }
        // Other days: use first showtime
        return { m, key: list[0].show_time };
      });
  
      rows.sort((a, b) => {
        const t = a.key.localeCompare(b.key);
        if (t !== 0) return t;
        // tie-break by title
        return String(a.m.MOVIE_NAME || "").localeCompare(String(b.m.MOVIE_NAME || ""), "bg", {
          sensitivity: "accent",
          numeric: true,
        });
      });
      return rows.map((r) => r.m);
    }, [moviesForDay, filterDate, showtimesFor]);
  
    // If you ever need just the names:
    // const movieNamesSorted = useMemo(() => moviesForDaySorted.map(m => m.MOVIE_NAME), [moviesForDaySorted]);





  
  const onSelectMovie = async (m) => {
    setSelectedMovie(m);
    setSelectedShowtime(null);
    clearSelection();
    setSeats([]);
    setStatusMap({});
    setSelectedSeatType(null);
    const s = nearestShowtimeFor(m);
     if (s) {
         await loadSeats(s);
         //setSeatPaneOpen(true); // <-- open the right-side selector
       }

      };


   useEffect(() => {
       if (!selectedMovie) return;
       if (currentReservation) return;
       if (selectedShowtime) return;      // ✅ NEW: don’t auto-switch if user already picked one

       const s = nearestShowtimeFor(selectedMovie);
       if (s && selectedShowtime?.showtime_id !== s.showtime_id) loadSeats(s);
       // eslint-disable-next-line
     }, [selectedMovie]);




  const maxRow = useMemo(
    () => (seats.length ? Math.max(...seats.map((s) => Number(s.seat_row) || 0)) : 0),
    [seats]
  );

  const { seatByCoord, rowMeta, maxColOverall } = useMemo(() => {
    const byCoord = {};
    const meta = {};
    let maxCol = 0;
    for (const seat of seats) {
      const row = Number(seat.seat_row);
      const col = Number(seat.seat_column);
      if (!row || !col) continue;
      const coord = `${row}-${col}`;
      byCoord[coord] = seat;
      maxCol = Math.max(maxCol, col);
      if (!meta[row]) {
        meta[row] = {
          minCol: col,
          maxCol: col,
          align: (seat.row_alignment || "left").toLowerCase(),
          row_label: seat.row_label || null,
        };
      } else {
        meta[row].minCol = Math.min(meta[row].minCol, col);
        meta[row].maxCol = Math.max(meta[row].maxCol, col);
        if (!meta[row].row_label && seat.row_label) meta[row].row_label = seat.row_label;
      }
    }
    return { seatByCoord: byCoord, rowMeta: meta, maxColOverall: maxCol };
  }, [seats]);

  // fit calc
  const recalcFitSafe = useCallback(() => {
    const host = gridHostRef.current;
    if (!host) return;
    if (host.clientWidth < 150 || host.clientHeight < 150) {
      requestAnimationFrame(() => {
        const h = gridHostRef.current;
        if (!h) return;
        if (h.clientWidth >= 150 && h.clientHeight >= 150) recalcFitSafe();
      });
      return;
    }
    const cols = Math.max(0, maxColOverall);
    const rows = Math.max(0, maxRow);

    const gridW = cols * seatSize + (cols - 1) * gap + leftLabelW + rightLabelW;
    const gridH = screenBarH + rows * seatSize + (rows - 1) * rowGap;

    const availW = Math.max(0, host.clientWidth - 16);
    const availH = Math.max(0, host.clientHeight - 16);

    const widthScale = gridW > 0 ? availW / gridW : 1;
    const heightScale = gridH > 0 ? availH / gridH : 1;

    if (!Number.isFinite(widthScale) || !Number.isFinite(heightScale)) return;

    const rawFit = Math.min(widthScale, heightScale) * FIT_BONUS;
    const nextFit = Math.max(0.1, Math.min(rawFit, MAX_FIT));
    setFitScale(nextFit);
  }, [maxColOverall, maxRow, seatSize, gap, rowGap, leftLabelW, rightLabelW, screenBarH]);

  useEffect(() => {
    recalcFitSafe();
    const host = gridHostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => recalcFitSafe());
    ro.observe(host);
    return () => ro.disconnect();
  }, [recalcFitSafe, seats]);

  useEffect(() => {
    if (activeTab !== "tickets") return;
    const id1 = requestAnimationFrame(() => recalcFitSafe());
    const id2 = setTimeout(() => recalcFitSafe(), 80);
    return () => {
      cancelAnimationFrame(id1);
      clearTimeout(id2);
    };
  }, [activeTab, recalcFitSafe, maxColOverall, maxRow]);

  const onSeatClick = (row, col) => {
    const seat = seatByCoord[`${row}-${col}`];
    if (!seat) return;
    const id = seat.seat_id;
    const current = statusMap[id];
    if (current === "reserved" || current === "sold") return;

    const seatNumber = seat.label_text || `${col}`;
    const rowLabel = seat.row_label || `Ред ${row}`;
    const label = `${rowLabel} — Място ${seatNumber}`;

    if (currentReservation && !selectedSeats.has(id)) {
      const firstSelectedId = Array.from(selectedSeats.keys())[0];
      if (firstSelectedId) {
        setSeatMoves((mv) => [...mv, { from_seat_id: firstSelectedId, to_seat_id: id }]);
        const prev = selectedSeats.get(firstSelectedId);
        const next = new Map(selectedSeats);
        next.delete(firstSelectedId);
        next.set(id, {
          label,
          seat_type: prev?.seat_type || selectedSeatType || defaultSeatType(),
        });
        setSelectedSeats(next);
        return;
      }
    }

    setSelectedSeats((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
         const autoType =
           selectedSeatType ||                        // cashier override (pill)
           pickSeatTypeFor(seat, seatPricing) ||      // database seat’s own type/price
           defaultSeatType();                         // last resort fallback                    // last resort
         next.set(id, { label, seat_type: autoType });
      }
      return next;
    });
  };

  // pricing/receipt data (by seat_type)
  const priceBySeatType = useMemo(() => {
    const map = new Map();
    seatPricing.forEach((p) => {
      const key = p.seat_type || p.category; // tolerate older rows
      if (key) map.set(key, parseFloat(p.price));
    });
    return map;
  }, [seatPricing]);

  // --- Movie-specific AUTO pricing (does NOT affect pills / manual pricing) ---
const normalizeSeatTypeStr = (t) => String(t || "").trim().toLowerCase();

const seatTypeIsBalcony = (t) => {
  const s = normalizeSeatTypeStr(t);
  return s.includes("балкон") || s.includes("balcony") || s.includes("balkon") || s.includes("balcon");
};

const seatTypeIsVip = (t) => {
  const s = normalizeSeatTypeStr(t);
  return s === "vip" || s.includes("vip") || s.includes("вип");
};

const seatTypeIsStandard = (t) => {
  const s = normalizeSeatTypeStr(t);
  return s.includes("стандарт") || s === "standard" || s.includes("standard");
};

const autoPriceForSeatType = (seat_type) => {
  const fallback = priceBySeatType.get(seat_type) || 0;
  if (!movieOverride?.exists) return fallback;

  const base = Number(movieOverride?.base_price);
  if (!Number.isFinite(base)) return fallback;

  const balExtra = Number(movieOverride?.balcony_extra);
  const vipExtra = Number(movieOverride?.vip_extra);

  if (seatTypeIsBalcony(seat_type)) return base + (Number.isFinite(balExtra) ? balExtra : 0);
  if (seatTypeIsVip(seat_type))     return base + (Number.isFinite(vipExtra) ? vipExtra : 0);
  if (seatTypeIsStandard(seat_type)) return base;

  return fallback;
};


  const effectiveSeatPrice = (seat_id, seat_type, pricing_mode = "auto") => {
    const locked = lockedPriceBySeatId.get(seat_id);
    if (Number.isFinite(locked)) return locked;

    if (pricing_mode === "manual") {
      return priceBySeatType.get(seat_type) || 0;
    }
    return autoPriceForSeatType(seat_type);
  };
// Helpers (ids are inconsistent across endpoints)
  const getShowtimeId = (s) => Number(s?.showtime_id || s?.SHOWTIME_ID || s?.id || 0);
  const getHallId = (s) => Number(s?.hall_id || s?.HALL_ID || 0);

// load seats
  const loadSeats = useCallback(
    async (s) => {
      try {
        setSelectedShowtime(s);
        clearSelection();

        // showtime price has highest priority, then movie-cinema price
        try {
          setMovieOverride(null);
          const sid = Number(s?.showtime_id || s?.SHOWTIME_ID || s?.id || 0);
          if (Number.isFinite(sid) && sid > 0) {
            const showOv = await fetchJsonSoft(`${API}/api/showtime-pricing/by-showtime/${sid}`);
            if (showOv && showOv.exists) {
              setMovieOverride(showOv);
            } else {
              const movieOv = await fetchJsonSoft(`${API}/api/movie-cinema-pricing/by-showtime/${sid}`);
              if (movieOv && movieOv.exists) setMovieOverride(movieOv);
            }
          }
        } catch (e) {
          // ignore override errors; fallback to normal seat pricing
        }

setReservationSeatIds(new Set());
        setHallName(s.hall_name || s.hall || s.HALL_NAME || null);

        const hallId = s.hall_id;

        // Hall-based pricing overrides (cinema -> hall).
        // Keeps existing movieOverride logic unchanged.
        try {
          const cid = Number(s?.cinema_id || s?.CINEMA_ID || cinemaId || 0);
          const hid = Number(hallId || 0);
          if (cid && hid) {
            const prHall = await fetchJsonSoft(`${API}/api/seat-pricing/${cid}?hall_id=${hid}`);
            if (Array.isArray(prHall) && prHall.length) setSeatPricing(prHall);
          }
        } catch {}
        const layoutUrl = `${API}/api/seats/hall/${hallId}`;
        const layout = await fetchJsonStrict(layoutUrl);
        setSeats(Array.isArray(layout) ? layout : []);

        if (!s.hall_name && !s.hall && !s.HALL_NAME && Array.isArray(layout) && layout.length) {
          setHallName(layout[0].hall_name || layout[0].HALL_NAME || null);
        }

        const stId = (s?.showtime_id || s?.SHOWTIME_ID || s?.id);
        const statusRows = await fetchJsonSoft(`${API}/api/seats-reserve/status/${stId}`);
        const map = {};
        const meta = {};
        for (const r of statusRows || []) {
          const sid = r.seat_id ?? r.SEAT_ID ?? r.SEATID;
          const st = String(r.status ?? r.STATUS ?? "").toLowerCase();
          if (!sid) continue;
          if (st === "reserved" || st === "hold" || st === "sold") map[sid] = st;
          if (st === "reserved" || st === "hold") {
            meta[sid] = {
              seat_id: sid,
              status: st,
              order_id: r.order_id ?? r.ORDER_ID ?? null,
              order_date: r.order_date ?? r.ORDER_DATE ?? null,
              reservation_group_id: r.reservation_group_id ?? r.RESERVATION_GROUP_ID ?? null,
              customer_name: r.customer_name ?? r.CUSTOMER_NAME ?? "",
              customer_phone: r.customer_phone ?? r.CUSTOMER_PHONE ?? "",
              customer_email: r.customer_email ?? r.CUSTOMER_EMAIL ?? "",
              customer_note: r.customer_note ?? r.CUSTOMER_NOTE ?? "",
            };
          }
        }
        setStatusMap(map);
        
        // compute reservation group sizes (for hover tooltips / highlighting)
        try {
          const gCount = {};
          for (const sid of Object.keys(meta || {})) {
            const g = meta[sid]?.reservation_group_id;
            if (g) gCount[g] = (gCount[g] || 0) + 1;
          }
          for (const sid of Object.keys(meta || {})) {
            const g = meta[sid]?.reservation_group_id;
            if (g) meta[sid].group_size = gCount[g] || 1;
          }
        } catch {}

        setReservedMetaMap(meta);

        // update availability for the showtime tile immediately
        try {
          const totalSeats = Array.isArray(layout)
            ? layout.filter((seat) => (seat.seat_id ?? seat.SEAT_ID ?? seat.id)).length
            : 0;
          const takenSeats = (statusRows || []).filter((r) => {
            const st = String(r.status ?? r.STATUS ?? "").toLowerCase();
            return st === "reserved" || st === "sold";
          }).length;
          const freeSeats = Math.max(0, totalSeats - takenSeats);
          const _sid = getShowtimeId(s);
          if (_sid) {
            setAvailMap((m) => {
              const n = new Map(m);
              n.set(_sid, { free: freeSeats, total: totalSeats });
              return n;
            });
          }
        } catch {}


        const savedZ = parseFloat(sessionStorage.getItem(keyZoom(stId)));
        if (!Number.isNaN(savedZ)) setZoom(clamp(savedZ, 0.5, 3));

        const savedScroll = sessionStorage.getItem(keyScroll(stId));
        if (savedScroll) {
          const pos = JSON.parse(savedScroll);
          requestAnimationFrame(() => {
            if (gridHostRef.current) {
              gridHostRef.current.scrollLeft = pos.left || 0;
              gridHostRef.current.scrollTop = pos.top || 0;
            }
          });
        }

        // if no current seat_type chosen, pick default from this cinema pricing
        setSelectedSeatType((prev) => prev ?? null);
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: "Грешка при зареждане на места", severity: "error" });
      }
    },
    [clearSelection, defaultSeatType]
  );

  // delete reservation
  const [busyDeletes, setBusyDeletes] = useState(new Set());
  const [busyPurges, setBusyPurges] = useState(new Set());

  const purgeCancelledReservation = useCallback(
    async (reservationId) => {
      if (!reservationId) return;
      if (busyPurges.has(reservationId)) return;
      setBusyPurges((s) => new Set(s).add(reservationId));

      try {
        const res = await fetch(`${API}/api/reservations/${reservationId}/cancelled`, {
          method: "DELETE",
          credentials: "same-origin",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${txt ? ` — ${txt}` : ""}`);
        }

        setSnackbar({ open: true, message: "Отменената резервация е изтрита.", severity: "success" });
        await searchReservations();
      } catch (e) {
        console.error("purgeCancelledReservation failed:", e);
        setSnackbar({
          open: true,
          message: `Грешка при изтриване. ${e?.message || ""}`,
          severity: "error",
        });
      } finally {
        setBusyPurges((s) => {
          const n = new Set(s);
          n.delete(reservationId);
          return n;
        });
      }
    },
    [busyPurges, searchReservations]
  );

  const cancelReservation = useCallback(
    async (reservationId) => {
      if (!reservationId) return;
      if (busyDeletes.has(reservationId)) return;
      setBusyDeletes((s) => new Set(s).add(reservationId));

      try {
        const res = await fetch(`${API}/api/reservations/${reservationId}/cancel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${txt ? ` — ${txt}` : ""}`);
        }

        setSnackbar({ open: true, message: "Резервацията е изтрита.", severity: "success" });

        await searchReservations();

        if (currentReservation?.id === reservationId) {
          clearSelection();
          setSeatMoves([]);
          setCurrentReservation(null);
          setReservationSeatIds(new Set());
          if (selectedShowtime) await loadSeats(selectedShowtime);
        }
      } catch (e) {
        console.error("cancelReservation failed:", e);
        setSnackbar({
          open: true,
          message: `Грешка при изтриване на резервация. ${e?.message || ""}`,
          severity: "error",
        });
      } finally {
        setBusyDeletes((s) => {
          const n = new Set(s);
          n.delete(reservationId);
          return n;
        });
      }
    },
    [
      busyDeletes,
      searchReservations,
      currentReservation,
      selectedShowtime,
      clearSelection,
      loadSeats,
    ]
  );

  const saveReservationChanges = useCallback(async () => {
    if (!currentReservation) {
      setSnackbar({ open: true, message: "Няма заредена резервация.", severity: "warning" });
      return;
    }
    if (!selectedSeats.size) {
      setSnackbar({ open: true, message: "Няма избрани места.", severity: "warning" });
      return;
    }

    try {
      await fetchJsonStrict(`${API}/api/reservations/${currentReservation.id}/cancel`, {
        method: "POST",
      });

      const groups = new Map(); // key = "12.00", value = seat_ids[]

      selectedSeats.forEach((v, seat_id) => {
        const locked = lockedPriceBySeatId.get(seat_id);
        const p = effectiveSeatPrice(seat_id, v.seat_type, v.pricing_mode || "auto");
      
        const key = Number(p).toFixed(2);
        const arr = groups.get(key) || [];
        arr.push(seat_id);
        groups.set(key, arr);
      });
      
      for (const [key, seat_ids] of groups.entries()) {
        const price = Number(key);
        await fetchJsonStrict(`${API}/api/seats-reserve/reserve`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            showtime_id: currentReservation.showtime.showtime_id,
            seat_ids,
            cinema_id: cinemaId,
            customer_name: currentReservation.customer?.name || "POS",
            customer_phone: currentReservation.customer?.phone || null,
            customer_note: currentReservation.customer?.note || null,
            price,
          }),
        });
      }
      

      setSnackbar({ open: true, message: "Резервацията е обновена.", severity: "success" });
      await loadSeats(currentReservation.showtime);

      searchReservations();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: "Грешка при обновяване на резервация.", severity: "error" });
    }
  }, [
    currentReservation,
    selectedSeats,
    priceBySeatType,
    cinemaId,
    loadSeats,
    searchReservations,
  ]);

  const loadReservationIntoBon = useCallback(
    async (reservationId) => {
      try {
        const r = await fetchJsonStrict(`${API}/api/reservations/${reservationId}`);
        if (!r) return;
  
        // ⚠️ кажи на ефекта да НЕ ресетва при тази смяна на датата
        skipDateResetRef.current = true;
        setFilterDate(dayjs(r.showtime.show_date));
  
        // по възможност използвай каноничния обект от showtimes, за да се попълни dropdown-ът
        const sCanonical =
          showtimes.find(x => x.showtime_id === r.showtime?.showtime_id) || r.showtime;
  
        await loadSeats(sCanonical);
  
             // ✅ show the seat-map immediately (you were not opening it)
     setSeatPaneOpen(true);
        // ... останалото ви съдържание остава както е ...
  

        const nextSel = new Map();
        (r.seats || []).forEach((seat) => {
          const seatNumber = seat.label_text || `${seat.seat_column}`;
          const rowLabel = seat.row_label || `Ред ${seat.seat_row}`;
          const label = `${rowLabel} — Място ${seatNumber}`;
           nextSel.set(seat.seat_id, {
              label,
               // prefer the seat's own DB type if priced; else fall back to UI/default
               seat_type:
               pickSeatTypeFor(seat, seatPricing) || selectedSeatType || defaultSeatType(),
             });
        });
        setSelectedSeats(nextSel);

        setReservationSeatIds(new Set((r.seats || []).map((s) => s.seat_id)));

        const customer =
  r.customer || {
    name: r.customer_name || r.name || null,
    phone: r.customer_phone || null,
    email: r.customer_email || null,
    note: r.customer_note || r.note || null,
  };

setCurrentReservation({
  id: r.id,
  status: normResStatus(r.status || r.STATUS),
  customer,
  showtime: r.showtime,
  seats: r.seats || [],
});


        const mv = movies.find((m) => m.MOVIE_NAME === r.showtime?.movie_title);
        if (mv) setSelectedMovie(mv);

        setSelectedSeatType(null);

        // NEW: jump to Tickets and show the seats immediately (no auto-opening receipt on mobile)
        setActiveTab("tickets");
        if (isSM) toggleReceipt(true);
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: "Неуспешно зареждане на резервация.", severity: "error" });
      }
    },
    [isSM, movies, defaultSeatType, loadSeats, toggleReceipt, selectedSeatType]
  );


  const lockedPriceBySeatId = useMemo(() => {
    const m = new Map();
    (currentReservation?.seats || []).forEach((s) => {
      const n = s?.price == null ? NaN : Number(s.price); // handles DECIMAL strings
      if (Number.isFinite(n)) m.set(s.seat_id, n);
    });
    return m;
  }, [currentReservation]);
  



  const receiptLines = useMemo(() => {
    return Array.from(selectedSeats.entries()).map(([seat_id, { label, seat_type, pricing_mode }]) => {
      const price = effectiveSeatPrice(seat_id, seat_type, pricing_mode || "auto");
      return { seat_id, label, seat_type, pricing_mode: pricing_mode || "auto", price };
    });
  }, [selectedSeats, priceBySeatType, lockedPriceBySeatId, movieOverride]);
const movieTitle = selectedMovie?.MOVIE_NAME || selectedShowtime?.movie_title || null;

  const showDate = selectedShowtime?.show_date || selectedShowtime?.SHOW_DATE || null;
  const showTime = selectedShowtime?.show_time || selectedShowtime?.SHOW_TIME || null;
  const theaterTitle = cinemaName || "Кино";

  const selectedSeatLabels = useMemo(() => receiptLines.map((l) => l.label), [receiptLines]);
  const ticketTotalBGN = receiptLines.reduce((sum, l) => sum + l.price, 0);

  // BAR derived
  const barLines = useMemo(() => Array.from(barCart.values()), [barCart]);
  const barTotalBGN = useMemo(
    () => barLines.reduce((s, x) => s + x.price * x.qty, 0),
    [barLines]
  );

  const totalBGN = ticketTotalBGN + barTotalBGN;
  const previewTotalBGN = Number(bonPreview?.total);
  const finalTotalBGN = Number.isFinite(previewTotalBGN) ? previewTotalBGN : totalBGN;
  const totalEUR = finalTotalBGN / EUR_RATE;

// ---- UNP PREVIEW (right receipt)
const ticketsPreview = useMemo(() => {
  if (!selectedShowtime) return [];
  return receiptLines.map((l) => ({
    showtime_id: (selectedShowtime.showtime_id || selectedShowtime.SHOWTIME_ID),
    seat_id: l.seat_id,
    seat_label: l.label,
    seat_type: l.seat_type,
    price: Number(l.price || 0),
  }));
}, [selectedShowtime, receiptLines]);

const barPreview = useMemo(
  () => barLines.map((b) => ({ article_id: b.article_id, qty: b.qty })),
  [barLines]
);

// Fetch a preview UNP whenever the cart changes (tickets/bar)
useEffect(() => {
  const hasItems = (ticketsPreview.length + barPreview.length) > 0;
  if (!cinemaId || !hasItems) { setBonPreview(null); setPromoEffectPreview(null); return; }

  const ctrl = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API}/api/pos/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({
          cinema_id: Number(cinemaId),
          payment_method: "cash",      // preview only; real method is chosen on sale
          tickets: ticketsPreview,
          bar: barPreview,
          promo_id: selectedPromo?.promo_id || selectedPromo?.id || null,
          promo_code: selectedPromo?.code || null,
          promo_scope: selectedPromo?.scope || "all",
        }),
      });
      const data = await res.json().catch(() => null);
      setBonPreview(data?.bonPreview || null);
      setPromoEffectPreview(data?.promo_effect || null);
    } catch {
      setBonPreview(null);
      setPromoEffectPreview(null);
    }
  })();

  return () => ctrl.abort();
}, [cinemaId, ticketsPreview, barPreview, selectedPromo]);


async function handleDuplicateSelected() {
  if (!selectedBon) {
    setSnackbar({ open: true, severity: "info", message: "Изберете бон от списъка." });
    return;
  }

  const bon_number =
    selectedBon.bon_number || selectedBon.unp || selectedBon.fiscal_unp || "";
  const payload = {
    cinema_id: Number(cinemaId),
    bon_number,
    unp: bon_number,   // някои бекенд версии очакват точно `unp`
    count: 1
  };

  // helper за проба с даден URL през твоя apiJson (с Bearer + credentials)
  const tryDuplicate = (base) =>
    apiJson(`${base}/api/pos/duplicate`, {
      method: "POST",
      body: JSON.stringify(payload),
    });

  try {
    // 1) опитай към API_BASE
    await tryDuplicate(API_BASE);
    setSnackbar({ open: true, severity: "success", message: "Дубликат отпечатан." });
  } catch (e1) {
    // 2) ако е auth проблем → проба към API (другия домейн/прокси с cookie)
    const isAuthErr = /401|auth required|unauthor/i.test(String(e1?.message || ""));
    if (!isAuthErr) {
      setSnackbar({ open: true, severity: "error", message: `Неуспешен дубликат: ${e1.message}` });
      return;
    }
    try {
      await tryDuplicate(API); // втори опит
      setSnackbar({ open: true, severity: "success", message: "Дубликат отпечатан." });
    } catch (e2) {
      setSnackbar({ open: true, severity: "error", message: `Неуспешен дубликат: ${e2.message}` });
    }
  }
}

// helpers (place near other helpers)
const pmBucket = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return ""; // treat empty as unknown
  if (/(card|pos|terminal|bank[_\s-]?card|debit|credit)/.test(s)) return "card";
  if (/(cash|cash[_\s-]?payment|kesh|кеш)/.test(s)) return "cash";
  return s; // leave as-is for rare methods (voucher, etc.)
};

const isStornoFlexible = (row) => {
  const n = Number(row?.total_amount || 0);
  if (Number.isFinite(n) && n < 0) return true;
  if (row?.storno_of_bon_id) return true;
  if (row?.is_storno === true || String(row?.type || "").toLowerCase() === "storno") return true;
  return false;
};

// NEW state to track missing records on either side
const [missingInDB, setMissingInDB] = React.useState(new Set());
const [missingInDevice, setMissingInDevice] = React.useState(new Set());

useEffect(() => {
  if (stornoSource !== "both") {
    setMismatches(new Set());
    setMissingInDB(new Set());
    setMissingInDevice(new Set());
    return;
  }

  const dev = asPos(stornoRecent || []);
  const db  = asPos(stornoRecentDB || []);
  const mism = new Set();
  const missDB = new Set();
  const missDev = new Set();

  const keys = new Set([...dev.keys(), ...db.keys()]);
  for (const k of keys) {
    const A = dev.get(k);
    const B = db.get(k);

    if (!A && B) { missDev.add(k); continue; }
    if (A && !B) { missDB.add(k); continue; }

    // both present → compare with tolerance
    const totA = Number(A.total_amount || 0);
    const totB = Number(B.total_amount || 0);
    const EPS = 0.01; // 1 stotinka

    const pmA = pmBucket(A.payment_method);
    const pmB = pmBucket(B.payment_method);

    const storA = isStornoFlexible(A);
    const storB = isStornoFlexible(B);

    const totalsDiff = !(Number.isFinite(totA) && Number.isFinite(totB)) ||
                       Math.abs(totA - totB) > EPS;
    const pmDiff = pmA !== pmB && !(pmA === "" && pmB === ""); // tolerate unknown vs empty
    const storDiff = storA !== storB;

    if (totalsDiff || pmDiff || storDiff) mism.add(k);
  }

  setMismatches(mism);
  setMissingInDB(missDB);
  setMissingInDevice(missDev);
}, [stornoSource, stornoRecent, stornoRecentDB]);



  // Non-fiscal receipt print (device first, then browser)
  async function printNonFiscalOnDevice() {
    const lines = [];
    const hdr = cinemaHdr || {};
    if (hdr.name) lines.push(hdr.name);
    const addrLine = [hdr.location || hdr.address, hdr.city].filter(Boolean).join(", ");
    if (addrLine) lines.push(addrLine);
    if (hdr.eik_code) lines.push(`ЕИК: ${hdr.eik_code}`);
    if (hdr.zdds_number) lines.push(`ЗДДС: ${hdr.zdds_number}`);
    if (hdr.fiscal_id) lines.push(`ФУ: ${hdr.fiscal_id}`);

        lines.push("------------------------------");
        if (receiptLines.length || selectedShowtime) {
          lines.push(`Филм: ${movieTitle || "-"}`);
          lines.push(`Зала: ${hallTitle || "-"}`);
          lines.push(`Дата/час: ${showDate || "-"} ${showTime || "-"}`);
        }
    

    lines.push(dayjs().format("DD.MM.YYYY HH:mm:ss"));

    if (receiptLines.length) {
      lines.push("— Билети —");
      for (const l of receiptLines)
        lines.push(`${l.label} — ${l.seat_type}  ${formatMainWithSecondary(l.price)}`);
    }
    if (barLines.length) {
      lines.push("— Бар —");
      for (const b of barLines) lines.push(`${b.name} x${b.qty}  ${formatMainWithSecondary((b.price * b.qty))}`);
    }

    lines.push("------------------------------");
    lines.push(`ОБЩО: ${formatMainWithSecondary(finalTotalBGN)} (≈ ${totalEUR.toFixed(2)} €)`);

    const body = JSON.stringify({ cinema_id: Number(cinemaId), lines });
    const candidates = [
      `${API_BASE}/api/fiscal/nonfisc/print`,
      `${API_BASE}/api/fiscal/nonfiscal/print`,
      `${API_BASE}/api/fiscal/print-nonfisc`,
    ];

    for (const url of candidates) {
      try {
        await apiJson(url, { method: "POST", body });
        setSnackbar({
          open: true,
          severity: "success",
          message: "Нефискалният бон е изпратен към устройството.",
        });
        return;
      } catch (e) {
        if (!/404|Not found/i.test(String(e?.message || ""))) {
          setSnackbar({ open: true, severity: "error", message: `Печатът не успя: ${e.message}` });
          return;
        }
      }
    }

    const html = `
      <html><head><meta charset="utf-8">
        <style>
          body{margin:0;font:14px/1.3 Arial, sans-serif}
          .ticket{width:80mm;padding:8px}
          .line{white-space:pre-wrap}
          .hr{border-top:1px dashed #000;margin:6px 0}
        </style>
      </head><body onload="window.print()">
        <div class="ticket">
          ${lines.map(nfEscape).map((s) => `<div class="line">${s}</div>`).join("")}
        </div>
      </body></html>`;
    openPrintWindow(html);
    setSnackbar({
      open: true,
      severity: "info",
      message: "Устройство не е намерено – отпечатано в браузъра.",
    });
  }





  // BAR actions
  const incBar = useCallback((a) => {
    setBarCart((prev) => {
      const next = new Map(prev);
      const existing = next.get(a.article_id);
      const cur = existing
        ? { ...existing }
        : {
            article_id: a.article_id,
            name: a.article_name,
            price: Number(a.price || 0),
            qty: 0,
            image_path: a.image_path,
            quantity: a.quantity ?? 0,
          };

      if (cur.qty >= (a.quantity ?? cur.quantity ?? 0)) return prev;
      cur.qty = cur.qty + 1;
      next.set(a.article_id, cur);
      return next;
    });
  }, []);

  const decBar = useCallback((a) => {
    setBarCart((prev) => {
      const next = new Map(prev);
      const existing = next.get(a.article_id);
      if (!existing) return prev;

      const cur = { ...existing };
      cur.qty = cur.qty - 1;

      if (cur.qty <= 0) {
        next.delete(a.article_id);
      } else {
        next.set(a.article_id, cur);
      }
      return next;
    });
  }, []);

  const commitBarSale = async (method = "cash") => {
    if (!barLines.length) {
      setSnackbar({ open: true, message: "Няма избрани артикули.", severity: "warning" });
      return;
    }
    try {
      const payload = {
        cinema_id: cinemaId,
        user_id: null,
        payment_method: method,
        tickets: [],
        bar: barLines.map((x) => ({ article_id: x.article_id, qty: x.qty })),
        promo_id: selectedPromo?.promo_id || selectedPromo?.id || null,
        promo_code: selectedPromo?.code || null,
        promo_scope: selectedPromo?.scope || "all",
      };
      const data = await fetchJsonStrict(`${API}/api/pos/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!data?.success) throw new Error(data?.error || "Commit failed");
      const shownBon =
        data?.bon_number ?? data?.fiscal_unp ?? data?.unp ?? data?.dy ?? data?.bon_id ?? "—";

      setSnackbar({
        open: true,
        message: `Продажба успешна. Бон № ${shownBon}`,
        severity: "success",
      });

      const saleItems = barLines.map((b) => ({
        text: b.name,
        qty: Number(b.qty || 0),
        unit_price: Number(b.price || 0),
        ...(b.tax_dept != null ? { tax_code: Number(b.tax_dept) } :
        b.tax_code != null ? { tax_code: Number(b.tax_code) } : {})
      }));

            setLastBon(shownBon);

      setBarCart(new Map());

      const list = await fetchJsonSoft(`${API}/api/bar/articles/${cinemaId}`);
      setBarArticles(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: `Грешка при продажба (Бар). ${e.message || ""}`,
        severity: "error",
      });
    }
  };


  // NEW: clear current bon (seats + bar + reservation)
  const clearBon = useCallback(() => {
    clearSelection();
    setBarCart(new Map());
    setSeatMoves([]);
    setCurrentReservation(null);
    setReservationSeatIds(new Set());
    setLastBon(null);
    setSnackbar({ open: true, severity: "info", message: "Бонът е изчистен." });
    setSelectedShowtime(null);
    setSelectedMovie(null);
    setBonPreview(null);
    setSelectedPromo(null);
    setPromoEffectPreview(null);

    setSeats([]);
    setStatusMap({});
    setHallName("");
    setSelectedSeatType(null);


      setSeatPaneOpen(false);   // <—
  }, [clearSelection]);

  clearBonRef.current = clearBon;

  // Reservations / Tickets
  const openReserveDialog = () => {
    if (!selectedShowtime || !selectedSeats.size) {
      setSnackbar({ open: true, message: "Изберете поне едно място.", severity: "warning" });
      return;
    }
    setReserveDlgOpen(true);
  };

  const confirmReservation = async ({ name, phone, email, note }) => {
    if (!selectedShowtime || !selectedSeats.size) {
      setSnackbar({ open: true, message: "Изберете поне едно място.", severity: "warning" });
      return;
    }
    try {
      const groups = new Map(); // key = "7.00", value = seat_ids[]
      selectedSeats.forEach((val, seat_id) => {
        const p = effectiveSeatPrice(seat_id, val.seat_type, val.pricing_mode || "auto");
        const key = Number(p).toFixed(2);
        const arr = groups.get(key) || [];
        arr.push(seat_id);
        groups.set(key, arr);
      });

      for (const [key, seat_ids] of groups.entries()) {
        const price = Number(key);
const res = await fetch(`${API}/api/seats-reserve/reserve`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            showtime_id: (selectedShowtime.showtime_id || selectedShowtime.SHOWTIME_ID),
            seat_ids,
            cinema_id: cinemaId,
            customer_name: name?.trim() || "POS",
            customer_phone: phone?.trim() || null,
            customer_email: email?.trim() || null,
            customer_note: note?.trim() || null,
            price,
          }),
        });
        const data = await res.json();
        if (!data?.success) throw new Error(data?.error || "reserve failed");
      }

      setSnackbar({ open: true, message: "Успешна резервация!", severity: "success" });
      setReserveDlgOpen(false);
      setReserveForm({ name: "", phone: "", email: "", note: "" });
      await loadSeats(selectedShowtime);
      // keep Reservations tab in sync (no need to press "Обнови")
      try { await searchReservations(); } catch {}

    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: "Грешка при резервация.", severity: "error" });
    }
  };

  const handleReserveSave = () => {
    confirmReservation(reserveForm);
  };

  const confirmSale = async (payment_method = "cash") => {
    // 1) Няма избрани места → ако има само бар, продаваме само бар
    if (!selectedShowtime || !selectedSeats.size) {
      if (barLines.length) return commitBarSale(payment_method);
      setSnackbar({
        open: true,
        message: "Изберете поне едно място.",
        severity: "warning",
      });
      return;
    }
  
    try {
      // 2) Сглобяваме билетите
      const tickets = [];
      for (const [seat_id, { label, seat_type, pricing_mode }] of selectedSeats.entries()) {
        const locked = currentReservation?.seats?.find(
          (s) => s.seat_id === seat_id
        )?.price;

        const price = Number.isFinite(locked)
          ? locked
          : effectiveSeatPrice(seat_id, seat_type, pricing_mode || "auto");

        tickets.push({
          showtime_id: (selectedShowtime.showtime_id || selectedShowtime.SHOWTIME_ID),
          seat_id,
          seat_label: label,
          seat_type,
          price,
        });
      }
  
      const hasBar = barLines.length > 0;

    const discountLines = (bonPreview?.items || []).filter((it) => Number(it?.lineTotal || 0) < 0);
const showPromoSection = Boolean(selectedPromo) || Boolean(promoEffectPreview?.applied) || discountLines.length > 0;
      const allFreeTickets =
        tickets.length > 0 &&
        tickets.every((t) => Number(t.price || 0) === 0);
  
      // 🔴 ЗАБРАНА: безплатни билети + бар в една продажба
      if (allFreeTickets && hasBar) {
        setSnackbar({
          open: true,
          severity: "warning",
          message:
            "Първо издайте ПРОМО билетите (без бар), после направете отделна продажба само за бара.",
        });
        return;
      }
  
      // ---------------- PROMO / FREE FLOW (НЕФИСКАЛНО) ----------------
      // само безплатни билети, без бар → promo-tickets + service bon
      if (allFreeTickets && !hasBar) {
        const payload = {
          cinema_id: cinemaId,
          user_id: null,
          payment_method,
          reservation_id: currentReservation?.id || null,
          tickets,
          seat_changes: seatMoves.length ? seatMoves : undefined,
        };
  
        const data = await fetchJsonStrict(`${API}/api/pos/promo-tickets`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!data?.success) throw new Error(data?.error || "promo sale failed");
  
        // Нефискален служебен бон за промо прожекция
        const serviceLines = [
          movieTitle || "—",
          `Зала: ${hallTitle || "—"}`,
          `Дата: ${
            showDate || filterDate.format("YYYY-MM-DD")
          }  Час: ${showTime || "—"}`,
          "",
          "ПРОМО БЕЗПЛАТНИ БИЛЕТИ:",
          ...tickets.map((t) => `Място ${t.seat_label} — ${t.seat_type}`),
        ];
  
        try {
          await apiJson(`${API_BASE}/api/fiscal/service-bon`, {
            method: "POST",
            body: JSON.stringify({
              cinema_id: Number(cinemaId),
              title: "ПРОМО ПРОЖЕКЦИЯ",
              lines: serviceLines,
            }),
          });
        } catch (e) {
          console.error(e);
          setSnackbar({
            open: true,
            severity: "warning",
            message: `Билетите са издадени, но служебният бон не се отпечата: ${e.message}`,
          });
        }
  
        setSnackbar({
          open: true,
          message: `Издадени безплатни билети (${tickets.length} бр.)`,
          severity: "success",
        });
  
        // чистим локалното състояние
        clearSelection();
        setBarCart(new Map());
        setSeatMoves([]);
        setCurrentReservation(null);
        setReservationSeatIds(new Set());
        await loadSeats(selectedShowtime);
        return; // ❗ НЕ продължаваме към фискален поток
      }
  
      // ---------------- НОРМАЛЕН ФИСКАЛЕН ПОТОК ----------------
      const payload = {
        cinema_id: cinemaId,
        user_id: null,
        payment_method,
        reservation_id: currentReservation?.id || null,
        tickets,
        bar: barLines.map((x) => ({ article_id: x.article_id, qty: x.qty })),
        promo_id: selectedPromo?.promo_id || selectedPromo?.id || null,
        promo_code: selectedPromo?.code || null,
        promo_scope: selectedPromo?.scope || "all",
        seat_changes: seatMoves.length ? seatMoves : undefined,
      };
  
      const data = await fetchJsonStrict(`${API}/api/pos/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!data?.success) throw new Error(data?.error || "sale failed");
  
      const saleItems = [
        ...tickets.map((t) => ({
          text: `${t.seat_label} — ${t.seat_type}`,
          qty: 1,
          unit_price: Number(t.price),
        })),
        ...barLines.map((b) => ({
          text: b.name,
          qty: Number(b.qty || 0),
          unit_price: Number(b.price || 0),
        })),
      ];
  
      const shownBon =
        data?.bon_number ??
        data?.fiscal_unp ??
        data?.unp ??
        data?.dy ??
        data?.bon_id ??
        null;


        // Optional: print 1 seat-ticket (non-fiscal "служебен бон") per sold seat.
// Prints BEFORE the fiscal receipt so the paper is cut between tickets and the fiscal bon.
const hasTrue = (v) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";

let shouldPrintSeatTickets = Boolean(printSeatTickets);

// if not loaded yet, fetch DB value now (so it’s NEVER “sometimes”)
if (printSeatTickets === null && cinemaId) {
  const hdr = await fetchJsonSoft(`${API}/api/cinemas/${cinemaId}/brief`);
  if (hdr && !hdr.error) {
    setCinemaHdr(hdr);
    shouldPrintSeatTickets = hasTrue(hdr.print_seat_tickets);
  }
}

if (shouldPrintSeatTickets && tickets.length) {
  
  const parseSeat = (lab) => {
    const s = String(lab || "");
    const mRow = s.match(/Ред\s*[:\-]?\s*([^\s—-]+)/i);
    const mSeat = s.match(/Място\s*[:\-]?\s*([^\s—-]+)/i);
    return { row: mRow ? mRow[1] : null, seat: mSeat ? mSeat[1] : null };
  };

  for (const t of tickets) {
    const { row, seat } = parseSeat(t.seat_label);

    const WIDTH = 42;

    const bgWeekdays = ["Неделя","Понеделник","Вторник","Сряда","Четвъртък","Петък","Събота"];
    const bgMonthsShort = ["ян","фев","мар","апр","май","юни","юли","авг","сеп","окт","ное","дек"];
    
    const wrap = (text, width = WIDTH) => {
      const s = String(text || "").replace(/\r?\n/g, " ").trim();
      if (!s) return [];
      const words = s.split(/\s+/);
      const out = [];
      let line = "";
      for (const w of words) {
        if (!line) {
          line = w;
        } else if ((line + " " + w).length <= width) {
          line += " " + w;
        } else {
          out.push(line);
          line = w;
        }
        // super-long word fallback
        if (line.length > width) {
          out.push(line.slice(0, width));
          line = line.slice(width);
        }
      }
      if (line) out.push(line);
      return out;
    };
    
    const center = (text, width = WIDTH) => {
      const s = String(text || "").trim();
      if (!s) return "";
      if (s.length >= width) return s.slice(0, width);
      const pad = Math.floor((width - s.length) / 2);
      return " ".repeat(pad) + s;
    };
    
    const dotFill = (label, value, width = WIDTH) => {
      const left = `${label}:`;
      let val = String(value ?? "");
      const maxValLen = Math.max(1, width - left.length - 2);
      if (val.length > maxValLen) val = val.slice(0, maxValLen);
      const dotsCount = Math.max(1, width - left.length - 1 - val.length);
      return left + " " + ".".repeat(dotsCount) + val; // total ~ width
    };
    
    const fmtBgDate = (isoDate) => {
      const d = dayjs(isoDate);
      const dd = String(d.date()).padStart(2, "0");
      return `${dd} ${bgMonthsShort[d.month()]} ${d.year()}`;
    };
    
    const weekdayBg = (isoDate) => {
      const d = dayjs(isoDate);
      return bgWeekdays[d.day()];
    };
    
    // ---------- build lines ----------
    const cinemaName = (cinemaHdr?.name || theaterTitle || "").trim();
    const cinemaAddr = (cinemaHdr?.location || cinemaHdr?.address || "").trim();
    const cinemaPhone = (cinemaHdr?.phone || "").trim();
    
    const showIso = showDate || filterDate.format("YYYY-MM-DD");
    const timeHHMM = (showTime || "—").slice(0, 5);
    
    const cinemaNameLines = wrap(cinemaName).map(center);
    const titleLine = cinemaNameLines[0] || "";         // goes as "title"
    const headerExtra = cinemaNameLines.slice(1);       // extra lines under it
    
    const headerLines = [
      ...headerExtra,
      ...wrap(cinemaAddr).map(center),
      ...(cinemaPhone ? [center(`Тел: ${cinemaPhone}`)] : []),
      "", // blank line after header
    ];
    
    const filmLines = wrap(`Филм: ${movieTitle || "—"}`);
    const infoLines = [
      ...filmLines,
      `Час: ${timeHHMM}`,
      `Дата: ${fmtBgDate(showIso)} ${weekdayBg(showIso)}`,
      "",
    ];
    
    const rowLine = dotFill("Ред", row || "—");
    const seatLine = dotFill("Място", seat || "—");
    const priceLine = dotFill("Цена", `${formatMainWithSecondary(Number(t.price || 0))}`);
    
    const codeLine = shownBon ? `Код: ${shownBon}` : null;
    
    const lines = [
      ...headerLines,
      ...infoLines,
      rowLine,
      seatLine,
      priceLine,
      ...(codeLine ? [codeLine] : []),
      "",
      center("СЛУЖЕБЕН БОН"),
    ].filter(Boolean);
    

    

    try {
      await apiJson(`${API_BASE}/api/fiscal/service-bon`, {
        method: "POST",
        body: JSON.stringify({
          cinema_id: Number(cinemaId),
          title: titleLine || center(" "),     // ✅ title is cinema name (centered)
          lines,
          width: 42,
          include_nonfiscal_footer: false,
        }),
      });
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        severity: "warning",
        message: `Продажбата е успешна, но билетите не се отпечатаха: ${e.message}`,
      });
      break;
    }
  }
}

  
      try {
        await apiJson(`${API_BASE}/api/fiscal/print-sale`, {
          method: "POST",
          body: JSON.stringify({
            cinema_id: Number(cinemaId),
            payment_method,
            items: saleItems,
            header_lines: [
              movieTitle || "—",
              `Зала: ${hallTitle || "—"}`,
              `Дата: ${
                showDate || filterDate.format("YYYY-MM-DD")
              }  Час: ${showTime || "—"}`,
            ],
            unp_code: shownBon,
            related_ticket_order_id:
              data?.ticket_order_group_id ?? data?.ticket_order_id ?? null,
            related_bar_order_id: data?.bar_order_id ?? null,
          }),
        });
        setLastBon(shownBon);
      } catch (e) {
        console.error(e);
        setSnackbar({
          open: true,
          severity: "warning",
          message: `Продажбата е успешна, но печатът не успя: ${e.message}`,
        });
      }
  
      setSnackbar({
        open: true,
        message: `Продажба успешна. Бон № ${shownBon || "—"}`,
        severity: "success",
      });
  
      clearSelection();
      setBarCart(new Map());
      setSeatMoves([]);
      setCurrentReservation(null);
      setReservationSeatIds(new Set());
      await loadSeats(selectedShowtime);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: "Грешка при продажба.",
        severity: "error",
      });
    }
  };
  
  const [showQuickBon, setShowQuickBon] = React.useState(false);


// ✅ Print 1 seat ticket ("служебен бон") per sold seat
// Persisted per cinema in DB (so it stays the same on every device / user session)
const [printSeatTickets, setPrintSeatTickets] = React.useState(null); // null = not loaded yet


// When cinema changes, default to OFF until we load the cinema settings
React.useEffect(() => {
    if (!cinemaId) return;
    setPrintSeatTickets(null);
  }, [cinemaId]);

// Sync checkbox from DB field: cinema_theater.print_seat_tickets
React.useEffect(() => {
    if (!cinemaId) return;
    // don’t apply old cinemaHdr to a new cinemaId
    if (cinemaHdr?.cinema_id != null && Number(cinemaHdr.cinema_id) !== Number(cinemaId)) return;
  
    const v = cinemaHdr?.print_seat_tickets;
    if (v == null) return;
    const enabled =
      v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
    setPrintSeatTickets(enabled);
  }, [cinemaId, cinemaHdr?.cinema_id, cinemaHdr?.print_seat_tickets]);

const setPrintSeatTicketsPersist = React.useCallback(async (checked) => {
  const prev = printSeatTickets;
  setPrintSeatTickets(checked);
  if (!cinemaId) return;

  try {
    const r = await apiJson(`${API_BASE}/api/cinemas/${cinemaId}/print-seat-tickets`, {
      method: "POST",
      body: JSON.stringify({ enabled: checked }),
    });

    // keep cinemaHdr in-sync (so a refresh shows the same)
    if (r && typeof r.print_seat_tickets !== "undefined") {
      setCinemaHdr((h) => (h ? { ...h, print_seat_tickets: r.print_seat_tickets } : h));
    }
  } catch (e) {
    // revert local UI on failure
    setPrintSeatTickets(prev);
    setSnackbar({
      open: true,
      severity: "error",
      message: `Неуспешно записване на настройката “Печат на билети (места)”: ${e.message}`,
    });
  }
}, [cinemaId, printSeatTickets]);

  // >>> FIX: compute selectedPricingRow at top level (Rules of Hooks safe)
  const selectedPricingRow = useMemo(
    () => seatPricing.find((p) => (p.seat_type || p.category) === selectedSeatType) || null,
    [seatPricing, selectedSeatType]
  );
  // <<< FIX END


// handlers
const doXReport = async () => {
  if (!cinemaId) {
    setSnackbar({ open: true, severity: "warning", message: "Изберете кино." });
    return;
  }
  try {
    setReportBusy(true);
    const r = await runXReport(cinemaId);
    setSnackbar({ open: true, severity: "success", message: "X отчет отпечатан." });
    if (r?.log_id) await openFiscalReportPreview(r.log_id);
  } catch (e) {
    setSnackbar({ open: true, severity: "error", message: `Грешка при X отчет: ${e.message}` });
  } finally {
    setReportBusy(false);
  }
};

const doZReport = async () => {
  if (!cinemaId) {
    setSnackbar({ open: true, severity: "warning", message: "Изберете кино." });
    return;
  }
  if (!window.confirm("Z отчет ще занули дневните тотали. Сигурни ли сте?")) return;
  try {
    setReportBusy(true);
    const r = await runZReport(cinemaId);
    setSnackbar({ open: true, severity: "success", message: "Z отчет отпечатан." });
    if (r?.log_id) await openFiscalReportPreview(r.log_id);
  } catch (e) {
    setSnackbar({ open: true, severity: "error", message: `Грешка при Z отчет: ${e.message}` });
  } finally {
    setReportBusy(false);
  }
};

const seatCount = receiptLines.length;


  // Right receipt (desktop/tablet unified; mobile unchanged)
  const RightReceipt = () => {

    const previewUnp = React.useMemo(() => {
      const b = bonPreview;
      if (!b) return null;
      return b.unp || b.fiscal_unp || b.bon_number || b.dy || b.bon_id || null;
    }, [bonPreview]);
  

    const btnSize = (isSM || isTablet) ? "small" : "medium";
    
    const hasTickets = receiptLines.length > 0;
    const hasBar = barLines.length > 0;

    const discountLines = React.useMemo(() => {
      const items = bonPreview?.items || [];
      return items.filter((it) => Number(it?.lineTotal || 0) < 0);
    }, [bonPreview]);

    const showPromoSection = Boolean(selectedPromo) || Boolean(promoEffectPreview?.applied) || discountLines.length > 0;

    const Content = (
      <>
        <Box
  className="rcpt-body"
  sx={{ p: isSM ? 0.75 : isTablet ? 1 : 1.25, overflowY: "auto", flex: 1 }}
>
          <Typography
            align="center"
            sx={{ fontWeight: 800, mb: isSM ? 0.25 : 0.5 }}
            className="rcpt-heading"
          >
            ФИСКАЛЕН БОН
          </Typography>

          <Box sx={{ textAlign: "center", mb: isSM ? 0.5 : 0.75 }}>
            <div style={{ fontWeight: 500 }}>{cinemaHdr?.name || theaterTitle}</div>
            {(cinemaHdr?.location || cinemaHdr?.address) && (
              <div>
                {cinemaHdr?.location || cinemaHdr?.address}
                {cinemaHdr?.city ? `, ${cinemaHdr.city}` : ""}
              </div>
            )}
            {cinemaHdr?.eik_code && <div>ЕИК: {cinemaHdr.eik_code} </div>}
            {cinemaHdr?.zdds_number && <div>ЗДДС: {cinemaHdr.zdds_number}</div>}
            {cinemaHdr?.fiscal_id && <div>Фискално устройство: {cinemaHdr.fiscal_id}</div>}
          </Box>

          <Paper variant="outlined" sx={{ p: isSM ? 0.75 : 1, bgcolor: "#fff" }}>
            <Typography sx={{ fontWeight: 500, mb: 0.25 }} className="rcpt-title">
              {movieTitle || "—"}
            </Typography>
            <Typography className="rcpt-text">
              Зала: <strong>{hallTitle || "—"}</strong>
            </Typography>
            <Typography className="rcpt-text">
  Дата: <strong>{fmtBGDate(showDate || filterDate)}</strong>
</Typography>

            <Typography sx={{ mb: 0.25 }} className="rcpt-text">
              Час: <strong>{showTime || "—"}</strong>
            </Typography>

            {!isSM && previewUnp && (
  <Paper
    variant="outlined"
    sx={{ p: isSM ? 0.5 : 0.75, mt: 0.75, bgcolor: "#fffbe6", borderColor: "#ffe58f" }}
  >
    <Typography className="rcpt-text" sx={{ fontWeight: 800 }}>
      UNP :{" "}
      <span style={{ fontFamily: "monospace" }}>{previewUnp}</span>
    </Typography>

  </Paper>
)}



            

            {!!currentReservation?.customer && (
              <Paper variant="outlined" sx={{ p: isSM ? 0.75 : 1, mt: 0.5, bgcolor: "#fafafa" }}>
                <Typography sx={{ fontWeight: 700, mb: 0.25 }} className="rcpt-subhdr">Клиент</Typography>
                <Typography className="rcpt-text">
                  Име: <strong>{currentReservation.customer.name || "—"}</strong>
                </Typography>
                {!!currentReservation.customer.phone && (
                  <Typography className="rcpt-text">
                    Тел: <strong>{currentReservation.customer.phone}</strong>
                  </Typography>
                )}
                {!!currentReservation.customer.email && (
                  <Typography className="rcpt-text">
                    Имейл: <strong>{currentReservation.customer.email}</strong>
                  </Typography>
                )}
                {!!currentReservation.customer.note && (
                  <Typography sx={{ opacity: 0.8 }} className="rcpt-text">
                    Бележка: {currentReservation.customer.note}
                  </Typography>
                )}
              </Paper>
            )}
          </Paper>

          {hasTickets && (
            <>
              <Divider sx={{ my: isSM ? 0.75 : 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }} className="rcpt-subhdr">
                Билети
              </Typography>
              {receiptLines.map((l, i) => (
                <Box key={`t-${i}`} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.25, gap: 1 }}>
                  <Typography className="rcpt-text" sx={{ flex: 1, pr: 0.5 }}>
                    {l.label} — {l.seat_type}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 700 }} className="rcpt-text">
                      {formatMainWithSecondary(l.price)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeSeatById(l.seat_id)}
                      title="Премахни"
                      sx={{ ml: 0.25 }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          )}

          {hasBar && (
            <>
              <Divider sx={{ my: isSM ? 0.75 : 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }} className="rcpt-subhdr">
                Бар
              </Typography>
              {barLines.map((row) => (
                <Box key={row.article_id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.25, gap: 1 }}>
                  <Typography className="rcpt-text" sx={{ flex: 1, pr: 0.5 }}>
                    {row.name} × {row.qty}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 700 }} className="rcpt-text">
                      {formatMainWithSecondary(row.price * row.qty)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeBarById(row.article_id)}
                      title="Премахни"
                      sx={{ ml: 0.25 }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          )}


          {showPromoSection && (
            <>
              <Divider sx={{ my: isSM ? 0.75 : 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }} className="rcpt-subhdr">
                Промоция
              </Typography>

              {selectedPromo && (
                <Typography className="rcpt-text" sx={{ mb: 0.25 }}>
                  Избрана: <strong>{selectedPromo.code}</strong>
                  {selectedPromo.name ? ` — ${selectedPromo.name}` : ""}
                  {selectedPromo.scope ? ` (${selectedPromo.scope})` : ""}
                </Typography>
              )}

              {discountLines.map((d, i) => (
                <Box key={`d-${i}`} sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                  <Typography className="rcpt-text">{d.text}</Typography>
                  <Typography sx={{ fontWeight: 800 }} className="rcpt-text">
                    {formatMainWithSecondary(d.lineTotal)}
                  </Typography>
                </Box>
              ))}

              {promoEffectPreview?.applied === false && selectedPromo && (
                <Typography className="rcpt-text" sx={{ opacity: 0.75 }}>
                  (Промото не се прилага още — условията не са изпълнени)
                </Typography>
              )}
            </>
          )}


          {lastBon && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8 }} className="rcpt-caption">
              Последен бон: <strong>{lastBon}</strong>
            </Typography>
          )}





        </Box>

        {/* Footer: unified desktop/tablet; mobile uses Drawer */}
        <Box sx={{ bgcolor: "#fff", borderTop: "1px solid #ddd" }}>
  <Box sx={{ p: isSM ? 0.75 : 1 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      {/* Лява част: "ОБЩО" + брой места */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontWeight: 800 }} className="rcpt-subhdr">
          ОБЩО
        </Typography>

        {seatCount > 0 && (
          <Box
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              bgcolor: "#eef2ff",
              color: "#1f2937",
              border: "1px solid #dbeafe",
              lineHeight: 1,
            }}
          >
            {seatCount} {seatCount === 1 ? "място" : "места"}
          </Box>
        )}
      </Box>

      {/* Дясна част: сумата */}
      <Typography sx={{ fontWeight: 800 }} className="rcpt-total">
        {formatMainWithSecondary(finalTotalBGN)}
      </Typography>
    </Box>


    <FormControlLabel
  sx={{ mt: 1, mb: 0, userSelect: "none" }}
  control={
    <Checkbox
    checked={Boolean(printSeatTickets)}
    disabled={printSeatTickets === null} 
    onChange={(e) => setPrintSeatTicketsPersist(e.target.checked)}
      sx={{ transform: "scale(1.35)" }}   // ✅ bigger checkbox
    />
  }
  label={
    <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
      Печат на билети (места)
    </Typography>
  }
/>



  </Box>


 {/* BIG buttons (2 columns) */}
<Box
  sx={{
    p: isSM ? 1.1 : 1.25,
    pt: 1,
    pb: 1.5,
  }}
>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 1.1, // space between buttons
    }}
  >
    {/* ПРОДАЖБА */}
    <Button
      fullWidth
      variant="contained"
      disabled={(!receiptLines.length && !barLines.length) || !cinemaId}
      onClick={() => setCashOpen(true)}
      sx={{
        height: isSM ? 54 : 60,     // ✅ nice finger height
        fontWeight: 1000,
        fontSize: isSM ? 14 : 15,
        borderRadius: 3,
        letterSpacing: 0.4,
        bgcolor: UI.cta,
        "&:hover": { bgcolor: UI.cta },
      }}
    >
      ПРОДАЖБА
    </Button>

    {/* РЕЗЕРВАЦИЯ */}
    <Button
      fullWidth
      variant="contained"
      disabled={!selectedShowtime || !receiptLines.length}
      onClick={openReserveDialog}
      sx={{
        height: isSM ? 54 : 60,
        fontWeight: 1000,
        fontSize: isSM ? 14 : 15,
        borderRadius: 3,
        letterSpacing: 0.3,
        bgcolor: UI.secondary,
        "&:hover": { bgcolor: UI.secondary },
      }}
    >
      РЕЗЕРВАЦИЯ ({receiptLines.length || 0})
    </Button>

    {/* БЪРЗ БОН */}
    <Button
      fullWidth
      variant="contained"
      onClick={() => setShowQuickBon(true)}
      disabled={!cinemaId}
      sx={{
        height: isSM ? 54 : 60,
        fontWeight: 1000,
        fontSize: isSM ? 14 : 15,
        borderRadius: 3,
        letterSpacing: 0.3,
        bgcolor: UI.primary,
        "&:hover": { bgcolor: UI.primary },
      }}
    >
      БЪРЗ БОН
    </Button>

    {/* ИЗЧИСТИ БОН */}
    <Button
      fullWidth
      variant="contained"
      onClick={clearBon}
      sx={{
        height: isSM ? 54 : 60,
        fontWeight: 1000,
        fontSize: isSM ? 14 : 15,
        borderRadius: 3,
        letterSpacing: 0.3,
        bgcolor: UI.danger,
        "&:hover": { bgcolor: UI.danger },
      }}
    >
      ИЗЧИСТИ БОН
    </Button>
  </Box>
</Box>




        </Box>
      </>
    );

    if (!isSM) {
      return (
        <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: receiptW,
          height: "100vh",
          bgcolor: "#f7f7f7",
          color: "#111",
          borderLeft: `1px solid ${UI.panelBorder}`,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        
          // ↓ compact typography & controls for tablet
          "& .rcpt-body .MuiButton-root": { fontSize: isTablet ? 12 : 14, minHeight: isTablet ? 36 : 40 },
          "& .rcpt-heading":   { fontSize: isTablet ? 14 : 16 },
          "& .rcpt-title":     { fontSize: isTablet ? 12 : 14 },
          "& .rcpt-subhdr":    { fontSize: isTablet ? 12 : 13 },
          "& .rcpt-text":      { fontSize: isTablet ? 12 : 13 },
          "& .rcpt-caption, & .MuiTypography-caption": { fontSize: isTablet ? 10 : 11 },
          "& .rcpt-total":     { fontSize: isTablet ? 16 : 18 },
        }}
        
        >
          {Content}





        </Box>
      );
    }

    // Mobile (unchanged pattern: Drawer FAB)
    return (
      <>
        <Drawer
          anchor="bottom"
          open={receiptOpen}
          onClose={() => toggleReceipt(false)}
          PaperProps={{
            sx: {
              height: "74vh",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <IconButton onClick={() => toggleReceipt(false)}>
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 1, fontWeight: 700 }}>Фискален бон</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontWeight: 800, mr: 1 }}>{formatMainWithSecondary(finalTotalBGN)}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>{Content}</Box>
        </Drawer>

        <Fab
          size="small"                         // ← add this

          onClick={() => toggleReceipt(true)}
          sx={{ position: "fixed", right: 10, bottom: 10, zIndex: 1200 }} // slightly tighter
          color="primary"
        >
          <Badge
            badgeContent={receiptLines.length + barLines.reduce((a, b) => a + b.qty, 0)}
            color="secondary"
            overlap="circular"
          >
            <ReceiptLongIcon />
          </Badge>
        </Fab>



      </>
    );



  };

// Live seat availability per showtime
const hallTotalCacheRef = useRef(new Map()); // hall_id -> total seats
const [availMap, setAvailMap] = useState(new Map()); // showtime_id -> { free, total }

const computeAvail = useCallback(async (s) => {
  try {
    const hallId = Number(s?.hall_id || s?.HALL_ID || 0);
    const stId = Number(s?.showtime_id || s?.SHOWTIME_ID || s?.id || 0);
    if (!hallId || !stId) return;

    let total = hallTotalCacheRef.current.get(hallId);
    if (total == null) {
      const layout = await fetchJsonSoft(`${API}/api/seats/hall/${hallId}`);
      total = Array.isArray(layout)
        ? layout.filter((seat) => (seat.seat_id ?? seat.SEAT_ID ?? seat.id)).length
        : 0;
      hallTotalCacheRef.current.set(hallId, total);
    }

    const statusRows = await fetchJsonSoft(`${API}/api/seats-reserve/status/${stId}`);
    const taken = (statusRows || []).filter((r) => {
      const st = String(r.status || r.STATUS || "").toLowerCase();
      return st === "reserved" || st === "sold";
    }).length;
    const free = Math.max(0, total - taken);

    setAvailMap((m) => {
      const n = new Map(m);
      n.set(stId, { free, total });
      return n;
    });
  } catch {}
}, []);

useEffect(() => {
  if (!filterDate || !showtimes.length) return;
  // recompute for the selected date
  const ymd = filterDate.format("YYYY-MM-DD");
  const todays = showtimes.filter((s) => String(s.show_date || s.SHOW_DATE || "") === ymd);
  todays.forEach((s) => computeAvail(s));

  // lightweight polling (10s) + hall totals cached
  const t = setInterval(() => todays.forEach((s) => computeAvail(s)), 10000);
  return () => clearInterval(t);
}, [filterDate, showtimes, computeAvail]);

// If a new reservation appears, refresh availability immediately (cards update without manual "Обнови")
useEffect(() => {
  if (!hasNewRes || !filterDate || !showtimes.length) return;
  const ymd = filterDate.format("YYYY-MM-DD");
  showtimes
    .filter((s) => String(s.show_date || s.SHOW_DATE || "") === ymd)
    .forEach((s) => computeAvail(s));
}, [hasNewRes, filterDate, showtimes, computeAvail]);

// Poster sizing (responsive)
const POSTER = {
  w: { xs: 56, sm: 72, md: 88 },   // was 64/84/100
  h: { xs: 84, sm: 114, md: 146 }, // was 96/130/165
  radius: 0.75,
};


function PosterThumb({ src, alt = "Poster", sx }) {
  return (
    <Box
      sx={{
        width: { xs: POSTER.w.xs, sm: POSTER.w.sm, md: POSTER.w.md },
        height: { xs: POSTER.h.xs, sm: POSTER.h.sm, md: POSTER.h.md },
        borderRadius: POSTER.radius,
        overflow: "hidden",
        bgcolor: UI.bg,
        boxShadow: "0 6px 18px rgba(0,0,0,.18)",
        ...sx,
      }}
    >
      {!!src && (
        <img
          src={src}
          alt={alt}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </Box>
  );
}


const ShowtimeTile = React.memo(function ShowtimeTile({ movie, s, onPick }) {
    const poster = (() => {
      const p = movie?.POSTER_URL;
      if (!p) return null;
      return /^https?:\/\//i.test(p) ? p : `${API}/${String(p).replace(/^\/+/, "")}`;
    })();
  
        const stId = Number(s?.showtime_id || s?.SHOWTIME_ID || s?.id || 0);
    const av = availMap.get(stId) || { free: 0, total: 0 };
    const ratio = av.total ? av.free / av.total : 0;

    const metaChips = getShowtimeMetaChips(s);
   // const trailerUrl = getTrailerUrl(movie);

    return (
      <Paper
        onClick={() => onPick?.(s, movie)}                  // ✅ was onOpen(s)
        elevation={0}
         sx={{
             width: "100%",           // fill the grid cell
             maxWidth: "unset",       // don't cap the width inside the grid                      // ✅ now defined
          p: { xs: 1, sm: 1.25, md: 1.5 },
          bgcolor: UI.card,
          color: "#EDEBFF",
          border: `1px solid ${UI.panelBorder}`,
          borderRadius: 2,
          cursor: "pointer",
          display: "grid",
          gridTemplateColumns: { xs: "64px 1fr", sm: "80px 1fr", md: "92px 1fr" },
          columnGap: 2,
          minHeight: { xs: 168, sm: 188, md: 208 },

             transition: "background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
             transform: "translateY(0) translateZ(0)",   // allocate a composited layer up-front
             willChange: "transform, box-shadow",
             backfaceVisibility: "hidden",
             contain: "paint",
          boxShadow: "0 6px 18px rgba(0,0,0,.18)",
          "&:hover": {
            bgcolor: UI.cardAlt,
            transform: "translateY(-1px) translateZ(0)",
            boxShadow: "0 10px 26px rgba(0,0,0,.28)",
          },
        }}
      >
        
        {/* poster */}
        <Box sx={{ width: POSTER.w, height: POSTER.h, borderRadius: POSTER.radius, overflow: "hidden", bgcolor: "#201a35" }}>
          {poster && <img src={poster} alt={movie?.MOVIE_NAME || "Poster"} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />}
        </Box>
  
        {/* right column */}
        <Box sx={{ display: "grid", gridTemplateRows: "auto auto 1fr auto", gap: 0.75 }}>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Pill>Зала {s.hall_name?.replace(/\D/g, "") || (s.hall_name || "1")}</Pill>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
              <CategoryBadge code={pickCategory(movie, s)} />

            </Box>
          </Box>

  
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14, md: 15 }, lineHeight: 1.3 }}>
            {movie?.MOVIE_NAME || "—"}
          </Typography>
  
          <Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontWeight: 800 }}>{s.show_time?.slice(0, 5)}</Typography>
              <Typography sx={{ opacity: 0.7 }}>
                ({hm(s.show_time)}–{endTimeFor(s.show_time, movie)})
              </Typography>
            </Box>

            {metaChips.length > 0 && (
              <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {metaChips.map((ch) => (
                  <Pill
                    key={ch.key}
                    sx={{
                      fontSize: 11,
                      px: 0.9,
                      py: 0.25,
                      bgcolor: "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,.14)",
                    }}
                  >
                    {ch.label}
                  </Pill>
                ))}
              </Box>
            )}
  
            <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: UI.progressBack, overflow: "hidden" }}>
              <Box sx={{ width: `${Math.round(ratio * 100)}%`, height: "100%", bgcolor: UI.progress }} /> {/* ✅ UI.progress */}
            </Box>
  
            <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.85 }}>
              {av.free}/{av.total} свободни места
            </Typography>
          </Box>
  
          <Box sx={{ ml: "auto" }}>
            <Pill>{formatDurationBG(getDurationMin(movie))}</Pill>
          </Box>
        </Box>
      </Paper>
    );
  });
  




// Build the list of { s, movie } pairs for the selected day
// Build the list of { s, movie } pairs for the selected day
const showsForDay = React.useMemo(() => {
  const ymd = filterDate.format("YYYY-MM-DD");
  const isToday = filterDate.isSame(dayjs(), "day");
  const nowHM = dayjs().format("HH:mm:ss"); // "HH:mm:ss" – safe for lexicographic compare

  // 1) collect all showtimes for the date and attach their movie
  const rows = showtimes
    .filter(st => st.show_date === ymd)
    .map(s => ({
      s,
      movie: movies.find(m => m.MOVIE_NAME === s.movie_title) || null,
    }));

  // 2) sort globally by time; if today, upcoming first, then past
  rows.sort((a, b) => {
    const ta = String(a.s.show_time || "");
    const tb = String(b.s.show_time || "");

    if (isToday) {
      const aPast = ta < nowHM;
      const bPast = tb < nowHM;
      if (aPast !== bPast) return aPast ? 1 : -1; // upcoming (false) before past (true)
    }

    const t = ta.localeCompare(tb);              // earlier time first
    if (t !== 0) return t;

    // tie-break by movie title for a stable order
    return String(a.movie?.MOVIE_NAME || "")
      .localeCompare(String(b.movie?.MOVIE_NAME || ""), "bg", { numeric: true, sensitivity: "accent" });
  });

  return rows;
}, [filterDate, showtimes, movies]);



// wherever you open SeatMap
function openSeatMap(showId) {
  sessionStorage.setItem("postersScrollY", String(window.scrollY || 0));
  navigate(`/show/${showId}`);
}

  // --- NEW compact selectors row (dropdowns) ---
  const renderTicketsPane = () => {
    // helpers
    const showtimesForMovie = (m) => {
      if (!m) return [];
      const ymd = filterDate.format("YYYY-MM-DD");
      return showtimes
        .filter((s) => s.movie_title === m.MOVIE_NAME && s.show_date === ymd)
        .sort((a, b) => a.show_time.localeCompare(b.show_time));
    };
  
    // pretty date/time for summary (used inside slide panel header)
    const showDatePretty = selectedShowtime?.show_date
      ? dayjs(selectedShowtime.show_date).format("DD.MM.YYYY")
      : filterDate.format("DD.MM.YYYY");
    const showTimeStr = selectedShowtime?.show_time || "—";
    const ymd = filterDate.format("YYYY-MM-DD");

    // poster grid (only movies that have showtimes that day)
    const moviesForThisDay = movies
      .filter((m) => showtimes.some((s) => s.movie_title === m.MOVIE_NAME && s.show_date === ymd))
      // optional: next-up sort (like you already do)
      .sort((a, b) => String(a.MOVIE_NAME || "").localeCompare(String(b.MOVIE_NAME || ""), "bg", { sensitivity: "accent", numeric: true }));
  
    return (
      
      <Box
        sx={{
          p: 2,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: UI.bg,
          color: UI.text,
          overflow: "hidden",
          position: "relative",
        }}
      >
        
        {/* top toolbar (unchanged) */}
        
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Button variant="contained" size={(isSM || isTablet) ? "small" : "medium"} onClick={() => setFilterDate((d) => d.subtract(1, "day"))}>⬅</Button>
            <Button variant="contained" size={(isSM || isTablet) ? "small" : "medium"}>{filterDate.format("DD MMMM YYYY, dddd")}</Button>
            <Button variant="contained" size={(isSM || isTablet) ? "small" : "medium"} sx={{ bgcolor: UI.primary }} onClick={() => setFilterDate(dayjs())}>ДНЕС</Button>
            <Button variant="contained" size={(isSM || isTablet) ? "small" : "medium"} onClick={() => setFilterDate((d) => d.add(1, "day"))}>➡</Button>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  <LiveClock />
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
    <PosBigPill
      posBusy={posBusy}
      posOk={posOk}
      refreshPosStatus={refreshPosStatus}
      hideName
      sx={{ alignSelf: "flex-end" }}
    />
    {bridgeOnline != null && (
      <BridgeModePill
        bridgeOnline={bridgeOnline}
        bridgeMode={bridgeMode}
        usePrintjob={usePrintjob}
      />
    )}
  </Box>
</Box>
          

        </Box>


        
  
        {/* MOVIE POSTER GRID */}
<Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 1 }}>
  <Box
    sx={{
      display: "grid",
      gap: 1.25,
      // responsive multi-column layout
      gridTemplateColumns: {
        xs: "1fr",                                   // phones: 1 col
        sm: "repeat(2, minmax(260px, 1fr))",         // tablets: narrower cards
        lg: "repeat(3, minmax(300px, 1fr))",
      },
      gap: isCompact ? 1 : 1.25,
      
      alignItems: "stretch",
    }}
  >
    {showsForDay.length === 0 ? (
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        Няма прожекции за деня.
      </Typography>
    ) : (
      showsForDay.map(({ s, movie }) => (
        <ShowtimeTile
          key={s.showtime_id}
          movie={movie}
          s={s}
          onPick={openSeatPaneFor}
        />
      ))
    )}
  </Box>
</Box>


  
        {/* RIGHT SLIDE-IN SEAT PANEL */}
        <Slide direction="left" in={seatPaneOpen} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: isSM ? 0 : leftNavW,
              right: isSM ? 0 : receiptW, // stop before fiscal preview
              height: "100vh",
              bgcolor: UI.nav,  // or UI.bg if you want it identical
              color: "white",
              borderLeft: `1px solid ${UI.panelBorder}`,
              zIndex: 900,                // under the receipt (which is 1200)
              display: "flex",
              flexDirection: "column",
              boxShadow: "-16px 0 48px rgba(0,0,0,.35)",
              
            }}
          >
            {/* header */}
            <Box
  sx={{
    px: 1,
    py: 0.75,
    borderBottom: "1px solid rgba(255,255,255,.1)",
    display: "flex",
    alignItems: "center",
    gap: 1,
  }}
>
<IconButton size="small" onClick={handleSeatsExit} sx={{ color: "white" }} title="Затвори">
    <CloseIcon />
  </IconButton>

  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontWeight: 900,
        fontSize: { xs: 16, sm: 18, md: 20 },
        lineHeight: 1.15,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {selectedMovie?.MOVIE_NAME || "—"}
    </Typography>
    <Typography
      sx={{
        opacity: 0.9,
        fontSize: { xs: 12, sm: 13 },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      Зала {hallTitle || "—"} • {showDatePretty} • {String(showTimeStr).slice(0, 5)}
    </Typography>

    {selectedShowtime && getShowtimeMetaChips(selectedShowtime).length > 0 && (
      <Box sx={{ mt: 0.6, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {getShowtimeMetaChips(selectedShowtime).map((ch) => (
          <Pill
            key={`hdr-${ch.key}`}
            sx={{
              fontSize: 11,
              px: 0.9,
              py: 0.25,
              bgcolor: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,.14)",
            }}
          >
            {ch.label}
          </Pill>
        ))}
      </Box>
    )}
  </Box>
</Box>


  
            {/* body — your EXISTING seat-map UI, unchanged */}
            <Box sx={{ flex: 1, minHeight: 0, p: 1, display: "flex", flexDirection: "column" }}>
              {/* ↓↓↓ keep your current seat-map code here (the same JSX you already render in tickets) ↓↓↓ */}
              {/* it uses gridHostRef, totalScale, maxRow, rowMeta, seatByCoord, etc. — no changes needed */}
              <Paper sx={{ bgcolor: UI.card, color: "#fff", p: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, position: "relative" }}>
                <Box
                  ref={gridHostRef}
                  onScroll={handleGridScroll}
                  onWheel={handleWheelZoom}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    overflow: totalScale > 1 ? "auto" : "hidden",
                    p: 1,
                    overscrollBehavior: "contain",
                    cursor: "default",
                    ...(totalScale <= 1 ? { "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" } : {}),
                  }}
                >
                  {selectedShowtime && maxRow > 0 ? (
                    <Box sx={{ transform: `scale(${totalScale})`, transformOrigin: "top center" }}>
                      <Box sx={{ bgcolor: UI.pill, color: UI.text, textAlign: "center", px: 2, py: .5, borderRadius: 1, mb: 1 }}>
                        ЕКРАН
                      </Box>
  
                      {Array.from({ length: maxRow }, (_, rIdx) => {
  const row = rIdx + 1;
  const meta = rowMeta[row];
  const rowHasAny =
    meta && meta.minCol !== undefined && meta.maxCol !== undefined && meta.maxCol >= meta.minCol;
    if (!rowHasAny) {
      // Bigger gap for missing row numbers (visual separation)
      return (
        <Box
          key={`row-${row}-empty`}
          sx={{ height: Math.round(seatSize * 0.55) }}   // 👈 adjust: 0.45–0.90
        />
      );
    }
    
  const usedSpan  = meta.maxCol - meta.minCol + 1; // visual span incl. holes
  const totalCols = maxColOverall;

  // optional per-row extra padding if you have such fields on any seat of the row
  const extraLeft  = Number(meta.left_pad  || meta.leftPad  || 0);  // seats-worth
  const extraRight = Number(meta.right_pad || meta.rightPad || 0);

  const freeSpace = Math.max(0, totalCols - usedSpan - extraLeft - extraRight);

  const rawOffset =
    meta.align === "center"
      ? Math.floor(freeSpace / 2) + extraLeft
      : meta.align === "right"
      ? freeSpace + extraLeft
      : extraLeft;

  const offset = Math.max(0, Math.min(rawOffset, Math.max(0, totalCols - usedSpan)));

  const rowLabel = meta.row_label || `Ред ${row}`;

  return (
    <Box
      key={`row-${row}`}
      sx={{ display: "flex", alignItems: "center", gap: .5, justifyContent: "center", mb: `${rowGap}px` }}
    >
      <Box sx={{ width: leftLabelW, textAlign: "right", pr: 1, opacity: .8 }}>{rowLabel}</Box>

  {/* seats only, centered */}
  <Box sx={{ display: "flex", justifyContent: "center" }}>
    {Array.from({ length: totalCols }, /* ... render seats as you already do ... */)}
  </Box>


      {/* render the full width; map visible column -> real column via offset */}
      {Array.from({ length: totalCols }, (_, cIdx) => {
        const displayCol = cIdx + 1;
        const realCol    = displayCol - offset; // <= this creates the "leading spaces"
        const seat       = seatByCoord[`${row}-${realCol}`];

        // no seat at that real column -> keep an empty placeholder box (preserves internal holes)
        if (!seat) {
          return <Box key={`empty-${row}-${displayCol}`} sx={{ width: seatSize, height: seatSize }} />;
        }

        const id       = seat.seat_id;
        const status   = statusMap[id];
        const selected = selectedSeats.has(id);
        const isMine   = currentReservation && reservationSeatIds.has(id);

        let bg = seatTypeFreeColor(seat.seat_type);
let fg = "#fff";
let border = "2px solid transparent";
let boxShadow = "0 1px 0 rgba(0,0,0,.20)";

if (status === "sold")          { bg = UI.seatSold;     fg = "#fff"; }
else if (isMine)                { bg = UI.seatMine;     fg = "#fff"; border = `2px solid ${UI.cta}`; }
else if (status === "reserved") { bg = UI.seatReserved; fg = "#111"; }

// ✅ Selected = keep the seat’s base color, but add a strong ring
if (selected && !isMine) {
  bg = UI.seatSelected;        // <-- add this (fill)
  fg = "#fff";                 // keep text readable
  border = "2px solid rgba(255,255,255,.95)";
  boxShadow = "0 0 0 3px rgba(242,184,74,.95), 0 1px 0 rgba(0,0,0,.20)";
}

        const display = seat.label_text || `${realCol}`;
        let tooltip = `${rowLabel} — Място ${display}`;

        // Reserved seat: show reservation info on hover tooltip
        const _st = String(statusMap?.[seat?.seat_id] || '').toLowerCase();
        if (_st === 'reserved' || _st === 'hold') {
          const meta = reservedMetaMap?.[seat?.seat_id] || null;
          if (meta && (meta.customer_name || meta.customer_phone || meta.reservation_group_id)) {
            const who = (meta.customer_name || meta.customer_phone || 'Резервация').toString().trim();
            const cnt = Number(meta.group_size || 0) > 0 ? Number(meta.group_size) : null;
            tooltip = `${rowLabel} — Място ${display} • ${who}${cnt ? ` (${cnt} места)` : ''}`;
          }
        }

        return (
          <Box
            key={`seat-${id}`}
            sx={{
              width: seatSize, height: seatSize, bgcolor: bg, color: fg,
              fontSize: Math.max(12, Math.round(seatSize * 0.42)), fontWeight: 800, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: `${Math.max(8, Math.round(seatSize * 0.25))}px`,
              userSelect: "none", 
              border: (hoverReservationGroup && (reservedMetaMap?.[seat?.seat_id]?.reservation_group_id === hoverReservationGroup)) ? `2px solid #fff` : border,


              boxShadow: boxShadow,

            }}
            onPointerDown={(e) => {
              if (e.button !== 0) return;     // only left button
              e.preventDefault();
              e.stopPropagation();
              startDragOn(seat);
            }}
            // when painting across seats while holding the button
            onPointerEnter={() => {
              const st = statusMap?.[seat?.seat_id];
              if (st === "reserved" || st === "hold") {
                const g = reservedMetaMap?.[seat?.seat_id]?.reservation_group_id || null;
                if (g) setHoverReservationGroup(g);
              } else {
                if (hoverReservationGroup) setHoverReservationGroup(null);
              }
              continueDragOn(seat);
            }}
            // extra safety if pointerup fires on the same element
            onPointerUp={() => endDrag()}
            onPointerLeave={() => {
              // clear hover highlight when leaving seats grid
              // (will be re-set on next reserved seat hover)
              // do not clear if moving within same reservation quickly
            }}
            title={tooltip}
            style={{ touchAction: "none" }}   // keeps touch from panning while dragging (optional)
          >
            {display}
          </Box>




        );
      })}

      <Box sx={{ width: rightLabelW, textAlign: "left", pl: 1, opacity: .8 }}>{rowLabel}</Box>
    </Box>
  );
})}

                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ opacity: .7 }}>Изберете час за да видите схемата</Typography>
                  )}
                </Box>
  
                {/* bottom summary & zoom (reuse your existing controls) */}
                <Divider sx={{ borderColor: UI.divider, mt: 1, mb: 1 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>

                 <SeatTypeStrip
   seatPricing={seatPricing}
   value={selectedSeatType}
   onChange={(v) => setSelectedSeatType(v)}
   sx={{ mr: 1 }}
 />
 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
   <Pill active={!!selectedPromo} onClick={() => setPromoDlgOpen(true)}>
     🎟️ {selectedPromo ? `Промо ${selectedPromo.code}` : "Промо: избери"}
   </Pill>
   {selectedPromo && (
     <Pill onClick={() => { setSelectedPromo(null); setPromoEffectPreview(null); }} sx={{ bgcolor: "rgba(255,255,255,.10)" }}>
       ✕
     </Pill>
   )}
 </Box>
 {SHOW_SEAT_SUMMARY && (
   <Typography sx={{ alignSelf: "center" }}>
     Места: <strong>{selectedSeatLabels.join(", ") || "-"}</strong> • Тип за нови:{" "}
     <strong>{seatTypeLabelBG(selectedSeatType) || "-"}</strong> • Общо:{" "}
     <strong>{formatMainWithSecondary(ticketTotalBGN)}</strong>
   </Typography>
 )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                    <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, +(z / 1.1).toFixed(3)))} sx={{ width: 32, height: 32, color: UI.text, border: `1px solid ${UI.panelBorder}` }}>−</IconButton>
                    <IconButton size="small" onClick={() => setZoom((z) => Math.min(4, +(z * 1.1).toFixed(3)))} sx={{ minWidth: 44, height: 32, borderColor: UI.panelBorder, color: UI.text }}>+</IconButton>
                    <Button size="small" variant="outlined" onClick={resetZoom} sx={{ minWidth: 44, height: 32, borderColor: "#555", color: "#fff" }}>FIT</Button>
                    <Typography variant="caption" sx={{ ml: .5, minWidth: 44, textAlign: "center", opacity: .9 }}>
                      {Math.round(fitScale * zoom * 100)}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Slide>
      </Box>
    );
  };

  
// top-level, keep this single source of truth
const GROUP_ORDER = [
  "Пуканки",
  "Студени напитки",
  "Бира",
  "Кафе / Топли напитки",
  "Аксесоари",
  "Други",
];



  const renderBarPane = () => {
 
// put this near the top of renderBarPane (so it can see incBar/decBar)
const handleBarCardMouse = (a) => (e) => {
  // Right click or context menu → decrease
  if (e.type === "contextmenu" || e.button === 2) {
    e.preventDefault();
    e.stopPropagation();
    decBar(a);
    return;
  }
  // Left click (primary) → increase
  if (e.type === "click" || e.button === 0) {
    e.preventDefault();
    e.stopPropagation();
    incBar(a);
  }
};


    // --- grouping & sorting: Пуканки → Кафе/Топли → Студени → Бира → Други ---

    // 2) update classifyGroup



    // put this near classifyGroup
const hasWord = (s, word) =>
  new RegExp(`(?<!\\p{L})${word}(?!\\p{L})`, "iu").test(s); // Unicode letters

const hasAnyWord = (s, words) => words.some(w => hasWord(s, w));


const classifyGroup = (a) => {
  const s = String(`${a.article_name || ""} ${a.article_type || ""}`).toLowerCase();

  if (/(пукан|попкорн|popcorn)/i.test(s)) return "Пуканки";
  if (hasAnyWord(s, ["очила", "3d очила", "3д очила", "3d glasses"])) return "Аксесоари";
  if (hasAnyWord(s, ["бира", "beer", "lager", "ale", "ipa", "stout", "heineken", "амстел", "каменица", "carlsberg"]))
    return "Бира";

  const isEnergy = /(енерг(ий|и)н|energy|red\s*bull|monster|hell|burn)/i.test(s);
  const isIcedOrCold =
    /(айс|iced|ice[-\s]?|леден|студен|фрапе|фрапучино|ксиксо|xixo)/i.test(s) ||
    hasAnyWord(s, ["ice tea", "леден чай", "студен чай"]);

  const hasTea    = hasAnyWord(s, ["чай", "tea"]);
  const hasCoffee = /(каф|coffee|espresso|еспресо|лате|латте|cappuccino|капучино)/i.test(s);
  const hasWarm   = /(топл[аоии]?|горещ|hot|warm)/i.test(s) || /топли\s*напитк/i.test(s);

  const isGenericCold =
    isEnergy ||
    isIcedOrCold ||
    hasAnyWord(s, ["кола","coca","fanta","sprite","вода","devin","water","сок","juice","айрян","айран","безалкохолно","напитка","drink"]);

  if (isGenericCold) return "Студени напитки";
  if (hasCoffee || hasWarm || (hasTea && !isIcedOrCold)) return "Кафе / Топли напитки";

  return "Други";
};


    const groupPriority = (g) => {
      const i = GROUP_ORDER.indexOf(g);
      return i === -1 ? 98 : i;
    };

    const sortItemsInGroup = (group, items) =>
      [...items].sort((a, b) => {
        const pa = Number(a.price ?? 0);
        const pb = Number(b.price ?? 0);
        if (pa !== pb) return pa - pb; // cheaper first
        return String(a.article_name || "").localeCompare(String(b.article_name || ""), "bg", {
          numeric: true,
          sensitivity: "accent",
        });
      });




    // --- build groups from barArticles ---
    const groupsMap = new Map();
    for (const a of barArticles) {
      const key = classifyGroup(a);
      if (!groupsMap.has(key)) groupsMap.set(key, []);
      groupsMap.get(key).push(a);
    }

    // sort group names by priority, then by locale
    const groupNames = Array.from(groupsMap.keys()).sort((a, b) => {
      const pa = groupPriority(a), pb = groupPriority(b);
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b, "bg", { sensitivity: "accent" });
    });

    return (
      <Box
        sx={{
          p: 2,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: UI.bg,
          color: UI.text,
          // used for responsive card sizing
          "--barScale": barZoom,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Typography variant="h6">Бар артикули</Typography>

          {/* zoom controls (reflow cards) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setBarZoom((z) => clamp(+(z / 1.1).toFixed(3), 0.6, 1.6))}
              sx={{ width: 32, height: 32, color: UI.text, border: `1px solid ${UI.panelBorder}` }}
              title="Zoom out"
            >
              −
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setBarZoom((z) => clamp(+(z * 1.1).toFixed(3), 0.6, 1.6))}
              sx={{ width: 32, height: 32, color: UI.text, border: `1px solid ${UI.panelBorder}` }}
              title="Zoom in"
            >
              +
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBarZoom(1)}
              sx={{ minWidth: 64, height: 32, borderColor: UI.panelBorder, color: UI.text }}
              title="Reset"
            >
              100%
            </Button>
            <Typography variant="caption" sx={{ minWidth: 44, textAlign: "center", opacity: 0.9 }}>
              {Math.round(barZoom * 100)}%
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            pr: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {groupNames.map((group) => {
            const items = sortItemsInGroup(group, groupsMap.get(group) || []);
            if (!items.length) return null;

            return (
              <Box key={group}>
                <Typography variant="subtitle1" sx={{ mb: 1.0, fontWeight: 800 }}>
                  {group}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    // as you zoom, cards reflow (more/less per row)
                    gridTemplateColumns: "repeat(auto-fill, minmax(calc(220px * var(--barScale)), 1fr))",
                    gap: "calc(12px * var(--barScale))",
                  }}
                >
                  {items.map((a) => {
                    const inCart = barCart.get(a.article_id)?.qty || 0;
                    const imgSrc = a.image_path ? `/${String(a.image_path).replace(/\\/g, "/")}` : null;

                    return (
<Paper
  key={a.article_id}
  // +1 on left click
  onClick={(e) => {
    // React's onClick is left-button, so this is safe
    e.preventDefault();
    e.stopPropagation();
    incBar(a);
  }}
  // −1 on right click (context menu)
  onContextMenu={(e) => {
    e.preventDefault();   // suppress menu
    e.stopPropagation();
    decBar(a);
  }}
  sx={{
    padding: "calc(8px * var(--barScale))",
    bgcolor: inCart ? UI.seatFree : UI.surface,
    color: inCart ? "#111" : UI.text,
    border: `1px solid ${UI.panelBorder}`,
    borderRadius: "calc(16px * var(--barScale))",
    display: "flex",
    flexDirection: "column",
    gap: "calc(6px * var(--barScale))",
    cursor: "pointer",
    userSelect: "none",
  }}
  title="Ляв клик: +1 • Десен клик: -1"
>


                        <Box sx={{ textAlign: "center", fontWeight: 700, minHeight: "calc(40px * var(--barScale))", fontSize: "calc(15px * var(--barScale))" }}>
                          {a.article_name}
                        </Box>

                        {imgSrc ? (
                          <Box sx={{ height: "calc(140px * var(--barScale))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <img
                              alt={a.article_name}
                              src={imgSrc}
                              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                            />
                          </Box>
                        ) : null}

                        <Box sx={{ textAlign: "center", fontSize: "calc(18px * var(--barScale))", fontWeight: 800 }}>
                          {formatMainWithSecondary(Number(a.price))}
                        </Box>
                        <Typography
  sx={{
    textAlign: "center",
    fontSize: "calc(14px * var(--barScale))",
    fontWeight: 400,          // bold
    letterSpacing: 0.2,
    mt: 0.25,
  }}
>
  Наличност:{" "}
    {a.quantity}
</Typography>


                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "calc(2px * var(--barScale))", marginTop: "calc(16px * var(--barScale))" }}>
  <Button
    variant="contained"
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); decBar(a); }}
    disabled={!inCart}
    sx={{ bgcolor: UI.warn, minHeight: "calc(36px * var(--barScale))", fontSize: "calc(16px * var(--barScale))" }}
  >
    −
  </Button>

  {/* 👇 replace this block */}
  <QtyBadge value={inCart} scale={barZoom} />

  <Button
    variant="contained"
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); incBar(a); }}
    disabled={inCart >= a.quantity}
    sx={{ bgcolor: UI.seatFree, color: "#111", minHeight: "calc(36px * var(--barScale))", fontSize: "calc(16px * var(--barScale))" }}
  >
    +
  </Button>
</Box>

                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            );
          })}

          {!groupNames.length && (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Няма бар артикули за това кино.
            </Typography>
          )}
        </Box>
      </Box>
    );
  };


  // --- Reservations pane (search + list) ---
  const renderReservationsPane = () => {
    return (
      <Box
        sx={{
          p: 2,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: UI.bg,
          color: UI.text,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Резервации
        </Typography>

        <Paper sx={{ p: 2, mb: 1, bgcolor: UI.selectorBg, color: UI.text, border: `1px solid ${UI.panelBorder}` }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              label="Търсене (име, телефон, имейл, филм...)"
              value={resSearch}
              onChange={(e) => setResSearch(e.target.value)}
              size="small"
              sx={{ flex: 1, bgcolor: UI.selectorBg, borderRadius: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={resUseDate}
                  onChange={(e) => setResUseDate(e.target.checked)}
                />
              }
              label="По дата"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#000",       // черен текст
                },
              }}
            />

            <DatePicker
              value={resDate}
              onChange={(v) => setResDate(v)}
              disabled={!resUseDate}
              slotProps={{
                textField: {
                  size: "small",
                  label: "Дата",
                  sx: { bgcolor: UI.selectorBg, borderRadius: 1, minWidth: 150 },
                },
              }}
            />

            <TextField
              label="Статус"
              value={resStatus}
              onChange={(e) => setResStatus(e.target.value)}
              size="small"
              select
              SelectProps={{ native: true }}
              sx={{ minWidth: 140, bgcolor: UI.selectorBg, borderRadius: 1 }}
            >
              <option value="active">Активни</option>
              <option value="sold">Продадени</option>
              <option value="cancelled">Отменени</option>
              <option value="all">Всички</option>
            </TextField>

            <TextField
              label="Сортиране"
              value={resSortKey}
              onChange={(e) => setResSortKey(e.target.value)}
              size="small"
              select
              SelectProps={{ native: true }}
              sx={{ minWidth: 150, bgcolor: UI.selectorBg, borderRadius: 1 }}
            >
              <option value="reservation">По резервация</option>
              <option value="showtime">По прожекция</option>
            </TextField>

            <TextField
              label="Посока"
              value={resSortDir}
              onChange={(e) => setResSortDir(e.target.value)}
              size="small"
              select
              SelectProps={{ native: true }}
              sx={{ minWidth: 140, bgcolor: UI.selectorBg, borderRadius: 1 }}
            >
              <option value="desc">Низходящо</option>
              <option value="asc">Възходящо</option>
            </TextField>


            <Button variant="contained" onClick={searchReservations}>
              Обнови
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, pr: 1 }}>
          {resResults.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Няма резултати.
            </Typography>
          ) : (
            
            resResults.map((r) => {
              const st = r._status || normResStatus(r.status);
              const seatLabels = Array.isArray(r.seat_labels)
                ? r.seat_labels.join(", ")
                : r.seat_labels || "";

              const isNew = newResIds.has(r.id);
              const normalBg = UI.card || UI.surfaceAlt || UI.bg;
              const newBg = UI.surface; // light gray highlight
              const canLoad = st === "active";
              const canCancel = st === "active";
              const canPurge = st === "cancelled";

              return (
                <Paper
                  key={r.id}
                  sx={{
                    p: 1.25,
                    mb: 1,
                    bgcolor: isNew ? newBg : normalBg,
                    color: UI.text,
                    border: `1px solid ${UI.panelBorder}`,
                    display: "grid",
                    gap: 0.5,
                    animation: isNew ? "resBlink 1.05s ease-in-out infinite" : "none",
                    "@keyframes resBlink": {
                      "0%":   { filter: "brightness(1.05)" },
                      "50%":  { filter: "brightness(0.85)" },
                      "100%": { filter: "brightness(1.05)" },
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isNew ? (
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ff3b30" }} />
                    ) : null}
                    <Typography sx={{ fontWeight: 700 }}>
                      {r.movie_title || "—"}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Дата/час: {fmtBGDate(r.show_date) || "—"} {fmtHM(r.show_time)}
                    {" • "}Зала: {r.hall_name || "—"}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Клиент: {r.customer_name || "—"}
                    {r.customer_phone ? ` • ${r.customer_phone}` : ""}
                    {r.customer_email ? ` • ${r.customer_email}` : ""}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    № (ORDER_ID): {r.order_id || "—"}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Места: {seatLabels || "—"}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Статус: {st === "active" ? "Активна" : st === "sold" ? "Продадена" : "Отменена"}
                  </Typography>

                  {canLoad || canCancel || canPurge ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {canLoad ? (
                        <Button
                          variant="contained"
                          onClick={() => loadReservationIntoBon(r.id)}
                          sx={{ bgcolor: "#1976d2" }}
                        >
                          Зареди в бон
                        </Button>
                      ) : null}

                      {canCancel ? (
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={busyDeletes.has(r.id)}
                          onClick={() => {
                            if (window.confirm("Отмяна на резервацията?"))
                              cancelReservation(r.id);
                          }}
                        >
                          Отмени
                        </Button>
                      ) : null}

                      {canPurge ? (
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={busyPurges.has(r.id)}
                          onClick={() => {
                            if (window.confirm("Да изтрия ли напълно отменената резервация (от списъка Отменени)?"))
                              purgeCancelledReservation(r.id);
                          }}
                        >
                          Изтрий
                        </Button>
                      ) : null}
                    </Stack>
                  ) : null}
                </Paper>
              );
            })
          )}
        </Box>
      </Box>
    );
  };

  
  const renderProjectionsPane = () => {
    if (!cinemaId) {
      return (
        <Box
          sx={{
            p: 2,
            height: "100vh",
            bgcolor: UI.bg,
            color: UI.text,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Прожекции
          </Typography>
          <Typography variant="body2">
            Няма зададено кино за този POS. Моля влезте с профил за конкретно кино.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 0,
          height: "100vh",
          bgcolor: UI.bg,
          color: UI.text,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* вътрешни табчета */}
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            gap: 1,
            borderBottom: `1px solid ${UI.panelBorder}`,
            bgcolor: UI.nav,
          }}
        >
          <Button
            size="small"
            variant={projMode === "showtimes" ? "contained" : "outlined"}
            onClick={() => setProjMode("showtimes")}
          >
            Прожекции ({cinemaName || `кино #${cinemaId}`})
          </Button>
          <Button
            size="small"
            variant={projMode === "movies" ? "contained" : "outlined"}
            onClick={() => setProjMode("movies")}
          >
            Филми (TMDb / ръчно)
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {projMode === "showtimes" ? (
            <AddShowtimes onlyCinemaId={cinemaId} hideBack />
          ) : (
            <AdminMovieImport hideBack />
          )}
        </Box>
      </Box>
    );
  };

  const renderArchivePane = () => {
    if (!cinemaId) {
      return (
        <Box sx={{ p: 2, height: "100vh", bgcolor: UI.bg, color: UI.text }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Архив / Backup
          </Typography>
          <Typography variant="body2">
            Няма зададено кино за този POS.
          </Typography>
        </Box>
      );
    }

    const fromStr = archiveFrom?.format("YYYY-MM-DD");
    const toStr = archiveTo?.format("YYYY-MM-DD");

    return (
      <Box sx={{ p: 2, ...UI.panelAlt }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Архив / Backup (за вашето кино)
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Период
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="От"
                value={archiveFrom}
                onChange={(v) => setArchiveFrom(v || dayjs())}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="До"
                value={archiveTo}
                onChange={(v) => setArchiveTo(v || dayjs())}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => { setArchiveFrom(dayjs()); setArchiveTo(dayjs()); }}>
                  Днес
                </Button>
                <Button variant="outlined" onClick={() => { const y = dayjs().subtract(1,"day"); setArchiveFrom(y); setArchiveTo(y); }}>
                  Вчера
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Изтегляне
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Excel е препоръчителен (много таблици). SQL е "DATA ONLY" архив – за прехвърляне/съхранение.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              onClick={() =>
                downloadToolsFile(
                  `${API_BASE}/api/tools/export-xlsx?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`,
                  `cinema_${cinemaId}_${fromStr}_to_${toStr}.xlsx`
                )
              }
            >
              Export Excel (.xlsx)
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                downloadToolsFile(
                  `${API_BASE}/api/tools/backup-sql?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`,
                  `cinema_${cinemaId}_${fromStr}_to_${toStr}.sql`
                )
              }
            >
              Export SQL (data)
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  };





  const renderRightPane = () => {
    if (activeTab === "tickets") return renderTicketsPane();
    if (activeTab === "bar") return renderBarPane();
    if (activeTab === "storno") return canStorno ? renderStornoPane() : (
      <Box sx={{ p: 2, color: "#fff" }}>Нямате право за Сторно.</Box>
    );
    if (activeTab === "reservations") return renderReservationsPane();
    if (activeTab === "archive") return renderArchivePane();
    if (activeTab === "projections") return renderProjectionsPane();


// 💵 NEW: Cash drawer tab
  if (activeTab === "cashdrawer") {

const dayOptions = Array.from({ length: 7 }).map((_, idx) => {
  const d = dayjs().subtract(idx, "day");
  const label =
    idx === 0 ? "Днес" : idx === 1 ? "Вчера" : d.format("DD.MM.YYYY");
  return { value: idx, label };
});

const fmtPair = (eur, bgn) => {
  const parts = [];
  if (eur != null && eur !== "") parts.push(formatMoney(Number(eur), "EUR"));
  if (bgn != null && bgn !== "") parts.push(formatMoney(Number(bgn), "BGN"));
  return parts.length ? parts.join(" / ") : "?";
};

    return (
      <Box sx={{ p: 2, ...UI.panelAlt }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Каса / касова наличност
        </Typography>

                {/* 1) Операция в касата – служебно въвеждане / извеждане */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Операция в касата (служебно въвеждане / извеждане)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Изберете валута, въведете сума и натиснете <strong>Въведи</strong> или{" "}
            <strong>Изведи</strong>. (По протокол служебното извеждане е отрицателна сума.)
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label={`Сума (${cashCurrency})`}
                type="number"
                fullWidth
                size="small"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                label="Валута"
                fullWidth
                size="small"
                value={cashCurrency}
                onChange={(e) => setCashCurrency(e.target.value)}
              >
                {allowEUR && <MenuItem value="EUR">EUR (€)</MenuItem>}
                {allowBGN && <MenuItem value="BGN">BGN (лв)</MenuItem>}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Коментар"
                fullWidth
                size="small"
                value={cashComment}
                onChange={(e) => setCashComment(e.target.value)}
                placeholder="напр. Каса от вчера / корекция"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Stack direction="column" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={cashBusy || !cashAmount || !cinemaId}
                  onClick={() => handleCashDrawerSubmit("IN")}
                >
                  {cashBusy ? "..." : "Служебно въведи"}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  disabled={cashBusy || !cashAmount || !cinemaId}
                  onClick={() => handleCashDrawerSubmit("OUT")}
                >
                  {cashBusy ? "..." : "Служебно изведи"}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {cashLast && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Последна операция:&nbsp;
                <strong>
                  {cashLast.direction === "IN"
                    ? "служебно въвеждане"
                    : cashLast.direction === "OUT"
                    ? "служебно извеждане"
                    : cashLast.direction === "STATE"
                    ? "прочит"
                    : cashLast.direction}
                </strong>{" "}
                — сума:{" "}
                <strong>
                  {cashLast.direction === "STATE"
                    ? "—"
                    : formatMoney(
                        Math.abs(Number(cashLast.amount || 0)),
                        cashLast.currency || cashCurrency
                      )}
                </strong>
                .
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Наличност (EUR / BGN):{" "}
                <strong>
                  {fmtPair(
                    cashLast.drawer?.cash?.EUR,
                    cashLast.drawer?.cash?.BGN
                  )}
                </strong>
                . Служебно въведени за деня (EUR / BGN):{" "}
                <strong>
                  {fmtPair(
                    cashLast.drawer?.serv_input?.EUR,
                    cashLast.drawer?.serv_input?.BGN
                  )}
                </strong>
                , служебно изведени (EUR / BGN):{" "}
                <strong>
                  {fmtPair(
                    cashLast.drawer?.serv_output?.EUR,
                    cashLast.drawer?.serv_output?.BGN
                  )}
                </strong>
                .
              </Typography>
            </Box>
          )}
        </Paper>

        {/* 2) Прочитане на текущата касова наличност от фискалното */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Прочитане на касата (текущо състояние)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Чете текущата касова наличност от фискалното устройство чрез
            FDServiceSum (Amount=0). Записва се и в таблица{" "}
            <code>fiscal_service_sum</code>.
          </Typography>

          {cashStateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cashStateError}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              mb: cashState ? 2 : 0,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              disabled={cashStateLoading || !cinemaId}
              onClick={handleCashDrawerReadState}
            >
              {cashStateLoading
                ? "Четене..."
                : "Прочети от фискалното устройство"}
            </Button>
          </Box>

          {cashState && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Последно прочитане:&nbsp;
                <strong>
                  {cashState.read_at
                    ? dayjs(cashState.read_at).format(
                        "DD.MM.YYYY HH:mm:ss"
                      )
                    : "—"}
                </strong>
              </Typography>
                            <Typography variant="body2">
                Наличност в касата (EUR / BGN):&nbsp;
                <strong>
                  {fmtPair(
                    cashState.drawer?.cash?.EUR,
                    cashState.drawer?.cash?.BGN
                  )}
                </strong>
                .
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Служебно въведени за деня (EUR / BGN):{" "}
                <strong>
                  {fmtPair(
                    cashState.drawer?.serv_input?.EUR,
                    cashState.drawer?.serv_input?.BGN
                  )}
                </strong>
                , служебно изведени (EUR / BGN):{" "}
                <strong>
                  {fmtPair(
                    cashState.drawer?.serv_output?.EUR,
                    cashState.drawer?.serv_output?.BGN
                  )}
                </strong>
                .
              </Typography>
            </Box>
          )}
        </Paper>

        {/* 3) История по дата – „dropbox“ за предишни дни */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            История на касата по дата
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Данните се четат от базата (
            <code>fiscal_service_sum</code>), където всеки ред е резултат от{" "}
            <code>FDServiceSum</code>. Така можеш да видиш касата за
            предишни дни.
          </Typography>

          {cashHistoryError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {cashHistoryError}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Дата"
                value={cashHistoryDayOffset}
                onChange={(e) =>
                  setCashHistoryDayOffset(
                    Number(e.target.value) || 0
                  )
                }
              >
                {dayOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={cashHistoryLoading || !cinemaId}
                onClick={handleCashDrawerHistory}
              >
                {cashHistoryLoading ? "Зареждане..." : "Прочети историята"}
              </Button>
            </Grid>
          </Grid>

          {cashHistoryRows && cashHistoryRows.length > 0 && (
            <Table size="small">
                            <TableHead>
                <TableRow>
                  <TableCell>Дата/час</TableCell>
                  <TableCell>Посока</TableCell>
                  <TableCell align="right">Сума (EUR / BGN)</TableCell>
                  <TableCell align="right">Наличност (EUR / BGN)</TableCell>
                  <TableCell align="right">Сл. въведени (EUR / BGN)</TableCell>
                  <TableCell align="right">Сл. изведени (EUR / BGN)</TableCell>
                  <TableCell>Бележка</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cashHistoryRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {row.created_at
                        ? dayjs(row.created_at).format(
                            "DD.MM.YYYY HH:mm:ss"
                          )
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {row.direction === "IN"
                        ? "ВЪВЕЖДАНЕ"
                        : row.direction === "OUT"
                        ? "ИЗВЕЖДАНЕ"
                        : "ПРОЧИТ"}
                    </TableCell>
                                        <TableCell align="right">
                      {row.direction === "STATE"
                        ? "—"
                        : fmtPair(
                            row.amount_eur != null ? Math.abs(Number(row.amount_eur)) : null,
                            row.amount_bgn != null ? Math.abs(Number(row.amount_bgn)) : null
                          )}
                    </TableCell>
                                        <TableCell align="right">
                      {fmtPair(
                        row.drawer?.cash?.EUR,
                        row.drawer?.cash?.BGN
                      )}
                    </TableCell>
                                        <TableCell align="right">
                      {fmtPair(
                        row.drawer?.serv_input?.EUR,
                        row.drawer?.serv_input?.BGN
                      )}
                    </TableCell>
                                        <TableCell align="right">
                      {fmtPair(
                        row.drawer?.serv_output?.EUR,
                        row.drawer?.serv_output?.BGN
                      )}
                    </TableCell>
                    <TableCell>{row.comment || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    );
  }

  // 🧾 NEW: Fiscal tab (X/Z + history)
  if (activeTab === "fiscal") {
    const dayOptions = Array.from({ length: 7 }).map((_, idx) => {
      const d = dayjs().subtract(idx, "day");
      const label = idx === 0 ? "Днес" : idx === 1 ? "Вчера" : d.format("DD.MM.YYYY");
      return { value: idx, label };
    });

    const runXAndRefresh = async () => {
      await doXReport();
      await handleFiscalReportsHistory();
    };
    const runZAndRefresh = async () => {
      await doZReport();
      await handleFiscalReportsHistory();
    };

    return (
      <Box sx={{ p: 2, ...UI.panelAlt }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Фискал
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            X / Z отчет
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Натискането на бутоните отпечатва отчета на фискалното устройство и записва човеко-четимо обобщение в базата.
            (RAW данните се пазят само за диагностика, но прегледът по подразбиране е „Отчет“.)
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button variant="contained" disabled={!cinemaId || reportBusy} onClick={runXAndRefresh}>
              {reportBusy ? "Печат…" : "X отчет"}
            </Button>
            <Button variant="contained" color="error" disabled={!cinemaId || reportBusy} onClick={runZAndRefresh}>
              {reportBusy ? "Печат…" : "Z отчет"}
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            История на фискалните отчети по дата
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Данните се четат от базата (<code>fiscal_report_log</code>). Всеки ред е X, Z или AUTO_Z.
          </Typography>

          {fiscalHistoryError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {fiscalHistoryError}
            </Alert>
          )}

          
<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Период"
                value={fiscalHistoryMode}
                onChange={(e) => setFiscalHistoryMode(String(e.target.value || "day"))}
              >
                <MenuItem value="day">Ден</MenuItem>
                <MenuItem value="month">Месец</MenuItem>
                <MenuItem value="year">Година</MenuItem>
                <MenuItem value="range">От/до</MenuItem>
              </TextField>
            </Grid>

            {fiscalHistoryMode === "range" ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="От"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={fiscalHistoryFrom}
                    onChange={(e) => setFiscalHistoryFrom(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="До"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={fiscalHistoryTo}
                    onChange={(e) => setFiscalHistoryTo(e.target.value)}
                  />
                </Grid>
              </>
            ) : fiscalHistoryMode === "month" ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Месец"
                  type="month"
                  InputLabelProps={{ shrink: true }}
                  value={fiscalHistoryMonth}
                  onChange={(e) => setFiscalHistoryMonth(e.target.value)}
                />
              </Grid>
            ) : fiscalHistoryMode === "year" ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Година"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  value={fiscalHistoryYear}
                  onChange={(e) => setFiscalHistoryYear(e.target.value)}
                  inputProps={{ min: 2000, max: 2100, step: 1 }}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Ден"
                  value={fiscalHistoryDayOffset}
                  onChange={(e) => setFiscalHistoryDayOffset(Number(e.target.value) || 0)}
                >
                  {dayOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFiscalReportsHistory}
              >
                {fiscalHistoryLoading ? "Зареждане..." : "Прочети историята"}
              </Button>
            </Grid>
          </Grid>{fiscalHistoryRows && fiscalHistoryRows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата/час</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell align="right">Closure</TableCell>
                  <TableCell>Устройство</TableCell>
                  <TableCell>Потребител</TableCell>
                  <TableCell align="right">Файл</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fiscalHistoryRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.created_at ? dayjs(row.created_at).format("DD.MM.YYYY HH:mm:ss") : "—"}
                    </TableCell>
                    <TableCell>{row.report_type}</TableCell>
                    <TableCell align="right">{row.closure_no != null ? row.closure_no : "—"}</TableCell>
                    <TableCell>{row.device_name || row.serial_no || row.device_id}</TableCell>
                    <TableCell>{row.username || "—"}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="outlined" onClick={() => openFiscalReportPreview(row.id)}>
                          Преглед
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => downloadFiscalReport(row.id, "txt")}>
                          TXT
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>



        {/* 🧾 Bon tools: KLEN / copy / seat tickets */}
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
              Бонове — КЛЕН / Копие / Билети
            </Typography>

            <Button
              variant="contained"
              onClick={() => setFiscalBonPickerOpen(true)}
              disabled={!cinemaId || !canStorno}
            >
              Избери бон
            </Button>

            <Button
              variant="outlined"
              onClick={() => openFiscalKlen(false)}
              disabled={!fiscalSelectedBon || fiscalKlenLoading || !canStorno}
            >
              КЛЕН
            </Button>

            <Button
              variant="outlined"
              onClick={printFiscalCopy}
              disabled={!fiscalSelectedBon || fiscalCopyBusy || !canStorno}
            >
              {fiscalCopyBusy ? "Печат..." : "Копие (нефиск.)"}
            </Button>

            <Button
              variant="outlined"
              onClick={printFiscalSeatTickets}
              disabled={!fiscalSelectedBonInfo || fiscalSeatsBusy || !canStorno}
            >
              {fiscalSeatsBusy ? "Печат..." : "Билети (служ.)"}
            </Button>
          </Stack>

          {!canStorno && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Тези функции изискват право „Сторно“ (can_storno).
            </Alert>
          )}

          {(() => {
            const s = fiscalSelectedBonInfo || fiscalSelectedBon;
            if (!s) {
              return (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Не е избран бон. Натиснете <strong>Избери бон</strong>.
                </Typography>
              );
            }

            const unp = String(s.unp_code || s.bon_number || "").trim();
            const docNo = s.device_doc_no || s.deviceDocNo || "—";
            const dt = s.sale_time || s.bon_datetime || s.bonDatetime || s.device_doc_datetime || null;
            const total = Number(s.total_amount || s.totalAmount || 0);
            const pm = (s.payment_method || s.paymentMethod || "").toUpperCase() || "—";

            const movie0 = Array.isArray(s.movieInfo) && s.movieInfo.length ? s.movieInfo[0] : null;

            return (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Избран бон: <strong>{unp || "—"}</strong> • Док. №: <strong>{docNo}</strong> • Плащане:{" "}
                  <strong>{pm}</strong> • Общо: <strong>{formatMainWithSecondary(Math.abs(total))}</strong>
                </Typography>

                {dt && (
                  <Typography variant="body2" color="text.secondary">
                    Дата/час: {dayjs(dt).isValid() ? dayjs(dt).format("DD.MM.YYYY HH:mm:ss") : String(dt)}
                  </Typography>
                )}

                {movie0 && (
                  <Typography variant="body2" color="text.secondary">
                    {movie0.movie_name ? `Филм: ${movie0.movie_name}` : ""}
                    {movie0.hall_id != null ? ` • Зала: ${movie0.hall_id}` : ""}
                    {movie0.show_date ? ` • ${movie0.show_date}` : ""} {movie0.show_time ? String(movie0.show_time).slice(0, 5) : ""}
                  </Typography>
                )}

                {Array.isArray(s.items) && s.items.length > 0 && (
                  <Box sx={{ mt: 1, overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Артикул</TableCell>
                          <TableCell align="right">К-во</TableCell>
                          <TableCell align="right">Ед. цена</TableCell>
                          <TableCell align="right">Сума</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {s.items.map((it, idx) => {
                          const qty = Number(it.qty || 0);
                          const up = Number(it.unit_price || 0);
                          const sum = qty * up;
                          return (
                            <TableRow key={idx}>
                              <TableCell>{it.text || "—"}</TableCell>
                              <TableCell align="right">{qty}</TableCell>
                              <TableCell align="right">{formatMainWithSecondary(up)}</TableCell>
                              <TableCell align="right">{formatMainWithSecondary(sum)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>

        {/* Picker dialog (STORNO-style search) */}
        <Dialog open={fiscalBonPickerOpen} onClose={() => setFiscalBonPickerOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Избор на бон</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                <TextField
                  label="Търсене (UNP, Док.№, метод...)"
                  value={fiscalBonQuery}
                  onChange={(e) => setFiscalBonQuery(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Филтър"
                  value={fiscalBonType}
                  onChange={(e) => setFiscalBonType(e.target.value)}
                  size="small"
                  select
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 160 }}
                >
                  <option value="all">Всички</option>
                  <option value="sale">Само продажби</option>
                  <option value="storno">Само сторно</option>
                </TextField>
                <TextField
                  label="Лимит"
                  type="number"
                  value={fiscalBonLimit}
                  onChange={(e) => setFiscalBonLimit(Math.max(10, Math.min(500, Number(e.target.value) || 80)))}
                  size="small"
                  sx={{ width: 120 }}
                />
                <Button variant="contained" onClick={loadFiscalBonPickerRows} disabled={fiscalBonLoading || !cinemaId}>
                  {fiscalBonLoading ? "..." : "Обнови"}
                </Button>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                <TextField
                  label="От"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={fiscalBonFrom}
                  onChange={(e) => setFiscalBonFrom(e.target.value)}
                  sx={{ width: 200 }}
                />
                <TextField
                  label="До"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={fiscalBonTo}
                  onChange={(e) => setFiscalBonTo(e.target.value)}
                  sx={{ width: 200 }}
                />
                {fiscalBonError && (
                  <Alert severity="warning" sx={{ flex: 1 }}>
                    {fiscalBonError}
                  </Alert>
                )}
              </Stack>

              <Box sx={{ maxHeight: 460, overflow: "auto", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата/час</TableCell>
                      <TableCell>UNP</TableCell>
                      <TableCell>Док. №</TableCell>
                      <TableCell align="right">Сума</TableCell>
                      <TableCell>Метод</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(fiscalBonRows || []).map((r) => {
                      const unp = String(r.unp_code || r.bon_number || "").trim();
                      const isSt = Number(r.total_amount || 0) < 0 || !!r.is_storno || !!r.storno_of_bon_id;
                      return (
                        <TableRow
                          key={String(r.bon_id || unp || Math.random())}
                          hover
                          sx={{ cursor: "pointer", bgcolor: isSt ? "rgba(220,38,38,0.06)" : undefined }}
                          onClick={() => selectFiscalBonRow(r)}
                        >
                          <TableCell>
                            {r.bon_datetime ? dayjs(r.bon_datetime).format("DD.MM.YYYY HH:mm:ss") : "—"}
                          </TableCell>
                          <TableCell>{unp || "—"}</TableCell>
                          <TableCell>{r.device_doc_no || "—"}</TableCell>
                          <TableCell align="right">{formatMainWithSecondary(Math.abs(Number(r.total_amount || 0)))}</TableCell>
                          <TableCell>{(r.payment_method || "").toUpperCase() || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!fiscalBonLoading && (!fiscalBonRows || fiscalBonRows.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Няма резултати.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFiscalBonPickerOpen(false)}>Затвори</Button>
          </DialogActions>
        </Dialog>

        {/* KLEN preview dialog */}
        <Dialog open={fiscalKlenOpen} onClose={() => setFiscalKlenOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>КЛЕН (EJ) текст</DialogTitle>
          <DialogContent dividers>
            {fiscalKlenLoading && <Typography>Зареждане…</Typography>}
            {fiscalKlenError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {fiscalKlenError}
              </Alert>
            )}
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                whiteSpace: "pre-wrap",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                fontSize: 12,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                minHeight: 220,
              }}
            >
              {fiscalKlenText || ""}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => openFiscalKlen(true)} disabled={fiscalKlenLoading}>
              Обнови
            </Button>
            <Button onClick={printFiscalCopy} disabled={fiscalCopyBusy}>
              {fiscalCopyBusy ? "Печат..." : "Печат копие"}
            </Button>
            <Button onClick={() => setFiscalKlenOpen(false)}>Затвори</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }


    if (activeTab === "reports")
      return (
        <Box sx={{ p: 2, height: "100vh", overflow: "auto", bgcolor: UI.panelAlt, color: "#111" }}>
          <ReportsStandalone key={`reports-${cinemaId || "none"}`} cinemaId={cinemaId} autoRun />
        </Box>
      );
    return (
      <Box sx={{ p: 2, height: "100vh", overflow: "auto", bgcolor: UI.panelAlt, color: "#111" }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {(
            {
              storno: "Сторно Билет (скоро)",
              fiscal: "Фискал",
              archive: "Архив / Backup",
              reports: "Отчети (скоро)",
              projections: "Прожекции (скоро)",
            }[activeTab]
          ) || "Изберете модул отляво."}
        </Typography>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="bg">
      <>
        {/* left menu */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: leftNavW,
            height: "100vh",
            bgcolor: UI.card,
            color: UI.text,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 1,
            zIndex: 1200,
          }}
        >
          <Box>
          {[
  { key: "tickets",      label: "Билети",      icon: <ConfirmationNumberOutlinedIcon /> },
  { key: "bar",          label: "Бар",         icon: <LocalBarOutlinedIcon /> },
  ...(canStorno ? [{ key: "storno", label: "Сторно", icon: <UndoOutlinedIcon /> }] : []),
  { key: "reservations", label: "Резервации",  icon: <BookOnlineOutlinedIcon /> },
  { key: "cashdrawer",   label: "Каса",        icon: <AccountBalanceWalletOutlinedIcon /> },
  { key: "archive",      label: "Архив",       icon: <ArchiveOutlinedIcon /> },
  { key: "fiscal",       label: "Фискал",      icon: <PointOfSaleOutlinedIcon /> },
  { key: "reports",      label: "Отчети",      icon: <AssessmentOutlinedIcon /> },
  { key: "projections",  label: "Прожекции",   icon: <MovieOutlinedIcon /> },
].map((item) => {
  const active = activeTab === item.key;

  return (
    <Button
      key={item.key}
      fullWidth
      onClick={() => setActiveTab(item.key)}
      title={isSM ? item.label : ""} // ✅ tooltip when collapsed
      sx={{
        color: "#fff",
        justifyContent: isSM ? "center" : "flex-start",
        textTransform: "none",
        borderRadius: 2,
        mb: 0.6,
        px: isSM ? 0 : 1.25,
        py: 1.1,
        minHeight: isSM ? 52 : 44,

        bgcolor: active ? UI.navActive : "transparent",
        "&:hover": { bgcolor: active ? UI.navActive : "rgba(255,255,255,.08)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: isSM ? 0 : 1.1 }}>
        {/* icon */}
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: isSM ? 40 : 24,
            height: isSM ? 40 : 24,
            opacity: active ? 1 : 0.9,
          }}
        >
          {item.key === "reservations" ? (
            <Badge color="error" variant="dot" overlap="circular" invisible={!hasNewRes}>
              {React.cloneElement(item.icon, { sx: { fontSize: isSM ? 40 : 20 } })}
            </Badge>
          ) : (
            React.cloneElement(item.icon, { sx: { fontSize: isSM ? 40 : 20 } })
          )}
        </Box>

        {/* label (hidden on small) */}
        {!isSM && (
          <Typography sx={{ fontWeight: 400, fontSize: 18, letterSpacing: 0.2 }}>
            {item.label}
          </Typography>
        )}
      </Box>
    </Button>
  );
})}

          </Box>





  {/* Logout (red) */}
          
          <Button
            fullWidth
            onClick={handleClose}
            sx={{ backgroundColor: UI.danger, color: UI.text }}
          >
            🚫 Затвори
          </Button>
        </Box>

        {showRightReceipt && <RightReceipt />}


        {/* center */}
        <Box
          sx={{
            ml: `${leftNavW}px`,
            mr: showRightReceipt && !isSM ? `${receiptW}px` : 0,

            height: "100vh",
            overflow: reportMode ? "auto" : "hidden",
          }}
        >
          {renderRightPane()}
        </Box>

        <Dialog open={reserveDlgOpen} onClose={() => setReserveDlgOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Нова резервация</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.25} sx={{ mt: 0.5 }}>
              <TextField
                label="Име на клиент"
                value={reserveForm.name}
                onChange={(e) => setReserveForm((f) => ({ ...f, name: e.target.value }))}
                autoFocus
              />
              <TextField
                label="Телефон"
                value={reserveForm.phone}
                onChange={(e) => setReserveForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <TextField
                label="Имейл"
                value={reserveForm.email}
                onChange={(e) => setReserveForm((f) => ({ ...f, email: e.target.value }))}
              />
              <TextField 
                label="Бележка"
                value={reserveForm.note}
                onChange={(e) => setReserveForm((f) => ({ ...f, note: e.target.value }))}
                multiline
                minRows={2}
              />
              <Paper variant="outlined" sx={{ p: 1, bgcolor: "#fafafa" }}>
                <Typography variant="body2">
                  {selectedMovie?.MOVIE_NAME || "—"} • {selectedShowtime?.show_date}{" "}
                  {selectedShowtime?.show_time}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Места: {selectedSeatLabels.join(", ") || "-"}
                </Typography>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReserveDlgOpen(false)}>Отказ</Button>
            <Button variant="contained" onClick={handleReserveSave}>
              Запази
            </Button>
          </DialogActions>
        </Dialog>

        <QuickFiscalModal
          open={showQuickBon}
          onClose={(ok) => {
            setShowQuickBon(false);
            if (ok) {
              setSnackbar({
                open: true,
                severity: "success",
                message: "Изпратена команда за печат.",
              });
            }
          }}
          cinemaId={cinemaId}
        />

<AmountKeypadDialog
  open={cashOpen}
  totalBGN={totalBGN}
  onClose={() => setCashOpen(false)}
  onConfirm={({ amount, change, expr }) => {
    setCashOpen(false);
    // proceed with the normal cash sale
    confirmSale("cash");
  }}
/>


        {/* 📄 Fiscal report preview */}
        <Dialog
          open={fiscalViewOpen}
          onClose={() => setFiscalViewOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Преглед на фискален отчет
          </DialogTitle>
          <DialogContent dividers>
            {fiscalViewLoading ? (
              <Typography>Зареждане…</Typography>
            ) : fiscalViewError ? (
              <Alert severity="warning">{fiscalViewError}</Alert>
            ) : !fiscalViewRow ? (
              <Typography>Няма данни.</Typography>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Тип: <b>{fiscalViewRow.report_type}</b>
                    {fiscalViewRow.closure_no != null ? (
                      <> • Closure: <b>{fiscalViewRow.closure_no}</b></>
                    ) : null}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Дата/час:{" "}
                    <b>{fiscalViewRow.created_at ? dayjs(fiscalViewRow.created_at).format("DD.MM.YYYY HH:mm:ss") : "—"}</b>
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Устройство: <b>{fiscalViewRow.device_name || fiscalViewRow.serial_no || fiscalViewRow.device_id}</b>
                    {fiscalViewRow.username ? <> • Потребител: <b>{fiscalViewRow.username}</b></> : null}
                  </Typography>
                </Box>

                
<Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={fiscalViewMode === "original" ? "contained" : "outlined"}
                    onClick={() => setFiscalViewMode("original")}
                  >
                    RAW
                  </Button>
                  <Button
                    size="small"
                    variant={fiscalViewMode === "text" ? "contained" : "outlined"}
                    onClick={() => setFiscalViewMode("text")}
                  >
                    Отчет
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" variant="outlined" onClick={() => downloadFiscalReport(fiscalViewRow.id, "txt")}>
                    TXT
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => downloadFiscalReport(fiscalViewRow.id, "raw")}>
                    RAW
                  </Button>
                </Stack>

                <Box
                  sx={{
                    mx: "auto",
                    width: "100%",
                    maxWidth: 420,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#fff",
                    color: "#111",
                    border: "1px dashed rgba(0,0,0,.25)",
                    boxShadow: "0 8px 22px rgba(0,0,0,.18)",
                  }}
                >
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                      fontSize: 12,
                      lineHeight: 1.35,
                      whiteSpace: "pre",
                      overflowX: "auto",
                    }}
                  >
                    {String(
                      fiscalViewMode === "original"
                        ? (fiscalViewRow.report_raw_text || fiscalViewRow.report_text || "")
                        : (fiscalViewRow.report_text || fiscalViewRow.report_raw_text || "")
                    )}
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Забележка: Този преглед е записаният текст/RAW отговора в базата, показан в „касова бележка“ стил.
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFiscalViewOpen(false)}>Затвори</Button>
          </DialogActions>
        </Dialog>


        <PromotionPickerDialog
          open={promoDlgOpen}
          onClose={() => setPromoDlgOpen(false)}
          cinemaId={Number(cinemaId || 0)}
          movieId={Number(
            selectedShowtime?.MOVIE_ID ||
            selectedShowtime?.movie_id ||
            selectedShowtime?.MOVIEID ||
            selectedMovie?.MOVIE_ID ||
            selectedMovie?.movie_id ||
            0
          ) || null}
          initialScope={selectedPromo?.scope || "all"}
          onPick={(p) => {
            setSelectedPromo(p ? { ...p, promo_id: p.promo_id ?? p.id ?? null, scope: p.scope || "all" } : null);
            setPromoEffectPreview(null);
            setPromoDlgOpen(false);
          }}
        />


      {/* Reserved seat info dialog */}
      <Dialog open={reservedInfoDlgOpen} onClose={() => setReservedInfoDlgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Резервация</DialogTitle>
        <DialogContent dividers>
          {reservedInfo ? (
            <Stack spacing={1}>
              <Typography variant="body2"><b>Име:</b> {reservedInfo.customer_name || "—"}</Typography>
              <Typography variant="body2"><b>Тел:</b> {reservedInfo.customer_phone || "—"}</Typography>
              {reservedInfo.customer_email ? <Typography variant="body2"><b>Email:</b> {reservedInfo.customer_email}</Typography> : null}
              {reservedInfo.customer_note ? <Typography variant="body2"><b>Бележка:</b> {reservedInfo.customer_note}</Typography> : null}
              <Typography variant="body2"><b>Група:</b> {reservedInfo.reservation_group_id || "—"}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2">Няма данни.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservedInfoDlgOpen(false)}>Затвори</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                if (!reservedInfo?.reservation_group_id) {
                  setSnackbar({ open: true, message: "Липсва група резервация.", severity: "warning" });
                  return;
                }
                // Load reservation seats into bon: select all seats from same group currently reserved
                const gid = reservedInfo.reservation_group_id;
                const seatIds = Object.keys(reservedMetaMap || {})
                  .filter((k) => reservedMetaMap[k]?.reservation_group_id === gid)
                  .map((k) => Number(k))
                  .filter(Boolean);
                if (!seatIds.length) {
                  setSnackbar({ open: true, message: "Не са намерени места за тази резервация.", severity: "warning" });
                  return;
                }
                // mark them as selected (keep their labels/types from seats array)
                setSelectedSeats((prev) => {
                  const next = new Map(prev);
                  for (const sid of seatIds) {
                    const seatObj = (seats || []).find((x) => (x.seat_id ?? x.SEAT_ID) === sid);
                    if (!seatObj) continue;
                    const autoType = selectedSeatType || pickSeatTypeFor(seatObj, seatPricing) || defaultSeatType();
                    next.set(sid, { label: makeSeatLabel(seatObj), seat_type: autoType });
                  }
                  return next;
                });
                setReservedInfoDlgOpen(false);
                setSnackbar({ open: true, message: "Резервацията е заредена в бон.", severity: "success" });
              } catch (e) {
                setSnackbar({ open: true, message: `Грешка: ${e?.message || e}`, severity: "error" });
              }
            }}
          >
            Зареди в бон
          </Button>
        </DialogActions>
      </Dialog>


          <Snackbar
          open={snackbar.open}
          autoHideDuration={3200}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    </LocalizationProvider>
  );
}